/**
 * Rendu des batiments: le catalogue complet, des dix maisons ou l'on entre
 * jusqu'aux torches, aux chemins de pierre et au moulin.
 *
 * Quatre points delicats vivent ici:
 *
 * 1. Le fondu du toit. Quand le joueur entre dans une maison, le toit et les
 *    murs situes entre la camera et l'interieur deviennent transparents, pour
 *    qu'on voie le salon, la table, la cuisine et le lit sans changer de scene.
 *    Comme la camera pivote, on recalcule a chaque frame quels murs genent, a
 *    partir de leur normale sortante. Chaque face porte donc SON materiau:
 *    elles partageaient le meme, et se battaient pour son opacite.
 *
 * 2. Le budget de dessin. Une maison coutait une trentaine d'appels (un par
 *    boite). Tout est maintenant fusionne par materiau avec des couleurs de
 *    sommets (voir geometrie.ts): une maison tient en 7 appels, un amenagement
 *    en 1 a 3. Ce qui empeche de descendre plus bas est justement le fondu: le
 *    toit et chacune des quatre faces doivent rester des objets separes.
 *
 * 3. Les lumieres. Le nombre de lumieres d'une scene fait partie de la clef de
 *    cache des programmes de three.js: une lumiere par lampadaire ferait
 *    recompiler tous les materiaux a chaque pose. Un pool FIXE est attribue aux
 *    sources allumees les plus proches (voir POOL_LUMIERES).
 *
 * 4. Le fantome de placement. Le meme constructeur de maillage sert au batiment
 *    pose et a l'apercu translucide vert ou rouge, pour que ce qu'on voit avant
 *    de poser soit exactement ce qu'on obtient. Il est reconstruit a chaque
 *    rotation: tout ce qui est cree doit donc etre liberable (libererMaillage).
 */
import * as THREE from 'three'
import type { Batiment, BatimentDef, BatimentKind } from '../../shared/types/monde.ts'
import type { ITerrain, Vec2 } from '../../shared/types/world.ts'
import { batimentDef } from '../../shared/data/batiments.ts'
import {
  COLONNES_RUBAN,
  COULEURS_DEFAUT,
  EPAISSEUR_MUR,
  EPAISSEUR_PARAPET,
  EPAISSEUR_RUBAN,
  HAUTEUR_ETAGE,
  LARGEUR_PORTE,
  PLAN_ETAGE,
  TRANCHES_RUBAN,
  accesEtage,
  bassinsDe,
  hauteursRuban,
  pisteDe,
  boiteEtage,
  cloisonsInterieures,
  glissadesDe,
  LARGEUR_BAIE_ETAGE,
  largeurPorte,
  meublesDe,
  piecesEtage,
  planEtageDe,
  positionPiece,
  positionCuisine,
  positionFilon,
  positionLit,
  positionMeuble,
  positionSalon,
  positionTable,
  tourner,
  tournerInverse,
} from '../../shared/sim/batiments.ts'
import { CASCADE_HOTEL, ETANG_HOTEL, PALMIER_TOIT_HOTEL, PLAN_HOTEL } from '../../shared/data/hotel.ts'
import { FONTAINES_PARC } from '../../shared/data/parc-aquatique.ts'
import { couleurFenetre } from './lumiere.ts'
import { clamp, hache2 } from '../../shared/sim/alea.ts'
import { bosseler, fusionner, tr, type Partie } from './geometrie.ts'
import { journal } from '../log/journal.ts'

// --------------------------------------------------------------- apparence

type StyleToit = 'pignon' | 'pyramide' | 'dome' | 'plat' | 'penche'

interface StyleBatiment {
  /** Hauteur de reference du modele: mur d'une maison, hauteur totale sinon. */
  hauteurMur: number
  hauteurToit: number
  toit: StyleToit
  /** Couleurs par defaut, employees tant que le joueur n'a rien choisi. */
  murs: number
  toitCouleur: number
  porte: number
  /** Nombre de fenetres par mur lateral. */
  fenetres: number
  /** Vrai si le modele a un plancher visible. */
  plancher: boolean
  /**
   * STYLE du mobilier: une piece de maison (defaut) ou un campement.
   *
   * Il ne dit PAS ce que le modele contient — c'est `meublesDe`
   * (shared/sim/batiments.ts) qui repond, et lui seul, parce que la simulation
   * lit la meme table pour decider ce qu'on peut toucher. Il y a eu ici une
   * valeur 'aucun' pendant une heure: deux endroits qui disaient la meme chose,
   * dont un seul etait lu — exactement ce que ce projet paie a chaque fois.
   */
  meubles?: 'maison' | 'camp'
  /** Intensite de la veilleuse nocturne (0 = ce modele ne s'allume pas). */
  veilleuse?: number
}

const STYLES: Record<BatimentKind, StyleBatiment> = {
  // ------------------------------------------------------------ habitations
  cabane: {
    hauteurMur: 2.3,
    hauteurToit: 1.2,
    toit: 'pignon',
    murs: 0xb5844f,
    toitCouleur: 0x7a4b2a,
    porte: 0x5e3a1e,
    fenetres: 1,
    plancher: true,
  },
  chalet: {
    hauteurMur: 2.6,
    hauteurToit: 1.8,
    toit: 'pignon',
    murs: 0xd9a061,
    toitCouleur: 0x8c3b2f,
    porte: 0x6b4423,
    fenetres: 2,
    plancher: true,
  },
  maisonnette: {
    hauteurMur: 2.8,
    hauteurToit: 1.6,
    toit: 'pyramide',
    murs: 0xf2e3c4,
    toitCouleur: 0x4a6fa5,
    porte: 0xc25b4a,
    fenetres: 2,
    plancher: true,
  },
  villa: {
    hauteurMur: 3.2,
    hauteurToit: 0.9,
    toit: 'plat',
    murs: 0xfdf6e6,
    toitCouleur: 0xd8cdb4,
    porte: 0x3f7d8c,
    fenetres: 3,
    plancher: true,
  },
  villa_etage: {
    // Le mur du rez-de-chaussee monte exactement a HAUTEUR_ETAGE: la dalle de
    // la terrasse se pose dessus, et le joueur marche a cette hauteur-la. Les
    // deux nombres doivent rester d'accord, sinon le bonhomme flotte au-dessus
    // de sa terrasse ou s'y enfonce jusqu'aux genoux.
    hauteurMur: HAUTEUR_ETAGE,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xf4efe2,
    toitCouleur: 0xb8c4c9,
    porte: 0x2f5f6b,
    fenetres: 3,
    plancher: true,
    // Le bureau reste allume le soir: c'est ce qui donne son air habite a une
    // terrasse vue de loin. Une seule source, prise dans le pool fixe.
    veilleuse: 0.5,
  },
  hotel: {
    // Comme la villa a etage: le mur du rez-de-chaussee monte exactement a
    // HAUTEUR_ETAGE, la premiere dalle se pose dessus, et ainsi de suite —
    // chaque niveau est un multiple de la meme hauteur, celle que
    // `hauteurNiveau` donne a la simulation.
    hauteurMur: HAUTEUR_ETAGE,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xf3ead8,
    toitCouleur: 0xc8b89a,
    porte: 0x8a4a3a,
    fenetres: 3,
    plancher: true,
    // La salle a manger reste eclairee le soir: un hotel eteint se lit comme
    // un batiment abandonne. Une seule source, prise dans le pool fixe.
    veilleuse: 0.6,
  },
  ecole: {
    hauteurMur: 3.4,
    hauteurToit: 1.6,
    toit: 'pignon',
    murs: 0xe4d6c0,
    toitCouleur: 0x9c4a3c,
    porte: 0x3f5d8c,
    fenetres: 4,
    plancher: true,
    // Elle s'eclaire le soir: une ecole allumee dans la nuit se voit de loin et
    // sert de repere au village.
    veilleuse: 0.55,
  },
  /*
   * LES TROIS AEROGARES. Meme geometrie (meme emprise, meme etage, meme
   * escalier, meme restaurant): seules les COULEURS et la matiere changent —
   * bois pour l'aerodrome, beton clair pour l'aeroport, verre pour le
   * troisieme. « Fonctionnellement identiques » se lit donc jusque dans le
   * rendu: trois entrees de style, aucune branche de code.
   */
  aerodrome: {
    hauteurMur: HAUTEUR_ETAGE,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xd9b784,
    toitCouleur: 0x8a6a44,
    porte: 0x5f4326,
    fenetres: 3,
    plancher: true,
    // Une aerogare reste eclairee: c'est le repere qu'on cherche de nuit, et
    // c'est ce qui rend une piste trouvable quand on rentre a la tombee du jour.
    veilleuse: 0.8,
  },
  aeroport: {
    hauteurMur: HAUTEUR_ETAGE,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xf0ece2,
    toitCouleur: 0xb9c0c6,
    porte: 0x3f6f8c,
    fenetres: 4,
    plancher: true,
    veilleuse: 0.9,
  },
  aeroport_verre: {
    hauteurMur: HAUTEUR_ETAGE,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xcfe4ef,
    toitCouleur: 0x9fb4c2,
    porte: 0x2f5f7a,
    fenetres: 5,
    plancher: true,
    veilleuse: 1,
  },
  mine: {
    // Un chevalement de bois, bas et trapu, colle a la roche. Le toit penche
    // d'un seul pan comme un hangar de chantier: c'est ce qui la distingue au
    // premier coup d'oeil des batiments du village, tous a deux pans.
    hauteurMur: 3.4,
    hauteurToit: 0.7,
    toit: 'penche',
    murs: 0x6f5136,
    toitCouleur: 0x4c5257,
    porte: 0x3b2c1d,
    // Aucune fenetre: une galerie ne prend pas le jour. C'est aussi ce qui la
    // rend sombre a l'interieur, donc ce qui donne son sens aux lanternes.
    fenetres: 0,
    // Pas de plancher de bois: on marche sur la ROCHE. `EXTRAS.mine` pose le
    // sol de pierre, les rails et le front de taille.
    plancher: false,
    // Les lanternes de la galerie restent allumees: une mine eteinte se lit
    // comme une mine fermee. Une seule source, prise dans le pool fixe.
    veilleuse: 0.7,
  },
  garage: {
    // Bas et large, toit presque plat: on le lit tout de suite comme un abri
    // et pas comme une maison de plus.
    hauteurMur: 2.9,
    hauteurToit: 0.5,
    toit: 'penche',
    murs: 0xcfd4d8,
    toitCouleur: 0x6f7a82,
    porte: 0x4a5560,
    fenetres: 1,
    plancher: true,
  },
  musee: {
    hauteurMur: 4.2,
    hauteurToit: 1.4,
    toit: 'pignon',
    murs: 0xefe6d4,
    toitCouleur: 0x8a8f96,
    porte: 0x7a5a2c,
    fenetres: 3,
    plancher: true,
    // Un musee eclaire le soir: c'est ce qui donne envie d'y entrer de loin.
    veilleuse: 0.6,
  },
  bibliotheque: {
    hauteurMur: 3.6,
    hauteurToit: 1.5,
    toit: 'pignon',
    murs: 0xdcc9a8,
    toitCouleur: 0x5f6b4a,
    porte: 0x6b4a2c,
    fenetres: 3,
    plancher: true,
    veilleuse: 0.5,
  },
  zoo: {
    // Le pavillon d'accueil est bas: ce sont les enclos qu'on doit voir, pas
    // un mur de quatre metres qui les cacherait tous.
    hauteurMur: 3.2,
    hauteurToit: 1.2,
    toit: 'pignon',
    murs: 0xe8d9b4,
    toitCouleur: 0x7a5a3a,
    porte: 0x5f7d4a,
    fenetres: 0,
    // Pas de plancher: l'emprise est en plein air, c'est de la terre et de
    // l'herbe. Un parquet de dix-huit metres serait absurde.
    plancher: false,
  },
  /*
   * LE PARC AQUATIQUE. Il ne ressemble a aucun autre modele a etage, et c'est
   * la premiere ligne qui le dit: son mur ne monte PAS jusqu'a la dalle.
   */
  parc_aquatique: {
    /*
     * UNE ENCEINTE BASSE, ET UNE PASSERELLE QUI FLOTTE AU-DESSUS.
     *
     * Tous les autres modeles a etage font monter leur mur exactement a
     * HAUTEUR_ETAGE: la dalle se pose dessus, et l'ensemble se lit comme une
     * maison. Un parc aquatique, non. Ce qu'on doit voir de l'exterieur, c'est
     * le BASSIN, les fontaines et les glissades — pas une boite opaque de
     * dix-huit metres de large qui les cacherait tous. L'enceinte s'arrete donc
     * a hauteur d'appui, comme la cloture d'une piscine municipale, et la
     * passerelle est portee par des poteaux (voir `ajouterPasserelle`).
     *
     * Rien de la simulation n'en depend: `resoudreMurs` ne connait que
     * l'EPAISSEUR des murs et la largeur de leur ouverture, jamais leur
     * hauteur. Le mur qui repousse est exactement celui qu'on voit, il est
     * simplement moins haut qu'ailleurs.
     */
    hauteurMur: 1.6,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xe4edf2,
    toitCouleur: 0x4fa8c4,
    porte: 0x2f7f9c,
    // Aucune fenetre: un parc est a ciel ouvert, et une fenetre percee dans une
    // cloture d'un metre soixante ne voudrait rien dire.
    fenetres: 0,
    // Pas de plancher de bois: on marche sur le DALLAGE que `EXTRAS` pose
    // autour du bassin. Un parquet au bord d'une piscine serait absurde.
    plancher: false,
    // Le parc reste eclaire le soir. Une glissade dans le noir ne se voit pas,
    // et c'est justement ce qu'on vient regarder de loin.
    veilleuse: 0.8,
  },
  igloo: {
    hauteurMur: 1.4,
    hauteurToit: 1.6,
    toit: 'dome',
    murs: 0xe8f4ff,
    toitCouleur: 0xcfe6f7,
    porte: 0x9ec9e6,
    fenetres: 0,
    plancher: true,
  },
  cabane_plage: {
    hauteurMur: 2.2,
    hauteurToit: 0.9,
    toit: 'penche',
    murs: 0x8fd6c4,
    toitCouleur: 0xe8b04b,
    porte: 0xdd7a5a,
    fenetres: 1,
    plancher: true,
  },
  cabane_collations: {
    hauteurMur: 2.4,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xf6e3bf,
    toitCouleur: 0xe0524a,
    porte: 0x3fa9c4,
    fenetres: 0,
    plancher: false,
    veilleuse: 1.3,
  },
  tente: {
    hauteurMur: 2.5,
    hauteurToit: 0,
    toit: 'pignon',
    murs: 0xe4d9b8,
    toitCouleur: 0x3f8f6a,
    porte: 0x2f6b52,
    fenetres: 0,
    plancher: true,
    meubles: 'camp',
  },
  cabane_arbre: {
    hauteurMur: 2.3,
    hauteurToit: 1.1,
    toit: 'pignon',
    murs: 0xa9793f,
    toitCouleur: 0x6a4a2a,
    porte: 0x4f3520,
    fenetres: 1,
    plancher: true,
  },
  grange: {
    hauteurMur: 4.2,
    hauteurToit: 2.5,
    toit: 'pignon',
    murs: 0xa8382f,
    toitCouleur: 0x5b4335,
    porte: 0x8a2e26,
    fenetres: 2,
    plancher: true,
  },
  phare: {
    hauteurMur: 3.0,
    hauteurToit: 0.9,
    toit: 'plat',
    murs: 0xf4f4ef,
    toitCouleur: 0xd94b3a,
    porte: 0x2f4f6b,
    // Zero: la seule partie vitree du phare est sa lanterne, qui a besoin de
    // son propre materiau (elle fond avec le toit ET s'allume la nuit). Des
    // fenetres de mur auraient coute une huitieme maille pour rien: les hublots
    // du soubassement sont peints dans la maille des murs (voir EXTRAS.phare).
    fenetres: 0,
    plancher: true,
    veilleuse: 2,
  },

  // ------------------------------------------------------- eau et jardin
  piscine: {
    hauteurMur: 0.5,
    hauteurToit: 0,
    toit: 'plat',
    // `murs` est l'EAU et `toitCouleur` la margelle, comme dans le
    // constructeur et comme dans les couleurs par defaut de la simulation.
    murs: 0x38b6d9,
    toitCouleur: 0xeae0cc,
    porte: 0xeae0cc,
    fenetres: 0,
    plancher: false,
  },
  jardin: {
    hauteurMur: 0.35,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x8b6240,
    toitCouleur: 0x4f9b3f,
    porte: 0x8b6240,
    fenetres: 0,
    plancher: false,
  },
  potager: {
    hauteurMur: 0.4,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x9c6b3f,
    toitCouleur: 0x5aa83f,
    porte: 0x9c6b3f,
    fenetres: 0,
    plancher: false,
  },
  serre: {
    hauteurMur: 2.1,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xdfe6ea,
    toitCouleur: 0x5aa83f,
    porte: 0xdfe6ea,
    fenetres: 0,
    plancher: false,
  },
  fontaine: {
    hauteurMur: 1.0,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xc8c3b6,
    toitCouleur: 0x49b7d8,
    porte: 0xc8c3b6,
    fenetres: 0,
    plancher: false,
  },
  puits: {
    hauteurMur: 1.05,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x9aa0a6,
    toitCouleur: 0x7a4b2a,
    porte: 0x9aa0a6,
    fenetres: 0,
    plancher: false,
  },
  ruche: {
    hauteurMur: 0.9,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xe8c169,
    toitCouleur: 0x8a6a3a,
    porte: 0xe8c169,
    fenetres: 0,
    plancher: false,
  },

  // --------------------------------------------- cloisonnement, circulation
  cloture: {
    hauteurMur: 1.1,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xc9a273,
    toitCouleur: 0xc9a273,
    porte: 0xc9a273,
    fenetres: 0,
    plancher: false,
  },
  haie: {
    hauteurMur: 1.2,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x4f9b3f,
    toitCouleur: 0x36702c,
    porte: 0x4f9b3f,
    fenetres: 0,
    plancher: false,
  },
  muret: {
    hauteurMur: 0.8,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x9aa0a6,
    toitCouleur: 0x9aa0a6,
    porte: 0x9aa0a6,
    fenetres: 0,
    plancher: false,
  },
  portail: {
    hauteurMur: 1.7,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xa9793f,
    toitCouleur: 0xa9793f,
    porte: 0x6b4a2f,
    fenetres: 0,
    plancher: false,
  },
  chemin_pierre: {
    hauteurMur: 0.08,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xa8a49a,
    toitCouleur: 0xa8a49a,
    porte: 0xa8a49a,
    fenetres: 0,
    plancher: false,
  },
  route: {
    hauteurMur: 0.14,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x6e6a66,
    toitCouleur: 0x6e6a66,
    porte: 0x6e6a66,
    fenetres: 0,
    plancher: false,
  },
  pont_bois: {
    hauteurMur: 1.1,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xa9793f,
    toitCouleur: 0xa9793f,
    porte: 0xa9793f,
    fenetres: 0,
    plancher: false,
  },
  quai: {
    hauteurMur: 0.4,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xb08a56,
    toitCouleur: 0xb08a56,
    porte: 0xb08a56,
    fenetres: 0,
    plancher: false,
  },
  escalier: {
    hauteurMur: 1.2,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xa8a49a,
    toitCouleur: 0xa8a49a,
    porte: 0xa8a49a,
    fenetres: 0,
    plancher: false,
  },

  // ------------------------------------------------------------- mobilier
  banc: {
    hauteurMur: 0.9,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xb5844f,
    toitCouleur: 0xb5844f,
    porte: 0xb5844f,
    fenetres: 0,
    plancher: false,
  },
  table_picnic: {
    hauteurMur: 0.78,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xc09257,
    toitCouleur: 0xc09257,
    porte: 0xc09257,
    fenetres: 0,
    plancher: false,
  },
  hamac: {
    hauteurMur: 1.7,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x9c6b3f,
    toitCouleur: 0xe8734a,
    porte: 0x9c6b3f,
    fenetres: 0,
    plancher: false,
  },
  parasol: {
    hauteurMur: 2.3,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xb5844f,
    toitCouleur: 0xe0524a,
    porte: 0xb5844f,
    fenetres: 0,
    plancher: false,
  },
  chaise_longue: {
    hauteurMur: 0.85,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xc09257,
    toitCouleur: 0x4fb8c4,
    porte: 0xc09257,
    fenetres: 0,
    plancher: false,
  },
  balancoire: {
    hauteurMur: 2.2,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xb5844f,
    toitCouleur: 0xe0a03a,
    porte: 0xb5844f,
    fenetres: 0,
    plancher: false,
  },
  toboggan: {
    hauteurMur: 1.75,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xb5844f,
    toitCouleur: 0x4a9be0,
    porte: 0xb5844f,
    fenetres: 0,
    plancher: false,
  },
  bac_sable: {
    hauteurMur: 0.4,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xb5844f,
    toitCouleur: 0xefdca8,
    porte: 0xb5844f,
    fenetres: 0,
    plancher: false,
  },
  barbecue: {
    hauteurMur: 1.1,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x8d8880,
    toitCouleur: 0x8d8880,
    porte: 0x8d8880,
    fenetres: 0,
    plancher: false,
  },
  feu_camp: {
    hauteurMur: 0.7,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x8d8880,
    toitCouleur: 0x8d8880,
    porte: 0x8d8880,
    fenetres: 0,
    plancher: false,
    veilleuse: 1.7,
  },

  // ------------------------------------------------------------- eclairage
  lampadaire: {
    hauteurMur: 3.4,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x4a4a55,
    toitCouleur: 0xffd98a,
    porte: 0x4a4a55,
    fenetres: 0,
    plancher: false,
    veilleuse: 1.5,
  },
  torche: {
    hauteurMur: 1.8,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x7a5a38,
    toitCouleur: 0x7a5a38,
    porte: 0x7a5a38,
    fenetres: 0,
    plancher: false,
    veilleuse: 1.1,
  },
  guirlande: {
    hauteurMur: 2.4,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x8a6a44,
    toitCouleur: 0x8a6a44,
    porte: 0x8a6a44,
    fenetres: 0,
    plancher: false,
    veilleuse: 0.9,
  },

  // --------------------------------------------------- utilitaire et decor
  coffre: {
    hauteurMur: 0.75,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x9c6b3f,
    toitCouleur: 0x8a6a3a,
    porte: 0x9c6b3f,
    fenetres: 0,
    plancher: false,
  },
  atelier: {
    hauteurMur: 1.9,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xa9793f,
    toitCouleur: 0x7d848b,
    porte: 0xa9793f,
    fenetres: 0,
    plancher: false,
  },
  four_pain: {
    hauteurMur: 1.6,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xc8b49a,
    toitCouleur: 0x8d5a3a,
    porte: 0xc8b49a,
    fenetres: 0,
    plancher: false,
  },
  moulin: {
    hauteurMur: 5.6,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xe8e2d4,
    toitCouleur: 0x7a4b2a,
    porte: 0x6b4a2f,
    fenetres: 0,
    plancher: false,
  },
  tour_guet: {
    hauteurMur: 5.0,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xa9793f,
    toitCouleur: 0x6a4a2a,
    porte: 0xa9793f,
    fenetres: 0,
    plancher: false,
  },
  epouvantail: {
    hauteurMur: 1.9,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x8a6a44,
    toitCouleur: 0xd8543f,
    porte: 0x8a6a44,
    fenetres: 0,
    plancher: false,
  },
  statue: {
    hauteurMur: 2.3,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xdcd8cc,
    toitCouleur: 0xdcd8cc,
    porte: 0xdcd8cc,
    fenetres: 0,
    plancher: false,
  },
  totem: {
    hauteurMur: 2.7,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xb5844f,
    toitCouleur: 0xd8543f,
    porte: 0xb5844f,
    fenetres: 0,
    plancher: false,
  },
  drapeau: {
    hauteurMur: 3.2,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0xdcd8cc,
    toitCouleur: 0x3f7cc4,
    porte: 0xdcd8cc,
    fenetres: 0,
    plancher: false,
  },
  panneau: {
    hauteurMur: 1.5,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x9c6b3f,
    toitCouleur: 0xe8d9b8,
    porte: 0x9c6b3f,
    fenetres: 0,
    plancher: false,
  },
  boite_lettres: {
    hauteurMur: 1.3,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x9c6b3f,
    toitCouleur: 0x3f6fa5,
    porte: 0x9c6b3f,
    fenetres: 0,
    plancher: false,
  },
  rampe_feux: {
    hauteurMur: 0.7,
    hauteurToit: 0,
    toit: 'plat',
    murs: 0x5b5b66,
    toitCouleur: 0xef5f5f,
    porte: 0x5b5b66,
    fenetres: 0,
    plancher: false,
  },
}

/**
 * Epaisseur des murs et largeur de la porte: importees de la simulation, qui
 * s'en sert pour la collision. Les redeclarer ici a l'identique marchait tant
 * que personne ne changeait un seul des deux cotes.
 */
const EP_MUR = EPAISSEUR_MUR
const L_PORTE = LARGEUR_PORTE
/** Hauteur de l'ouverture de la porte. Purement visuelle. */
const H_PORTE = 2.0

/** Vitesse du fondu des toits et des murs, en unites d'opacite par seconde. */
const VITESSE_FONDU = 5

/**
 * Nombre de lumieres de batiment allumables simultanement.
 *
 * FIXE, et c'est le point important: le nombre de lumieres d'une scene fait
 * partie de la clef de cache des programmes de three.js. Une lumiere par
 * maison ferait donc recompiler TOUS les materiaux a chaque pose (a-coup
 * visible), et chacune couterait un calcul par pixel a l'ecran, pour toujours.
 * Le meme piege est documente dans client/render/feux.ts, qui garde lui aussi
 * un pool fixe. On attribue donc ces quelques lumieres aux batiments les plus
 * proches du joueur; le cout d'eclairage ne depend plus du nombre de maisons,
 * ni du nombre de torches, de guirlandes ou de feux de camp.
 */
const POOL_LUMIERES = 6

/** Portee d'une lumiere de batiment, en metres. */
const PORTEE_LUMIERE = 12

/**
 * Avance, en metres, qu'un batiment deja eclaire garde sur un nouveau venu.
 * Sans elle, deux sources a la meme distance s'echangeaient la lampe a chaque
 * pas du joueur.
 */
const MARGE_LUMIERE = 3

// ------------------------------------------------------- teintes de reference

/** Teintes qui ne se personnalisent pas: la nature et les materiaux. */
const BOIS = 0x9c6b3f
const BOIS_FONCE = 0x63421f
const BOIS_CLAIR = 0xc9a273
const PIERRE = 0x9aa0a6
const FEUILLE = 0x4f9b3f
const FEUILLE_FONCE = 0x2f6b28
const METAL = 0x8f959c
const TERRE = 0x5b3f26
const EAU = 0x38b6d9
const BLANC = 0xf6f2e8
const NOIR = 0x2a2622
const FLAMME = 0xff9a3a
const BRAISE = 0xd8401a
const PAILLE = 0xe0c265

// ------------------------------------------------------------------ couleurs

function canaux(c: number): [number, number, number] {
  return [(c >> 16) & 0xff, (c >> 8) & 0xff, c & 0xff]
}

function depuisCanaux(r: number, v: number, b: number): number {
  const borne = (x: number) => Math.max(0, Math.min(255, Math.round(x)))
  return (borne(r) << 16) | (borne(v) << 8) | borne(b)
}

/** Assombrit (f < 1) ou eclaircit (f > 1) une couleur. */
function teinter(c: number, f: number): number {
  const [r, v, b] = canaux(c)
  return depuisCanaux(r * f, v * f, b * f)
}

/** Melange vers le blanc, pour les rechampis et les reflets. */
function laver(c: number, t: number): number {
  const [r, v, b] = canaux(c)
  return depuisCanaux(r + (255 - r) * t, v + (255 - v) * t, b + (255 - b) * t)
}

/**
 * Couleur reellement employee pour un canal.
 *
 * Deux regles, dans cet ordre:
 *
 * 1. Un modele absent de `COULEURS_DEFAUT` (simulation) n'a pas de palette: elle
 *    lui sert un repli neutre, que `client/jeu.ts` teinte ensuite vers la
 *    couleur du proprietaire. Se fier a `b.couleurs` donnerait donc le MEME
 *    beige a tout le catalogue. On garde le style du modele, et le jour ou
 *    COULEURS_DEFAUT accueille ce modele, le choix du joueur reprend la main
 *    tout seul. (Comparer a la valeur neutre ne marche pas: la teinte du
 *    proprietaire l'a deja deplacee.)
 * 2. Un canal que le modele n'annonce pas dans `BatimentDef.couleurs` n'est pas
 *    personnalisable: le style decide. C'est ce qui garde le feuillage d'une
 *    haie vert, l'eau d'une fontaine bleue et les flammes orange.
 *
 * Sinon c'est le choix du joueur, y compris un noir parfait.
 */
function choisirCouleur(
  canal: 'murs' | 'toit' | 'porte',
  b: Batiment,
  def: BatimentDef,
  style: StyleBatiment,
): number {
  const parDefaut = canal === 'murs' ? style.murs : canal === 'toit' ? style.toitCouleur : style.porte
  const choix = b.couleurs?.[canal]
  if (choix === undefined) return parDefaut
  if (!(b.kind in COULEURS_DEFAUT)) return parDefaut
  if (!def.couleurs.includes(canal)) return parDefaut
  return choix
}

// ------------------------------------------------------------- lots de parties

/**
 * Un lot de primitives destinees a UNE seule maille: c'est l'unite de cout du
 * rendu. Tout ce qui partage un lot ne coute qu'un appel de dessin, et porte sa
 * couleur dans ses sommets.
 */
type Lot = Partie[]

const HAUT = new THREE.Vector3(0, 1, 0)

function pGeo(
  lot: Lot,
  couleur: number,
  geo: THREE.BufferGeometry,
  pos?: [number, number, number],
  rot?: [number, number, number],
  ech?: [number, number, number],
): void {
  lot.push({ geo, couleur, transforme: tr(pos, rot, ech) })
}

function pBoite(
  lot: Lot,
  couleur: number,
  dim: [number, number, number],
  pos?: [number, number, number],
  rot?: [number, number, number],
): void {
  pGeo(lot, couleur, new THREE.BoxGeometry(Math.abs(dim[0]), Math.abs(dim[1]), Math.abs(dim[2])), pos, rot)
}

function pCyl(
  lot: Lot,
  couleur: number,
  rHaut: number,
  rBas: number,
  h: number,
  seg: number,
  pos?: [number, number, number],
  rot?: [number, number, number],
  angle?: [number, number],
): void {
  const geo = new THREE.CylinderGeometry(rHaut, rBas, h, seg, 1, false, angle?.[0] ?? 0, angle?.[1] ?? Math.PI * 2)
  pGeo(lot, couleur, geo, pos, rot)
}

function pCone(
  lot: Lot,
  couleur: number,
  r: number,
  h: number,
  seg: number,
  pos?: [number, number, number],
  rot?: [number, number, number],
  angle?: [number, number],
): void {
  const geo = new THREE.ConeGeometry(r, h, seg, 1, false, angle?.[0] ?? 0, angle?.[1] ?? Math.PI * 2)
  pGeo(lot, couleur, geo, pos, rot)
}

function pSphere(
  lot: Lot,
  couleur: number,
  r: number,
  pos?: [number, number, number],
  ech?: [number, number, number],
  seg = 8,
  anneaux = 6,
): void {
  pGeo(lot, couleur, new THREE.SphereGeometry(r, seg, anneaux), pos, undefined, ech)
}

/** Une plaque horizontale: deux triangles, pour tout ce qui est plat au sol. */
function pPlaque(
  lot: Lot,
  couleur: number,
  l: number,
  p: number,
  pos: [number, number, number],
  rotY = 0,
): void {
  pGeo(lot, couleur, new THREE.PlaneGeometry(l, p), pos, [-Math.PI / 2, 0, rotY])
}

/** Un cylindre tendu entre deux points: cordes, haubans, barreaux d'echelle. */
function pTube(
  lot: Lot,
  couleur: number,
  r: number,
  a: [number, number, number],
  b: [number, number, number],
  seg = 5,
): void {
  const va = new THREE.Vector3(a[0], a[1], a[2])
  const dir = new THREE.Vector3(b[0], b[1], b[2]).sub(va)
  const longueur = dir.length()
  if (longueur < 1e-4) return
  const q = new THREE.Quaternion().setFromUnitVectors(HAUT, dir.clone().normalize())
  const mtx = new THREE.Matrix4().compose(
    va.clone().addScaledVector(dir, 0.5),
    q,
    new THREE.Vector3(1, 1, 1),
  )
  lot.push({ geo: new THREE.CylinderGeometry(r, r, longueur, seg), couleur, transforme: mtx })
}

/**
 * Deplace en bloc les parties ajoutees depuis `depuis`.
 *
 * C'est ce qui permet de dessiner un sous-ensemble dans son propre repere (une
 * aile de moulin, un battant de portail sur ses gonds, un barreau d'echelle)
 * puis de le poser d'un coup, sans recalculer chaque piece a la main.
 */
function grouper(lot: Lot, depuis: number, matrice: THREE.Matrix4): void {
  for (let i = depuis; i < lot.length; i++) {
    const partie = lot[i]
    if (partie) partie.transforme.premultiply(matrice)
  }
}

/** Une echelle appuyee: deux limons et des barreaux. */
function pEchelle(lot: Lot, couleur: number, largeur: number, hauteur: number, matrice: THREE.Matrix4): void {
  const debut = lot.length
  const demi = largeur / 2
  for (const signe of [1, -1]) {
    pBoite(lot, couleur, [0.07, hauteur, 0.07], [signe * demi, hauteur / 2, 0])
  }
  const barreaux = Math.max(2, Math.round(hauteur / 0.36))
  for (let i = 1; i <= barreaux; i++) {
    pBoite(lot, teinter(couleur, 0.85), [largeur, 0.06, 0.05], [0, (i * hauteur) / (barreaux + 1), 0])
  }
  grouper(lot, debut, matrice)
}

// ------------------------------------------------------------------ maillage

/** Un mur, avec sa normale sortante en repere local, pour le fondu. */
interface Mur {
  maillage: THREE.Mesh
  /** Normale sortante dans le repere du batiment (avant rotation). */
  normale: THREE.Vector3
}

interface Anime {
  objet: THREE.Object3D
  genre: 'rotation' | 'balancement'
  vitesse: number
  phase: number
}

// ------------------------------------------------------- pousse et repousse

/**
 * Une piece de vegetation qui suit la maturite de son jardin.
 *
 * Un jardin cueilli etait EXACTEMENT le meme objet qu'un jardin mur: rien, en
 * le regardant, ne disait qu'il n'y restait rien a prendre, et le proprietaire
 * y est revenu six fois en quatorze minutes pour l'apprendre par un message.
 * Une piece marquee ainsi se retracte vers son ancre et se fane a la
 * cueillette, puis ressort au fil de la repousse.
 */
interface Pousse {
  /** Rang de la primitive dans le lot ou elle vient d'etre deposee. */
  piece: number
  /** Point de retraction, en repere du batiment: le pied de la plante. */
  ancre: [number, number, number]
  /** Maturite a laquelle la piece commence a sortir: les fruits, tard. */
  seuil: number
}

/** Ce qu'on sait des pousses AVANT la fusion, quand les pieces existent encore. */
interface PlanRecolte {
  /** Premier sommet concerne dans la maille fusionnee. */
  debut: number
  /** Point de retraction de chaque sommet du domaine. */
  ancre: Float32Array
  /** Seuil d'apparition de chaque sommet; negatif = sommet immobile. */
  seuil: Float32Array
}

/**
 * Sommets d'une maille qui poussent, prepares une fois pour toutes.
 *
 * On reecrit les tampons de la geometrie deja fusionnee, sans jamais
 * reconstruire la maille (reconstruire a chaque cueillette est exactement ce
 * qui fait tomber les images par seconde) et sans un appel de dessin de plus:
 * la vegetation reste dans la maille unique du jardin.
 */
interface Recolte {
  geo: THREE.BufferGeometry
  debut: number
  /** Position et couleur a pleine maturite: l'etat de reference. */
  pleine: Float32Array
  mure: Float32Array
  ancre: Float32Array
  seuil: Float32Array
  /** Derniere maturite appliquee: on ne reecrit pas les tampons pour rien. */
  maturite: number
}

/** Teinte d'une feuille a maturite nulle: du vert passe, presque de la terre. */
const FANE = new THREE.Color(0x6f6134).toArray()

/** Sommets qu'une primitive apportera a la maille fusionnee (voir fusionner). */
function sommetsDe(p: Partie): number {
  return p.geo.index ? p.geo.index.count : (p.geo.getAttribute('position')?.count ?? 0)
}

/**
 * Repere les sommets des pousses AVANT la fusion.
 *
 * Apres, le lot est vide et les primitives liberees: plus rien ne dit quel
 * sommet de la maille venait de quelle piece.
 */
function planifierRecolte(lot: Lot, pousses: Pousse[]): PlanRecolte | null {
  if (pousses.length === 0) return null
  const debuts: number[] = []
  let total = 0
  for (const p of lot) {
    debuts.push(total)
    total += sommetsDe(p)
  }

  let debut = total
  let fin = 0
  for (const g of pousses) {
    const d = debuts[g.piece]
    const piece = lot[g.piece]
    if (d === undefined || !piece) continue
    debut = Math.min(debut, d)
    fin = Math.max(fin, d + sommetsDe(piece))
  }
  if (fin <= debut) return null

  const ancre = new Float32Array((fin - debut) * 3)
  const seuil = new Float32Array(fin - debut).fill(-1)
  for (const g of pousses) {
    const d = debuts[g.piece]
    const piece = lot[g.piece]
    if (d === undefined || !piece) continue
    const n = sommetsDe(piece)
    for (let i = d - debut; i < d - debut + n; i++) {
      ancre[i * 3] = g.ancre[0]
      ancre[i * 3 + 1] = g.ancre[1]
      ancre[i * 3 + 2] = g.ancre[2]
      seuil[i] = g.seuil
    }
  }
  return { debut, ancre, seuil }
}

/** Photographie l'etat mur de la maille fusionnee: tout part de la. */
function fixerRecolte(maille: THREE.Mesh, plan: PlanRecolte): Recolte {
  const geo = maille.geometry
  const pos = geo.getAttribute('position').array as Float32Array
  const col = geo.getAttribute('color').array as Float32Array
  const n = plan.seuil.length * 3
  const pleine = new Float32Array(n)
  const mure = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    pleine[i] = pos[plan.debut * 3 + i] ?? 0
    mure[i] = col[plan.debut * 3 + i] ?? 0
  }
  return { geo, debut: plan.debut, pleine, mure, ancre: plan.ancre, seuil: plan.seuil, maturite: 1 }
}

/**
 * Applique une maturite: 0 tout juste cueilli, 1 mur.
 *
 * La sphere englobante n'est pas recalculee a dessein: l'etat mur est le plus
 * grand de tous, celle qu'on a calculee a la fusion englobe donc tous les
 * autres.
 */
function appliquerRecolte(r: Recolte, maturite: number): void {
  const attrPos = r.geo.getAttribute('position')
  const attrCol = r.geo.getAttribute('color')
  const pos = attrPos.array as Float32Array
  const col = attrCol.array as Float32Array
  for (let i = 0; i < r.seuil.length; i++) {
    const s = r.seuil[i] ?? -1
    const brut = s < 0 ? 1 : (maturite - s) / Math.max(0.05, 1 - s)
    const t = clamp(brut, 0, 1)
    // Croissance en avance sur l'horloge, et non lineaire ni en S: avec une
    // courbe symetrique, un jardin cueilli depuis six heures etait encore
    // parfaitement rase, et le joueur ne voyait toujours pas que ca revenait.
    const f = Math.pow(t, 0.55)
    for (let a = 0; a < 3; a++) {
      const k = i * 3 + a
      const j = (r.debut + i) * 3 + a
      const ancre = r.ancre[k] ?? 0
      const mur = r.mure[k] ?? 0
      pos[j] = ancre + ((r.pleine[k] ?? 0) - ancre) * f
      const fane = FANE[a] ?? 0
      col[j] = fane + (mur - fane) * f
    }
  }
  attrPos.needsUpdate = true
  attrCol.needsUpdate = true
  r.maturite = maturite
}

interface Maillage {
  groupe: THREE.Group
  toit: THREE.Object3D[]
  /**
   * Ce qui n'existe qu'A L'ETAGE: dalle, acrotere, bureau, spa, tete
   * d'escalier. S'efface quand le joueur est dans le salon, pour la meme raison
   * que le toit — sans ca, la terrasse est un couvercle opaque au-dessus de la
   * piece qu'on vient regarder.
   */
  etage: THREE.Object3D[]
  /**
   * Ce qui n'existe qu'au DEUXIEME etage: la dalle du haut de l'hotel, son
   * acrotere, le salon de lecture, le bassin et sa cascade. Un lot a part
   * parce que le fondu ne montre que le niveau ou l'on se tient: du premier
   * etage, le deuxieme est un couvercle a effacer, exactement comme la
   * terrasse l'est depuis le salon.
   */
  etage2: THREE.Object3D[]
  /**
   * L'eau du spa: elle suit l'etage dans le fondu mais garde SA transparence.
   *
   * Une liste a part parce que `appliquerOpacite` ECRASE l'opacite du
   * materiau: rangee avec le reste de l'etage, l'eau serait redevenue un
   * carrelage bleu opaque des que la terrasse est entierement visible —
   * c'est-a-dire tout le temps ou l'on peut la regarder.
   */
  etageEau: THREE.Object3D[]
  /** L'eau du DERNIER niveau de l'hotel: bassin et cascade. Meme regle. */
  etage2Eau: THREE.Object3D[]
  /**
   * Ce qui appartient au REZ-DE-CHAUSSEE et s'efface quand on monte: le
   * plancher et son mobilier. Les murs, eux, ont deja leur propre fondu.
   */
  bas: THREE.Object3D[]
  murs: Mur[]
  fenetres: THREE.Mesh[]
  /** Materiaux a teinter pour le fantome. */
  materiaux: THREE.Material[]
  /** Parties qui bougent toutes seules: ailes du moulin, balancoire. */
  animes: Anime[]
  /** Vrai si ce batiment veut une lumiere du pool. */
  veutLumiere: boolean
  /** Intensite de la veilleuse nocturne (lampadaire, torche, phare...). */
  veilleuse: number
  /** Position de la lumiere, en repere monde. */
  posLumiere: THREE.Vector3
  /** Vegetation qui pousse et se cueille (jardin), sinon null. */
  recolte: Recolte | null
}

function maillageVide(nom: string): Maillage {
  const m: Maillage = {
    groupe: new THREE.Group(),
    toit: [],
    etage: [],
    etage2: [],
    etageEau: [],
    etage2Eau: [],
    bas: [],
    murs: [],
    fenetres: [],
    materiaux: [],
    animes: [],
    veutLumiere: false,
    veilleuse: 0,
    posLumiere: new THREE.Vector3(),
    recolte: null,
  }
  m.groupe.name = nom
  return m
}

/**
 * Libere tout ce qu'un maillage a cree.
 *
 * On parcourt l'arbre au lieu de se fier a une liste tenue a la main: des
 * geometries et des materiaux n'y etaient pas enregistres et fuyaient a chaque
 * rotation du fantome de construction. Plus rien n'est partage entre instances
 * (chaque batiment fusionne sa propre geometrie), donc tout est liberable.
 */
function libererMaillage(m: Maillage): void {
  m.groupe.traverse((o) => {
    const maille = o as THREE.Mesh
    if (!maille.isMesh) return
    if (maille.geometry) maille.geometry.dispose()
    const mt = maille.material
    if (Array.isArray(mt)) for (const x of mt) x.dispose()
    else if (mt) mt.dispose()
  })
  for (const mt of m.materiaux) mt.dispose()
  m.materiaux.length = 0
  m.animes.length = 0
  // La geometrie vient d'etre liberee: ecrire dedans plus tard reveillerait un
  // tampon mort.
  m.recolte = null
}

// ------------------------------------------------------------------ materiaux

interface OptionsMateriau {
  transparent?: boolean
  opacite?: number
  emissive?: number
}

/**
 * Materiau d'une maille fusionnee: la couleur vient des sommets, donc une
 * dizaine de pieces de teintes differentes tiennent dans un seul appel.
 */
function matFusion(m: Maillage, o?: OptionsMateriau): THREE.MeshLambertMaterial {
  const mt = new THREE.MeshLambertMaterial({
    color: 0xffffff,
    vertexColors: true,
    emissive: o?.emissive ?? 0x000000,
    transparent: o?.transparent ?? false,
    opacity: o?.opacite ?? 1,
  })
  m.materiaux.push(mt)
  return mt
}

interface OptionsPose {
  ombre?: boolean
  recoit?: boolean
  mat?: OptionsMateriau
}

/**
 * Fusionne un lot et l'ajoute au groupe: un lot = un appel de dessin.
 *
 * C'est `poser` qui cree le materiau, et seulement si le lot n'est pas vide:
 * le fabriquer chez l'appelant en creait un pour rien a chaque modele sans
 * partie lumineuse, et ce materiau, attache a aucune maille, echappait a
 * `libererMaillage`.
 */
function poser(m: Maillage, lot: Lot, o?: OptionsPose): THREE.Mesh | null {
  if (lot.length === 0) return null
  const maille = new THREE.Mesh(fusionner(lot), matFusion(m, o?.mat))
  maille.castShadow = o?.ombre ?? true
  maille.receiveShadow = o?.recoit ?? true
  m.groupe.add(maille)
  lot.length = 0
  return maille
}

/** Phase deterministe tiree de l'identifiant: desynchronise les animations. */
function phaseDe(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (Math.imul(h ^ id.charCodeAt(i), 16777619) >>> 0) >>> 0
  return ((h % 1000) / 1000) * Math.PI * 2
}

// ------------------------------------------------------------ toits et murs

function ajouterToit(lot: Lot, style: StyleBatiment, l: number, p: number, couleur: number): void {
  const y = style.hauteurMur

  if (style.toit === 'plat') {
    pBoite(lot, couleur, [l + 0.3, 0.22, p + 0.3], [0, y + 0.11, 0])
    // Un petit acrotere pour que la villa ne ressemble pas a une boite.
    for (const signe of [1, -1]) {
      pBoite(lot, laver(couleur, 0.12), [l + 0.3, 0.28, 0.16], [0, y + 0.36, (signe * (p + 0.3)) / 2 - signe * 0.08])
    }
    return
  }

  if (style.toit === 'penche') {
    pBoite(lot, couleur, [l + 0.5, 0.2, p + 0.5], [0, y + style.hauteurToit / 2, 0], [-0.22, 0, 0])
    return
  }

  if (style.toit === 'dome') {
    // 14 x 8 au lieu de 20 x 12: la coupole reste ronde pour trois fois moins
    // de triangles, et un igloo ne se regarde jamais de tres pres.
    const geo = new THREE.SphereGeometry(Math.min(l, p) / 2 + 0.15, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2)
    pGeo(lot, couleur, geo, [0, y - 0.1, 0])
    return
  }

  if (style.toit === 'pyramide') {
    pCone(lot, couleur, Math.max(l, p) * 0.75, style.hauteurToit, 4, [0, y + style.hauteurToit / 2, 0], [0, Math.PI / 4, 0])
    return
  }

  // Pignon: deux pans inclines plus deux triangles de fronton.
  const pente = Math.atan2(style.hauteurToit, p / 2)
  const longPan = Math.hypot(p / 2, style.hauteurToit) + 0.2
  for (const signe of [1, -1]) {
    pBoite(lot, couleur, [l + 0.4, 0.18, longPan], [0, y + style.hauteurToit / 2, (signe * p) / 4], [-signe * pente, 0, 0])
  }
  for (const signe of [1, -1]) {
    const fronton = new THREE.Shape()
    fronton.moveTo(-p / 2, 0)
    fronton.lineTo(p / 2, 0)
    fronton.lineTo(0, style.hauteurToit)
    fronton.closePath()
    const geo = new THREE.ExtrudeGeometry(fronton, { depth: EP_MUR, bevelEnabled: false })
    pGeo(lot, teinter(couleur, 1.08), geo, [(signe * l) / 2, y, 0], [0, Math.PI / 2, 0])
  }
}

/** Les fenetres d'un mur: une bande de vitres, toutes dans le meme lot. */
function ajouterFenetres(
  lot: Lot,
  nb: number,
  largeur: number,
  hauteurMur: number,
  placer: (dec: number, y: number) => [number, number, number],
  rotY: number,
): void {
  if (nb <= 0) return
  const pas = largeur / (nb + 1)
  for (let i = 1; i <= nb; i++) {
    const pos = placer(-largeur / 2 + pas * i, hauteurMur * 0.6)
    pBoite(lot, 0x9fd4e8, [0.75, 0.75, 0.06], pos, [0, rotY, 0])
    // Un croisillon: une vitre nue ne se lit pas comme une fenetre.
    pBoite(lot, laver(0x6b5136, 0.1), [0.82, 0.07, 0.09], pos, [0, rotY, 0])
    pBoite(lot, laver(0x6b5136, 0.1), [0.07, 0.82, 0.09], pos, [0, rotY, 0])
  }
}

// ---------------------------------------------------------------- mobilier

/** Teintes du mobilier interieur: elles ne se personnalisent pas. */
const TISSU = 0x5b7f9c
const TISSU_CLAIR = 0x86aac4
const TAPIS = 0xb0563f
const COUSSIN = 0xe8a13f
const ABAT_JOUR = 0xf4e3b8

/**
 * Echelle du mobilier.
 *
 * Le plan de la piece est le meme partout (shared/sim/batiments.ts), mais une
 * cabane de plage de 4 m ne peut pas recevoir le canape d'une villa de 12: on
 * garde le plan et on reduit les meubles. Les bornes evitent les deux ridicules,
 * le lit de poupee et le canape de geant.
 */
function echelleMeuble(l: number, p: number): number {
  return clamp(Math.min(l, p) / 6.5, 0.78, 1.12)
}

/** Cuisine: comptoir, plan de travail, cuisiniere, casserole. On y CUISINE. */
function ajouterCuisine(lot: Lot, x: number, z: number, e: number): void {
  pBoite(lot, BOIS, [1.6 * e, 0.85 * e, 0.6 * e], [x, 0.425 * e, z])
  pBoite(lot, 0xd8d2c4, [1.68 * e, 0.07 * e, 0.66 * e], [x, 0.88 * e, z])
  pBoite(lot, METAL, [0.5 * e, 0.5 * e, 0.5 * e], [x + 0.55 * e, 0.25 * e, z])
  pBoite(lot, teinter(METAL, 0.7), [0.42 * e, 0.04 * e, 0.42 * e], [x + 0.55 * e, 0.51 * e, z])
  pCyl(lot, METAL, 0.16 * e, 0.14 * e, 0.18 * e, 10, [x - 0.35 * e, 1.0 * e, z])
  pBoite(lot, 0xd8d2c4, [0.24 * e, 0.03 * e, 0.06 * e], [x - 0.35 * e, 1.0 * e, z - 0.2 * e])
}

/** Lit: cadre, matelas, couette, oreiller. */
function ajouterLit(lot: Lot, x: number, z: number, e: number): void {
  pBoite(lot, BOIS, [1.1 * e, 0.32 * e, 1.9 * e], [x, 0.16 * e, z])
  pBoite(lot, BOIS, [1.1 * e, 0.5 * e, 0.12 * e], [x, 0.4 * e, z + 0.94 * e])
  pBoite(lot, 0xfdf6e6, [1.02 * e, 0.22 * e, 1.8 * e], [x, 0.43 * e, z])
  pBoite(lot, 0xef8fa8, [1.06 * e, 0.1 * e, 1.15 * e], [x, 0.57 * e, z + 0.3 * e])
  pBoite(lot, 0xffffff, [0.72 * e, 0.14 * e, 0.34 * e], [x, 0.6 * e, z - 0.68 * e])
}

/**
 * Le salon: tapis borde, canape adosse au mur, table basse, plante, lampadaire.
 *
 * C'est le tapis qui fait le plus de travail: en vue orthographique a 45 degres
 * on voit surtout le SOL, et une tache de couleur au sol se lit comme un salon
 * bien avant qu'on distingue la forme du canape.
 *
 * Le lampadaire n'a pas de lumiere a lui: le nombre de lumieres de la scene
 * fait partie de la clef de cache des programmes three.js, et la maison tire
 * deja sa lampe du pool fixe (POOL_LUMIERES).
 */
function ajouterSalon(lot: Lot, x: number, z: number, e: number, l: number, p: number): void {
  // Decale vers la piece: entierement glisse sous le canape, il ne se voyait
  // pas. Les bornes le tiennent a l'ecart des deux murs du coin.
  const tapisX = x + 0.25 * e
  const demiX = Math.min(1.3 * e, l / 4 + 0.25 * e - 0.22)
  const demiZ = Math.min(1.1 * e, p / 4 - 0.22)
  pPlaque(lot, TAPIS, demiX * 2, demiZ * 2, [tapisX, 0.004, z])
  pPlaque(lot, laver(TAPIS, 0.3), demiX * 2 - 0.3 * e, demiZ * 2 - 0.3 * e, [tapisX, 0.008, z])

  // Canape adosse au mur de gauche, assise tournee vers la piece.
  const cx = x - 0.5 * e
  pBoite(lot, teinter(TISSU, 0.78), [0.78 * e, 0.32 * e, 1.72 * e], [cx, 0.16 * e, z])
  pBoite(lot, TISSU_CLAIR, [0.7 * e, 0.16 * e, 1.54 * e], [cx + 0.04 * e, 0.4 * e, z])
  pBoite(lot, TISSU, [0.22 * e, 0.62 * e, 1.72 * e], [cx - 0.3 * e, 0.63 * e, z])
  for (const signe of [1, -1]) {
    pBoite(lot, TISSU, [0.78 * e, 0.26 * e, 0.2 * e], [cx, 0.45 * e, z + signe * 0.76 * e])
    pBoite(lot, COUSSIN, [0.14 * e, 0.32 * e, 0.34 * e], [cx - 0.1 * e, 0.62 * e, z + signe * 0.42 * e], [0, 0, -0.3])
  }

  // Table basse, avec une tasse et un livre: vide, elle ne raconte rien.
  const bx = x + 0.72 * e
  pBoite(lot, BOIS, [0.56 * e, 0.07 * e, 0.98 * e], [bx, 0.42 * e, z])
  for (const [sx, sz] of [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const) {
    pBoite(lot, BOIS_FONCE, [0.07 * e, 0.4 * e, 0.07 * e], [bx + sx * 0.21 * e, 0.2 * e, z + sz * 0.42 * e])
  }
  pCyl(lot, BLANC, 0.08 * e, 0.07 * e, 0.12 * e, 8, [bx, 0.51 * e, z - 0.28 * e])
  pBoite(lot, 0x3f7d8c, [0.3 * e, 0.05 * e, 0.22 * e], [bx, 0.48 * e, z + 0.26 * e], [0, 0.4, 0])

  // Plante en pot au bout du canape, cote piece.
  const px = x - 0.62 * e
  pCyl(lot, 0xb5643f, 0.19 * e, 0.14 * e, 0.28 * e, 8, [px, 0.14 * e, z + 1.06 * e])
  pSphere(lot, FEUILLE, 0.3 * e, [px, 0.48 * e, z + 1.06 * e], [1, 0.9, 1], 6, 4)
  pSphere(lot, FEUILLE_FONCE, 0.2 * e, [px + 0.14 * e, 0.68 * e, z + 0.96 * e], undefined, 6, 4)

  // Lampadaire, a l'autre bout du canape.
  const lx = x - 0.5 * e
  const lz = z - 0.98 * e
  pCyl(lot, teinter(METAL, 0.8), 0.16 * e, 0.16 * e, 0.05 * e, 8, [lx, 0.025 * e, lz])
  pCyl(lot, METAL, 0.035 * e, 0.035 * e, 1.32 * e, 6, [lx, 0.66 * e, lz])
  pCyl(lot, ABAT_JOUR, 0.16 * e, 0.26 * e, 0.32 * e, 10, [lx, 1.46 * e, lz])
}

/**
 * La table a manger et ses quatre chaises: l'endroit ou l'on MANGE.
 *
 * Assiettes et bouquet compris. Sans eux, quatre chaises autour d'un plateau se
 * lisent comme un etabli.
 */
function ajouterTableAManger(lot: Lot, x: number, z: number, e: number): void {
  pBoite(lot, BOIS_CLAIR, [1.3 * e, 0.08 * e, 0.88 * e], [x, 0.74 * e, z])
  for (const [sx, sz] of [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const) {
    pBoite(lot, BOIS, [0.09 * e, 0.7 * e, 0.09 * e], [x + sx * 0.55 * e, 0.35 * e, z + sz * 0.34 * e])
  }

  // Deux chaises de chaque grand cote, dossier tourne vers l'exterieur.
  for (const sz of [1, -1]) {
    for (const sx of [1, -1]) {
      const cx = x + sx * 0.32 * e
      const cz = z + sz * 0.72 * e
      pBoite(lot, BOIS, [0.11 * e, 0.44 * e, 0.11 * e], [cx, 0.22 * e, cz])
      pBoite(lot, BOIS_CLAIR, [0.42 * e, 0.07 * e, 0.42 * e], [cx, 0.47 * e, cz])
      pBoite(lot, BOIS_CLAIR, [0.42 * e, 0.46 * e, 0.07 * e], [cx, 0.73 * e, cz + sz * 0.19 * e])
    }
  }

  for (const sx of [1, -1]) {
    pCyl(lot, BLANC, 0.17 * e, 0.15 * e, 0.035 * e, 10, [x + sx * 0.38 * e, 0.8 * e, z])
  }
  pCyl(lot, 0x9fd4e8, 0.07 * e, 0.09 * e, 0.24 * e, 8, [x, 0.9 * e, z])
  pSphere(lot, 0xef5f7f, 0.09 * e, [x - 0.05 * e, 1.06 * e, z], undefined, 6, 4)
  pSphere(lot, COUSSIN, 0.08 * e, [x + 0.07 * e, 1.09 * e, z - 0.03 * e], undefined, 6, 4)
}

/**
 * LE PUPITRE: un bureau, une chaise, un cahier et un pot a crayons.
 *
 * Il prend exactement la place du canape (meme ancrage), et pour la meme
 * raison qu'ailleurs: c'est le coin qu'on habite en entrant. On s'y assoit
 * pour travailler — le tap dessus ouvre les exercices, comme le portable de la
 * villa a etage. Aucun panneau neuf n'a ete ecrit pour lui.
 */
function ajouterPupitre(lot: Lot, x: number, z: number, e: number): void {
  // Le plateau, legerement incline vers l'eleve: un pupitre plat se lit comme
  // une table basse en vue de dessus.
  pBoite(lot, BOIS_CLAIR, [1.2 * e, 0.07 * e, 0.72 * e], [x, 0.76 * e, z], [-0.09, 0, 0])
  pBoite(lot, BOIS, [1.24 * e, 0.1 * e, 0.16 * e], [x, 0.72 * e, z - 0.34 * e])
  for (const [sx, sz] of [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const) {
    pBoite(lot, BOIS_FONCE, [0.08 * e, 0.72 * e, 0.08 * e], [x + sx * 0.52 * e, 0.36 * e, z + sz * 0.28 * e])
  }

  // La chaise, tournee vers le plateau.
  const cz = z - 0.78 * e
  pBoite(lot, BOIS, [0.1 * e, 0.42 * e, 0.1 * e], [x, 0.21 * e, cz])
  pBoite(lot, BOIS_CLAIR, [0.46 * e, 0.07 * e, 0.44 * e], [x, 0.45 * e, cz])
  pBoite(lot, BOIS_CLAIR, [0.46 * e, 0.48 * e, 0.07 * e], [x, 0.72 * e, cz - 0.2 * e])

  // Un cahier ouvert et un pot a crayons: un plateau nu ne raconte rien, et
  // c'est ce detail qui distingue un pupitre d'une table a manger vue de haut.
  pBoite(lot, BLANC, [0.42 * e, 0.02 * e, 0.3 * e], [x - 0.16 * e, 0.81 * e, z], [-0.09, 0, 0])
  pBoite(lot, 0x4f7fb8, [0.44 * e, 0.02 * e, 0.32 * e], [x - 0.16 * e, 0.79 * e, z], [-0.09, 0, 0])
  pCyl(lot, 0xd8b45a, 0.09 * e, 0.09 * e, 0.16 * e, 8, [x + 0.42 * e, 0.86 * e, z + 0.06 * e])
  for (const dx of [-0.03, 0.02, 0.05]) {
    pCyl(lot, 0xe05a4a, 0.014 * e, 0.014 * e, 0.22 * e, 5, [x + (0.42 + dx) * e, 0.96 * e, z + 0.06 * e])
  }
}

/**
 * LA CLASSE: le tableau au fond, et deux rangees de petits pupitres.
 *
 * Du DECOR, comme le salon: rien ne s'y declenche. C'est ce qui fait qu'une
 * ecole se lit comme une ecole plutot que comme une grange avec un bureau
 * dedans — et c'est ce qui remplace la cuisine et le lit qu'on y trouvait.
 *
 * `l` et `p` servent a tenir les rangees dans la piece quelle que soit sa
 * taille: ecrire des demi-largeurs en dur ferait sortir les eleves par les
 * murs le jour ou l'emprise de l'ecole changera.
 */
function ajouterClasse(lot: Lot, x: number, z: number, e: number, l: number, p: number): void {
  // Le tableau, plaque contre le mur du fond (+z).
  const zMur = Math.min(z + 0.9 * e, p / 2 - 0.14)
  pBoite(lot, 0x2f4a3a, [Math.min(3.4 * e, l - 1.4), 1.3 * e, 0.07 * e], [x, 1.75 * e, zMur])
  pBoite(lot, BOIS, [Math.min(3.6 * e, l - 1.2), 0.1 * e, 0.11 * e], [x, 1.05 * e, zMur - 0.02 * e])
  // Une craie et une brosse sur la tablette: sans elles, le panneau vert se
  // lit comme une porte.
  pCyl(lot, BLANC, 0.03 * e, 0.03 * e, 0.2 * e, 6, [x - 0.5 * e, 1.13 * e, zMur - 0.05 * e], [0, 0, Math.PI / 2])
  pBoite(lot, 0xd8c8a8, [0.24 * e, 0.09 * e, 0.13 * e], [x + 0.6 * e, 1.14 * e, zMur - 0.05 * e])

  // Deux rangees de deux petits pupitres, face au tableau. Les positions sont
  // bornees a l'emprise: c'est ce qui les garde dans la salle.
  const ecartX = Math.min(1.15 * e, l / 2 - 1.1)
  for (const sx of [-1, 1]) {
    for (const [i, dz] of [-0.5, -1.9].entries()) {
      const px = x + sx * ecartX
      const pz = clamp(z + dz * e, -p / 2 + 0.9, p / 2 - 0.9)
      pBoite(lot, BOIS_CLAIR, [0.86 * e, 0.06 * e, 0.5 * e], [px, 0.6 * e, pz])
      for (const sz2 of [1, -1]) {
        pBoite(lot, BOIS_FONCE, [0.07 * e, 0.57 * e, 0.07 * e], [px, 0.29 * e, pz + sz2 * 0.19 * e])
      }
      // Le tabouret, derriere le pupitre (donc du cote -z, face au tableau).
      pBoite(lot, i % 2 === 0 ? TISSU : COUSSIN, [0.34 * e, 0.06 * e, 0.34 * e], [px, 0.4 * e, pz - 0.62 * e])
      pBoite(lot, BOIS_FONCE, [0.07 * e, 0.37 * e, 0.07 * e], [px, 0.19 * e, pz - 0.62 * e])
    }
  }
}

/**
 * LE FRONT DE TAILLE: la roche qu'on pioche, au fond de la galerie.
 *
 * C'est le SEUL meuble du jeu qu'on frappe, et il doit donc se lire d'un coup
 * d'oeil comme quelque chose qui se pioche: un massif de roche claire en
 * gradins, des veines de minerai posees sur ses plateaux, un tas d'eboulis a
 * son pied et une pioche appuyee dessus. Il est dessine AUTOUR de son ancrage
 * (`positionFilon`), qui est aussi le point vers lequel le bonhomme marche:
 * ce qu'on voit est exactement ce avec quoi on interagit.
 *
 * `l` borne la largeur du massif a la galerie: ecrire une demi-largeur en dur
 * la ferait sortir par les murs le jour ou l'emprise de la mine changera.
 */
function ajouterFilon(lot: Lot, x: number, z: number, e: number, l: number): void {
  /*
   * LA COULEUR EST LE SUJET, pas un detail. Une premiere version peignait le
   * massif dans le meme gris-brun sombre que le sol et les parois de la
   * galerie: en vue de dessus a 45 degres, il DISPARAISSAIT — des captures de
   * bout en bout l'ont montre tout de suite. La roche du filon est donc
   * nettement plus CLAIRE que le sol, ses gradins plus clairs encore, et les
   * veines gardent la couleur de l'objet `minerai` (shared/data/objets.ts) pour
   * qu'on reconnaisse dans la roche ce qui tombera dans le sac.
   */
  const ROCHE = 0x8a7f6d
  const VEINE = 0x6fd1c4
  const largeur = Math.min(4.6 * e, l - 1.0)
  const h = 1.6 * e

  /*
   * UN MASSIF EN GRADINS, PAS UNE FALAISE — et c'est la CAMERA qui l'impose.
   *
   * Son azimut est FIXE (45 degres); la rotation du batiment, elle, ne l'est
   * pas. Une paroi haute et mince plaquee contre le mur du fond ne montre donc
   * sa face gravee que pour deux rotations sur quatre: pour les deux autres,
   * l'oeil est du cote du mur et ne voit plus qu'une tranche — un liseré. Des
   * captures l'ont montre sans appel: la roche etait bien la, et elle ne se
   * voyait pas.
   *
   * Ce qui se lit TOUJOURS dans une vue de dessus a 45 degres, c'est un
   * DESSUS. Le filon est donc un massif large et profond, en deux gradins, dont
   * les plateaux portent les veines. Il est aussi plus bas qu'un bonhomme: on
   * voit par-dessus celui qui pioche au lieu de le perdre derriere la roche.
   */
  pBoite(lot, ROCHE, [largeur, h, 1.6 * e], [x, h / 2, z + 0.3 * e])
  pBoite(lot, teinter(ROCHE, 1.14), [largeur * 0.78, h * 0.55, 0.95 * e], [x, h * 0.28, z - 0.85 * e])
  pBoite(
    lot,
    teinter(ROCHE, 0.86),
    [largeur * 0.3, h * 0.42, 0.7 * e],
    [x - largeur * 0.34, h * 0.21, z - 1.05 * e],
    [0, 0.22, 0],
  )

  /*
   * LES VEINES SONT POSEES SUR LES PLATEAUX, et elles debordent: c'est ce que
   * la camera voit en premier, quelle que soit la rotation. A moitie enfouies
   * et petites, elles se lisaient comme des cailloux.
   */
  for (const [dx, dy, dz] of [
    [-0.3, 1.0, 0.35],
    [0.06, 1.0, 0.15],
    [0.32, 1.0, 0.45],
    [-0.2, 0.58, -0.85],
    [0.22, 0.58, -0.85],
  ] as const) {
    pSphere(lot, VEINE, 0.3 * e, [x + dx * largeur, dy * h, z + dz * e], [1.2, 0.7, 1.2], 7, 5)
  }

  // Les eboulis au pied de la paroi: c'est ce qui dit qu'on a deja pioche ici.
  for (const [dx, dz, r] of [
    [-0.36, -0.55, 0.26],
    [-0.04, -0.68, 0.2],
    [0.3, -0.5, 0.28],
  ] as const) {
    pSphere(lot, teinter(ROCHE, 0.82), r * e, [x + dx * largeur, r * 0.6 * e, z + dz * e], [1.3, 0.7, 1.1], 6, 4)
  }

  // La pioche, appuyee contre la roche. Une action qu'on ne voit pas venir est
  // une action qu'on ne trouve pas: l'outil pose la est l'invitation.
  const px = x + largeur * 0.46
  pTube(lot, BOIS, 0.05 * e, [px, 0.05 * e, z - 1.3 * e], [px - 0.2 * e, 1.7 * e, z - 0.9 * e])
  pBoite(lot, METAL, [0.66 * e, 0.1 * e, 0.12 * e], [px - 0.22 * e, 1.75 * e, z - 0.88 * e], [0, 0, 0.25])
}

/**
 * La version campement du meme plan: rechaud, sac de couchage, coin repos et
 * caisse-table. La tente merite un endroit ou s'asseoir et manger comme les
 * autres, mais pas un canape.
 */
function ajouterCampement(lot: Lot, m: Record<'cuisine' | 'lit' | 'salon' | 'table', Vec2>, e: number): void {
  const { cuisine, lit, salon, table } = m
  pBoite(lot, BOIS_CLAIR, [0.7 * e, 0.42 * e, 0.5 * e], [cuisine.x, 0.21 * e, cuisine.z])
  pBoite(lot, METAL, [0.42 * e, 0.1 * e, 0.34 * e], [cuisine.x, 0.47 * e, cuisine.z])
  pCyl(lot, teinter(METAL, 0.8), 0.11 * e, 0.1 * e, 0.14 * e, 8, [cuisine.x - 0.14 * e, 0.59 * e, cuisine.z])
  pCyl(lot, 0xe8d9b8, 0.09 * e, 0.07 * e, 0.2 * e, 6, [cuisine.x + 0.24 * e, 0.52 * e, cuisine.z + 0.05 * e])

  pBoite(lot, 0x7a5a38, [0.9 * e, 0.06 * e, 1.9 * e], [lit.x, 0.03 * e, lit.z])
  pBoite(lot, 0x4f7fb8, [0.78 * e, 0.22 * e, 1.75 * e], [lit.x, 0.14 * e, lit.z])
  pBoite(lot, BLANC, [0.6 * e, 0.14 * e, 0.36 * e], [lit.x, 0.3 * e, lit.z - 0.66 * e])

  // Coin repos: une natte et deux gros coussins.
  pPlaque(lot, TAPIS, 1.9 * e, 1.6 * e, [salon.x, 0.004, salon.z])
  for (const signe of [1, -1]) {
    pBoite(lot, signe > 0 ? COUSSIN : TISSU, [0.46 * e, 0.18 * e, 0.4 * e], [salon.x, 0.09 * e, salon.z + signe * 0.34 * e])
  }
  pCyl(lot, ABAT_JOUR, 0.1 * e, 0.12 * e, 0.22 * e, 8, [salon.x - 0.45 * e, 0.11 * e, salon.z - 0.4 * e])

  // Coin repas: une caisse retournee et deux rondins.
  pBoite(lot, BOIS_CLAIR, [0.78 * e, 0.44 * e, 0.6 * e], [table.x, 0.22 * e, table.z])
  pCyl(lot, BLANC, 0.15 * e, 0.13 * e, 0.04 * e, 10, [table.x, 0.46 * e, table.z])
  for (const signe of [1, -1]) {
    pCyl(lot, BOIS_FONCE, 0.17 * e, 0.18 * e, 0.34 * e, 8, [table.x + signe * 0.62 * e, 0.17 * e, table.z])
  }
}

/**
 * Tout ce qu'une maison contient, aux positions que la SIMULATION a decidees.
 *
 * On ramene chaque position en repere local au lieu de la recalculer: c'est ce
 * qui garantit que le canape est la ou la simulation croit qu'il est, sous les
 * quatre rotations, et que la table qu'on voit est celle a laquelle on mange.
 *
 * ET C'EST PAR `tournerInverse` QU'ON Y REVIENT. Le groupe du batiment porte
 * deja la rotation (`groupe.rotation.y = rot * PI/2`, qui applique exactement
 * `tourner`): soustraire le centre ne suffit pas, ca laisse une position DEJA
 * tournee, qui se faisait tourner une seconde fois. Resultat, pour les
 * rotations 1, 2 et 3 le comptoir dessine n'etait pas celui de la simulation —
 * on ouvrait le panneau de cuisine debout devant le lit.
 *
 * `solY` est la hauteur du plancher deja pose. Le tapis d'avant etait dessine a
 * y = 0.03 alors que le plancher monte a 0.12: il etait entierement noye dedans
 * et personne ne l'a jamais vu.
 */
function ajouterMeubles(lot: Lot, b: Batiment, l: number, p: number, style: StyleBatiment, solY: number): void {
  const local = (v: Vec2): Vec2 => tournerInverse(v.x - b.pos.x, v.z - b.pos.z, b.rot)
  const cuisine = local(positionCuisine(b))
  const lit = local(positionLit(b))
  const salon = local(positionSalon(b))
  const table = local(positionTable(b))
  const e = echelleMeuble(l, p)

  const debut = lot.length
  if (style.meubles === 'camp') {
    ajouterCampement(lot, { cuisine, lit, salon, table }, e)
  } else {
    /*
     * C'EST LA DONNEE QUI DIT CE QU'IL Y A DEDANS, pas une liste ecrite ici.
     *
     * Les quatre meubles etaient poses pour TOUS les habitables, sans
     * condition: l'ecole avait donc une cuisine et un lit — on entrait en
     * classe et on pouvait s'y coucher. `meublesDe` est la MEME table que
     * celle que consulte `meubleProche` cote simulation: ce qu'on voit est
     * exactement ce qu'on peut toucher, ce qui est la regle de tout le projet.
     */
    const presents = meublesDe(b.kind)
    if (presents.includes('cuisine')) ajouterCuisine(lot, cuisine.x, cuisine.z, e)
    if (presents.includes('lit')) ajouterLit(lot, lit.x, lit.z, e)
    if (presents.includes('salon')) ajouterSalon(lot, salon.x, salon.z, e, l, p)
    if (presents.includes('table')) ajouterTableAManger(lot, table.x, table.z, e)
    if (presents.includes('pupitre')) {
      const pu = local(positionMeuble(b, 'pupitre'))
      ajouterPupitre(lot, pu.x, pu.z, e)
    }
    if (presents.includes('classe')) {
      const cl = local(positionMeuble(b, 'classe'))
      ajouterClasse(lot, cl.x, cl.z, e, l, p)
    }
    if (presents.includes('filon')) {
      const fi = local(positionMeuble(b, 'filon'))
      ajouterFilon(lot, fi.x, fi.z, e, l)
    }
  }
  grouper(lot, debut, tr([0, solY, 0]))
}

// -------------------------------------------------------------- habitations

/**
 * Chantier d'une habitation: chaque lot deviendra UNE maille.
 *
 * `sol` (plancher et mobilier) ne s'efface jamais, `toit` s'efface quand on
 * entre, chaque face de mur s'efface separement selon l'angle de la camera, et
 * `vitres` s'allume la nuit — puis suit le toit, faute de pouvoir suivre chaque
 * mur.
 */
interface ChantierMaison {
  m: Maillage
  b: Batiment
  style: StyleBatiment
  /** Le sol, pour ce qui doit l'epouser (la piste d'une aerogare). */
  terrain: ITerrain
  l: number
  p: number
  sol: Lot
  toit: Lot
  /** Tout le premier niveau, en un seul lot donc un seul appel de dessin. */
  etage: Lot
  /** Tout le DEUXIEME niveau de l'hotel, meme regle: un lot, un appel. */
  etage2: Lot
  /**
   * TOUTE L'EAU du modele: celle du spa de la terrasse, celle du bassin du zoo.
   *
   * Une maille de plus, mais UNE SEULE, et seulement pour les modeles qui ont
   * de l'eau: un materiau cree opaque ne devient jamais transparent, quoi qu'on
   * ecrive ensuite dans son opacite. La piscine emploie exactement le meme lot
   * a part (`translucide`) et pour exactement la meme raison.
   */
  eau: Lot
  /** L'eau du deuxieme niveau de l'hotel: le bassin aux poissons, la cascade. */
  eau2: Lot
  /**
   * L'EAU QUI EST AU SOL D'UN MODELE A ETAGE — le grand bassin du parc.
   *
   * Un TROISIEME lot d'eau, et il a fallu les trois. `eau` suit le fondu du
   * premier niveau (le spa de la villa EST la terrasse: il doit s'effacer avec
   * elle), `eau2` celui du deuxieme, et celui-ci ne s'efface JAMAIS.
   *
   * La regle deja ecrite plus bas — « seule l'eau d'un ETAGE suit le fondu » —
   * avait un trou: elle rangeait dans `eau` toute l'eau d'un modele a etage,
   * sans regarder a quelle hauteur elle se trouve. Le bassin du zoo y avait
   * echappe parce que le zoo n'a pas d'etage; le bassin du parc, lui, est trois
   * metres SOUS une passerelle. Le laisser dans `eau` l'aurait fait disparaitre
   * a l'instant precis ou l'on monte se jeter dedans — c'est-a-dire l'endroit
   * meme ou les glissades arrivent. La lecon des palmiers de l'hotel,
   * appliquee a de l'eau.
   *
   * Un lot a part, et non `dehors`, parce qu'un materiau cree opaque ne devient
   * jamais transparent: `appliquerOpacite` ECRASE l'opacite, et de l'eau rangee
   * avec le decor serait un carrelage bleu.
   */
  eauSol: Lot
  vitres: Lot
  /**
   * Ce qui vit DEHORS, autour du batiment, et ne s'efface jamais: les palmiers
   * de l'hotel, la vasque ou sa cascade retombe. Le lot `sol` d'un modele a
   * etage part dans `bas` et disparait quand on monte — un palmier du jardin
   * qui s'evanouit parce qu'on est sur le toit se lirait comme un bogue.
   */
  dehors: Lot
  faces: { avant: Lot; arriere: Lot; gauche: Lot; droite: Lot }
  cMurs: number
  cToit: number
  cPorte: number
}

/**
 * LA TERRASSE: le deuxieme niveau, dessine en repere LOCAL du batiment.
 *
 * Rien ici n'est ecrit en coordonnees du monde et aucun quart de tour n'est
 * traite a la main: le groupe entier porte deja la rotation du batiment. C'est
 * la regle du projet, et le seul moyen pour que le spa dessine soit celui dans
 * lequel la simulation croit qu'on trempe.
 *
 * TOUTES LES POSITIONS VIENNENT DE `PLAN_ETAGE` (shared/sim/batiments.ts), qui
 * sert aussi a la collision et aux cibles: le bureau qu'on voit est le bureau
 * dont les murs repoussent, et le portable qu'on tape est celui qui est pose
 * sur la table. Recopier ces nombres ici serait la meilleure facon de les faire
 * diverger.
 *
 * L'escalier n'a PAS de collision, comme le mobilier et la margelle d'une
 * piscine: il se traverse. Ce qui compte, c'est qu'on le voie et qu'on puisse
 * le viser.
 */
function ajouterEtage(c: ChantierMaison): void {
  const lot = c.etage
  const l = c.l
  const p = c.p
  const Y = HAUTEUR_ETAGE
  const cDalle = laver(c.cToit, 0.15)
  const cRampe = laver(c.cToit, 0.3)

  // La dalle et son acrotere SANS ouverture: c'est ce que `resoudreMurs`
  // resout a l'etage, et une porte la-haut ferait sortir dans le vide. La meme
  // fonction sert aux deux dalles de l'hotel.
  dalleEtAcrotere(lot, l, p, Y, cDalle, c.cMurs, cRampe)

  // -------------------------------------------------------- tete d'escalier
  const esc = { x: l * PLAN_ETAGE.escalier.x, z: p * PLAN_ETAGE.escalier.z }
  // La tremie, peinte et non percee: on ne troue pas une dalle avec des boites,
  // et un pan sombre se lit exactement comme une ouverture en vue de dessus.
  pPlaque(lot, 0x342c24, 1.9, 1.7, [esc.x, Y + 0.025, esc.z])
  for (let i = 0; i < 3; i++) {
    pBoite(lot, BOIS_CLAIR, [0.42, 0.13, 1.6], [esc.x + 0.55 - i * 0.44, Y - 0.06 - i * 0.2, esc.z])
  }
  // Garde-corps sur les trois cotes fermes: on descend par le quatrieme.
  for (const [dx, dz, dl, dp] of [
    [0, -0.9, 2.0, 0.09],
    [0, 0.9, 2.0, 0.09],
    [-1.0, 0, 0.09, 1.9],
  ] as const) {
    pBoite(lot, cRampe, [dl, 0.08, dp], [esc.x + dx, Y + 0.98, esc.z + dz])
    pBoite(lot, METAL, [0.06, 0.95, 0.06], [esc.x + dx, Y + 0.48, esc.z + dz])
  }

  // --------------------------------------------------------- la grande piece
  //
  // ELLE FAIT PRESQUE TOUTE LA LARGEUR DU BATIMENT, et les deux tiers de sa
  // profondeur. Ce qui reste devant elle est le PONT, a ciel ouvert, avec le
  // spa et les transats. La circulation est celle d'un vrai etage: on monte
  // l'escalier, on arrive dans la piece, on sort sur le pont par la baie.
  //
  // Pas de toit sur la piece, une CORNICHE seulement. C'est le meme choix que
  // pour l'ancien bureau, et pour la meme raison: un toit ferme, dessine dans
  // le lot de l'etage, cacherait tout ce qu'on vient d'y voir des qu'on y
  // monte — le fondu efface l'etage quand on est EN BAS, jamais quand on est
  // dedans. En vue orthographique a 45 degres, une piece a ciel ouvert se lit
  // exactement comme une piece dont on aurait retire le toit.
  const bu = boiteEtage(c.b.kind, PLAN_ETAGE.piece)
  const hBur = 2.4
  const hAllege = 0.85
  const hPorte = 2.0
  const cVitre = 0x9fd4e8
  // Face -z: LA BAIE. Meme convention que la porte d'une maison, et surtout
  // meme largeur que celle dont `murEtage` laisse passer le bonhomme — le trou
  // dessine et le trou de la collision sont le meme trou.
  const lBaie = LARGEUR_BAIE_ETAGE
  const zAvant = bu.cz - bu.hz + EP_MUR / 2
  const cote = (bu.hx * 2 - lBaie) / 2
  for (const signe of [1, -1]) {
    // Cote plein: allege, vitrage, linteau — la piece donne sur le pont.
    const x = bu.cx + (signe * (lBaie + cote)) / 2
    pBoite(lot, c.cMurs, [cote, hAllege, EP_MUR], [x, Y + hAllege / 2, zAvant])
    const hVitre = hBur - hAllege - 0.22
    pBoite(lot, cVitre, [cote - 0.28, hVitre, EP_MUR - 0.06], [x, Y + hAllege + hVitre / 2, zAvant])
    pBoite(lot, c.cMurs, [cote, 0.22, EP_MUR], [x, Y + hBur - 0.11, zAvant])
  }
  pBoite(lot, c.cMurs, [lBaie, hBur - hPorte, EP_MUR], [bu.cx, Y + hPorte + (hBur - hPorte) / 2, zAvant])
  for (const signe of [1, -1]) {
    pBoite(lot, c.cPorte, [0.1, hPorte, 0.12], [bu.cx + signe * (lBaie / 2 + 0.05), Y + hPorte / 2, zAvant])
  }
  pBoite(lot, c.cPorte, [lBaie + 0.2, 0.11, 0.12], [bu.cx, Y + hPorte + 0.05, zAvant])
  // Un panneau coulissant repousse d'un cote: on lit tout de suite que la baie
  // s'ouvre, sans qu'un battant vienne barrer le passage qu'on doit franchir.
  pBoite(lot, teinter(cVitre, 0.95), [lBaie * 0.46, hPorte - 0.12, 0.05], [
    bu.cx - lBaie * 0.25,
    Y + (hPorte - 0.12) / 2 + 0.06,
    zAvant - 0.06,
  ])

  // Les trois autres faces: une allege pleine, une baie vitree, un linteau.
  const pans: [number, number, number, number][] = [
    [bu.cx, bu.cz + bu.hz - EP_MUR / 2, bu.hx * 2, EP_MUR],
    [bu.cx - bu.hx + EP_MUR / 2, bu.cz, EP_MUR, bu.hz * 2],
    [bu.cx + bu.hx - EP_MUR / 2, bu.cz, EP_MUR, bu.hz * 2],
  ]
  for (const [x, z, dl, dp] of pans) {
    pBoite(lot, c.cMurs, [dl, hAllege, dp], [x, Y + hAllege / 2, z])
    const hBaie = hBur - hAllege - 0.22
    pBoite(lot, cVitre, [dl - 0.28, hBaie, dp - 0.06], [x, Y + hAllege + hBaie / 2, z])
    pBoite(lot, c.cMurs, [dl, 0.22, dp], [x, Y + hBur - 0.11, z])
  }
  // La corniche, tout autour.
  for (const signe of [1, -1]) {
    pBoite(lot, cRampe, [bu.hx * 2 + 0.24, 0.12, 0.24], [bu.cx, Y + hBur + 0.06, bu.cz + signe * bu.hz])
    pBoite(lot, cRampe, [0.24, 0.12, bu.hz * 2 + 0.24], [bu.cx + signe * bu.hx, Y + hBur + 0.06, bu.cz])
  }
  // Un tapis et un canape au fond de la piece: 11 x 5 m entierement vides se
  // lisent comme un chantier, pas comme un etage habite.
  const salonX = bu.cx - bu.hx * 0.5
  const salonZ = bu.cz + bu.hz * 0.35
  pPlaque(lot, TAPIS, 2.6, 2.0, [salonX, Y + 0.02, salonZ])
  pBoite(lot, teinter(TISSU, 0.8), [2.0, 0.34, 0.8], [salonX, Y + 0.19, salonZ + 0.75])
  pBoite(lot, TISSU, [2.0, 0.6, 0.22], [salonX, Y + 0.6, salonZ + 1.05])
  for (const signe of [1, -1]) {
    pBoite(lot, COUSSIN, [0.34, 0.3, 0.14], [salonX + signe * 0.7, Y + 0.5, salonZ + 0.92], [0.3, 0, 0])
  }
  pBoite(lot, BOIS_CLAIR, [1.1, 0.07, 0.6], [salonX, Y + 0.4, salonZ - 0.3])

  // ------------------------------------------------- la table et le portable
  const po = { x: l * PLAN_ETAGE.portable.x, z: p * PLAN_ETAGE.portable.z }
  pBoite(lot, BOIS_CLAIR, [1.7, 0.07, 0.78], [po.x, Y + 0.74, po.z])
  for (const [sx, sz] of [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const) {
    pBoite(lot, METAL, [0.06, 0.72, 0.06], [po.x + sx * 0.76, Y + 0.36, po.z + sz * 0.32])
  }
  // La chaise, tournee vers la table (donc vers +z, le portable etant au fond).
  pBoite(lot, teinter(TISSU, 0.85), [0.48, 0.06, 0.46], [po.x, Y + 0.46, po.z - 0.75])
  pBoite(lot, TISSU, [0.48, 0.5, 0.07], [po.x, Y + 0.72, po.z - 0.97])
  for (const [sx, sz] of [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const) {
    pBoite(lot, METAL, [0.05, 0.44, 0.05], [po.x + sx * 0.2, Y + 0.22, po.z - 0.75 + sz * 0.19])
  }
  // LE PORTABLE. Clavier a plat, ecran releve vers celui qui s'assoit.
  pBoite(lot, teinter(METAL, 0.85), [0.4, 0.03, 0.28], [po.x, Y + 0.79, po.z])
  pBoite(lot, teinter(METAL, 0.7), [0.36, 0.02, 0.2], [po.x, Y + 0.81, po.z + 0.01])
  pBoite(lot, teinter(METAL, 0.85), [0.4, 0.27, 0.02], [po.x, Y + 0.93, po.z + 0.14], [0.3, 0, 0])
  pBoite(lot, cVitre, [0.35, 0.22, 0.01], [po.x, Y + 0.93, po.z + 0.12], [0.3, 0, 0])
  // Une tasse et un carnet: une table nue ne raconte rien.
  pCyl(lot, BLANC, 0.05, 0.045, 0.09, 8, [po.x + 0.6, Y + 0.82, po.z - 0.1])
  pBoite(lot, 0xef8f5f, [0.22, 0.03, 0.16], [po.x - 0.62, Y + 0.79, po.z - 0.06], [0, 0.3, 0])

  // ----------------------------------------------------------------- le spa
  //
  // ENCASTRE dans la dalle, et pas pose dessus: rien ne bloque le joueur (pas
  // plus que la margelle d'une piscine), donc un bassin sureleve se traverserait
  // comme un mirage. Encastre, on y entre d'un pas, ce qui est exactement ce
  // que la simulation raconte (`dansSpa`).
  const sp = boiteEtage(c.b.kind, PLAN_ETAGE.spa)
  const fond = Y - 0.34
  pBoite(lot, 0x2f6b7a, [sp.hx * 2, 0.05, sp.hz * 2], [sp.cx, fond, sp.cz])
  for (const signe of [1, -1]) {
    pBoite(lot, 0x3f8fa5, [sp.hx * 2, 0.36, 0.1], [sp.cx, Y - 0.18, sp.cz + signe * (sp.hz - 0.05)])
    pBoite(lot, 0x3f8fa5, [0.1, 0.36, sp.hz * 2], [sp.cx + signe * (sp.hx - 0.05), Y - 0.18, sp.cz])
    // La margelle, juste assez haute pour se lire, assez basse pour s'enjamber.
    pBoite(lot, cRampe, [sp.hx * 2 + 0.3, 0.12, 0.3], [sp.cx, Y + 0.06, sp.cz + signe * (sp.hz + 0.1)])
    pBoite(lot, cRampe, [0.3, 0.12, sp.hz * 2 + 0.3], [sp.cx + signe * (sp.hx + 0.1), Y + 0.06, sp.cz])
  }
  /*
   * L'EAU, ET ELLE A UNE EPAISSEUR.
   *
   * C'etait une simple plaque opaque posee au fond: de dessus, le spa se
   * lisait comme un carrelage bleu, et le proprietaire a demande « de l'eau
   * dans le spa ». Un VOLUME translucide, lui, laisse voir le fond du bassin
   * a travers et se comporte comme la piscine, qui emploie deja le lot
   * `translucide` pour exactement la meme raison.
   *
   * Il part dans `c.translucideEtage` et non dans `lot`: un materiau cree
   * opaque ne devient jamais transparent, quoi qu'on ecrive ensuite dans son
   * opacite — c'est la meme regle que pour le plancher du bas.
   */
  const eau = c.eau
  const hEau = 0.3
  pBoite(eau, 0x4fc3e8, [sp.hx * 2 - 0.22, hEau, sp.hz * 2 - 0.22], [sp.cx, Y - 0.09 - hEau / 2 + 0.02, sp.cz])
  // Deux bouillons a la surface: sans eux, une eau immobile ne se lit pas comme
  // un bain a REMOUS.
  for (const signe of [1, -1]) {
    pSphere(eau, 0xd8f4ff, 0.12, [sp.cx + signe * sp.hx * 0.45, Y - 0.06, sp.cz + signe * sp.hz * 0.3], [1, 0.5, 1], 6, 4)
    pSphere(eau, 0xd8f4ff, 0.08, [sp.cx - signe * sp.hx * 0.2, Y - 0.06, sp.cz + signe * sp.hz * 0.55], [1, 0.5, 1], 6, 4)
  }

  // ------------------------------------------------------ le coin detente
  // Deux transats sur le PONT, a cote du spa et face au large. Ils etaient
  // places par rapport a la terrasse entiere: la ou la piece se dresse
  // maintenant, ils auraient traverse son mur.
  for (const [i, signe] of [1, -1].entries()) {
    const tx = -l * 0.22 + i * 0
    const tz = sp.cz + signe * 1.4
    pBoite(lot, BOIS_CLAIR, [1.9, 0.08, 0.62], [tx, Y + 0.34, tz], [0, 0, 0.06])
    pBoite(lot, teinter(TISSU_CLAIR, 1.0), [0.7, 0.06, 0.58], [tx - 0.72, Y + 0.52, tz], [0, 0, -0.5])
    for (const sx of [1, -1]) {
      pCyl(lot, METAL, 0.04, 0.04, 0.3, 6, [tx + sx * 0.8, Y + 0.15, tz])
    }
  }
}

/**
 * La dalle d'un niveau et son acrotere, communs a tous les etages.
 *
 * `avecDalle` a faux ne pose QUE l'acrotere: c'est ce dont la passerelle du
 * parc aquatique a besoin, elle qui borde toute l'emprise (parce que
 * `murEtage` la borde) mais ne la couvre pas d'un plancher plein — voir
 * `ajouterPasserelle`. Un parametre plutot qu'une seconde ecriture de
 * l'acrotere: deux endroits qui disent la meme chose finissent toujours par ne
 * plus etre d'accord, et celui-ci doit rester au millimetre celui que
 * `resoudreMurs` applique.
 */
function dalleEtAcrotere(
  lot: Lot,
  l: number,
  p: number,
  Y: number,
  cDalle: number,
  cMurs: number,
  cRampe: number,
  avecDalle = true,
): void {
  const epDalle = 0.26
  if (avecDalle) {
    pBoite(lot, cDalle, [l, epDalle, p], [0, Y - epDalle / 2, 0])
    pPlaque(lot, laver(cDalle, 0.25), l - 0.12, p - 0.12, [0, Y + 0.012, 0])
  }
  const hPar = 1.0
  const ep = EPAISSEUR_PARAPET
  for (const signe of [1, -1]) {
    pBoite(lot, cMurs, [ep, hPar, p], [(signe * (l - ep)) / 2, Y + hPar / 2, 0])
    pBoite(lot, cMurs, [l - ep * 2, hPar, ep], [0, Y + hPar / 2, (signe * (p - ep)) / 2])
    pBoite(lot, cRampe, [ep + 0.12, 0.09, p + 0.12], [(signe * (l - ep)) / 2, Y + hPar + 0.045, 0])
    pBoite(lot, cRampe, [l - ep * 2, 0.09, ep + 0.12], [0, Y + hPar + 0.045, (signe * (p - ep)) / 2])
  }
}

/**
 * Une piece vitree posee sur une dalle, en repere local du batiment.
 *
 * La MEME grammaire que la grande piece de la villa: alleges, vitrages,
 * linteau, et la porte sur la face -z — la largeur du trou dessine vient de la
 * piece elle-meme, celle-la meme que `murEtage` laisse passer. `grandesBaies`
 * abaisse les alleges des trois faces pleines: c'est le mur-fenetre de la
 * salle a manger, celui qu'on regarde en mangeant.
 */
function pieceVitreeEtage(
  c: ChantierMaison,
  lot: Lot,
  bo: { cx: number; cz: number; hx: number; hz: number },
  Y: number,
  hMur: number,
  porte: number,
  grandesBaies: boolean,
  corniche: boolean,
): void {
  const cVitre = 0x9fd4e8
  const cRampe = laver(c.cToit, 0.3)
  const hAllege = grandesBaies ? 0.38 : 0.85
  const hPorte = 2.0
  const zAvant = bo.cz - bo.hz + EP_MUR / 2

  // Face -z: la porte, encadree de deux pans vitres.
  const cote = (bo.hx * 2 - porte) / 2
  for (const signe of [1, -1]) {
    const x = bo.cx + (signe * (porte + cote)) / 2
    pBoite(lot, c.cMurs, [cote, hAllege, EP_MUR], [x, Y + hAllege / 2, zAvant])
    const hVitre = hMur - hAllege - 0.22
    pBoite(lot, cVitre, [Math.max(0.05, cote - 0.28), hVitre, EP_MUR - 0.06], [x, Y + hAllege + hVitre / 2, zAvant])
    pBoite(lot, c.cMurs, [cote, 0.22, EP_MUR], [x, Y + hMur - 0.11, zAvant])
  }
  pBoite(lot, c.cMurs, [porte, hMur - hPorte, EP_MUR], [bo.cx, Y + hPorte + (hMur - hPorte) / 2, zAvant])
  for (const signe of [1, -1]) {
    pBoite(lot, c.cPorte, [0.1, hPorte, 0.12], [bo.cx + signe * (porte / 2 + 0.05), Y + hPorte / 2, zAvant])
  }
  pBoite(lot, c.cPorte, [porte + 0.2, 0.11, 0.12], [bo.cx, Y + hPorte + 0.05, zAvant])

  // Les trois autres faces: allege, grand vitrage, linteau.
  const pans: [number, number, number, number][] = [
    [bo.cx, bo.cz + bo.hz - EP_MUR / 2, bo.hx * 2, EP_MUR],
    [bo.cx - bo.hx + EP_MUR / 2, bo.cz, EP_MUR, bo.hz * 2],
    [bo.cx + bo.hx - EP_MUR / 2, bo.cz, EP_MUR, bo.hz * 2],
  ]
  for (const [x, z, dl, dp] of pans) {
    pBoite(lot, c.cMurs, [dl, hAllege, dp], [x, Y + hAllege / 2, z])
    const hBaie = hMur - hAllege - 0.22
    pBoite(lot, cVitre, [dl - 0.28, hBaie, dp - 0.06], [x, Y + hAllege + hBaie / 2, z])
    pBoite(lot, c.cMurs, [dl, 0.22, dp], [x, Y + hMur - 0.11, z])
  }
  if (corniche) {
    for (const signe of [1, -1]) {
      pBoite(lot, cRampe, [bo.hx * 2 + 0.24, 0.12, 0.24], [bo.cx, Y + hMur + 0.06, bo.cz + signe * bo.hz])
      pBoite(lot, cRampe, [0.24, 0.12, bo.hz * 2 + 0.24], [bo.cx + signe * bo.hx, Y + hMur + 0.06, bo.cz])
    }
  }
}

/**
 * La colonne d'ascenseur, un troncon par niveau: cadre, portes coulissantes en
 * metal, plaque d'appel. `ouverture` entrouvre les battants — au
 * rez-de-chaussee, une cabine fermee ne dit pas « on peut monter ici ».
 */
function cabineAscenseur(lot: Lot, x: number, z: number, Y: number, h: number, cMurs: number, ouverture: number): void {
  const L = 1.7
  const P = 1.3
  const zPorte = z - P / 2
  // Les trois parois pleines et le plafond du troncon.
  pBoite(lot, teinter(cMurs, 0.92), [L, h, 0.12], [x, Y + h / 2, z + P / 2 - 0.06])
  for (const signe of [1, -1]) {
    pBoite(lot, teinter(cMurs, 0.92), [0.12, h, P], [x + (signe * (L - 0.12)) / 2, Y + h / 2, z])
  }
  pBoite(lot, teinter(cMurs, 0.85), [L + 0.16, 0.14, P + 0.16], [x, Y + h + 0.07, z])
  // L'encadrement de la porte, et son bandeau lumineux.
  const hPorte = 2.1
  for (const signe of [1, -1]) {
    pBoite(lot, METAL, [0.1, hPorte + 0.12, 0.14], [x + signe * 0.62, Y + (hPorte + 0.12) / 2, zPorte])
  }
  pBoite(lot, METAL, [1.34, 0.12, 0.14], [x, Y + hPorte + 0.06, zPorte])
  pBoite(lot, 0xf5d13a, [0.5, 0.09, 0.06], [x, Y + hPorte + 0.24, zPorte - 0.02])
  // Les deux battants, entrouverts de `ouverture` metres.
  for (const signe of [1, -1]) {
    pBoite(lot, teinter(METAL, 1.12), [0.56, hPorte, 0.06], [
      x + signe * (0.29 + ouverture / 2),
      Y + hPorte / 2,
      zPorte + 0.02,
    ])
  }
  // La plaque d'appel et son bouton: c'est ELLE qu'on vient toucher.
  pBoite(lot, teinter(METAL, 0.9), [0.16, 0.34, 0.05], [x + 0.78, Y + 1.15, zPorte - 0.02])
  pSphere(lot, 0xffe9a8, 0.035, [x + 0.78, Y + 1.15, zPorte - 0.06], undefined, 6, 4)
}

/** Un palmier procedural: tronc incline, palmes en couronne, noix de coco. */
function palmier(lot: Lot, x: number, y: number, z: number, h: number, phase: number): void {
  const TRONC = 0x9a7648
  const segments = 4
  const dirx = Math.cos(phase)
  const dirz = Math.sin(phase)
  const lean = 0.14
  for (let i = 0; i < segments; i++) {
    const y0 = y + (h / segments) * i
    pCyl(
      lot,
      teinter(TRONC, 1 - i * 0.04),
      0.15 - i * 0.02,
      0.18 - i * 0.02,
      h / segments + 0.1,
      6,
      [x + dirx * lean * i, y0 + h / segments / 2, z + dirz * lean * i],
      [dirz * 0.1, 0, -dirx * 0.1],
    )
  }
  const tx = x + dirx * lean * (segments - 1)
  const tz = z + dirz * lean * (segments - 1)
  const ty = y + h
  // La couronne: six palmes qui retombent tout autour.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + phase
    const fx = Math.cos(a)
    const fz = Math.sin(a)
    pBoite(lot, i % 2 === 0 ? 0x2f7d3a : 0x3f8f4a, [1.9, 0.05, 0.44], [tx + fx * 0.8, ty + 0.12, tz + fz * 0.8], [0, -a, -0.5])
  }
  pSphere(lot, 0x6b4a2a, 0.11, [tx + 0.12, ty - 0.1, tz], undefined, 6, 4)
  pSphere(lot, 0x6b4a2a, 0.1, [tx - 0.1, ty - 0.14, tz + 0.1], undefined, 6, 4)
}

/**
 * LES NIVEAUX DE L'HOTEL — deux dalles au-dessus du rez-de-chaussee, en repere
 * LOCAL du batiment, exactement comme la terrasse de la villa.
 *
 * TOUTES LES POSITIONS VIENNENT DU PLAN (shared/data/hotel.ts), le meme que
 * `murEtage` resout et que les cibles interrogent: la piece qu'on voit est la
 * piece qui repousse, le portable qu'on tape est celui qui est pose sur le
 * bureau, et le bassin est la ou les tests jurent qu'il est. Recopier ces
 * nombres ici serait la meilleure facon de les faire diverger.
 *
 * Niveau 1 (`c.etage`): le bureau et sa terrasse a gauche, la salle a manger
 * vitree a droite. Ses pieces montent jusqu'a la dalle du dessus: ce sont
 * elles qui la portent.
 * Niveau 2 (`c.etage2`): le salon de lecture a gauche — a ciel ouvert, comme
 * la piece de la villa, pour que le fondu fonctionne — et le pont de droite
 * avec le bassin aux poissons, son palmier, et la cascade qui tombe le long de
 * la facade jusqu'a la vasque du jardin (`c.dehors`).
 */
/**
 * LA TETE D'ESCALIER d'un etage: la tremie, les marches, le garde-corps.
 *
 * Extraite de la terrasse de la villa mot pour mot, parce que l'aerogare en a
 * exactement une pareille: une deuxieme ecriture aurait fini par ne plus
 * deboucher au meme endroit que `positionAcces`, et l'on serait monte a cote de
 * l'escalier.
 */
function teteEscalier(lot: Lot, x: number, z: number, Y: number, cRampe: number): void {
  pPlaque(lot, 0x342c24, 1.9, 1.7, [x, Y + 0.025, z])
  for (let i = 0; i < 3; i++) {
    pBoite(lot, BOIS_CLAIR, [0.42, 0.13, 1.6], [x + 0.55 - i * 0.44, Y - 0.06 - i * 0.2, z])
  }
  for (const [dx, dz, dl, dp] of [
    [0, -0.9, 2.0, 0.09],
    [0, 0.9, 2.0, 0.09],
    [-1.0, 0, 0.09, 1.9],
  ] as const) {
    pBoite(lot, cRampe, [dl, 0.08, dp], [x + dx, Y + 0.98, z + dz])
    pBoite(lot, METAL, [0.06, 0.95, 0.06], [x + dx, Y + 0.48, z + dz])
  }
}

/**
 * HAUTEUR DU DEPART d'une glissade, en metres au-dessus de la base du parc.
 *
 * UN DEMI-METRE AU-DESSUS DE LA PASSERELLE, et ce n'est pas decoratif: c'est
 * ce qui, avec l'amorce plate ci-dessous, tient le toboggan AU-DESSUS du
 * plancher jusqu'a ce qu'il l'ait quitte. Les seuils sont a deux metres du bord
 * de l'antenne (leurs positions viennent du plan), donc un toboggan qui
 * plongerait des le premier metre passerait sous le plancher et l'on verrait
 * une glissade commencer dans le vide — verifie en REGARDANT, pas en relisant
 * des coordonnees: la premiere version le faisait, et deux des trois toboggans
 * etaient invisibles depuis la passerelle.
 *
 * Le podium qui rattrape ces cinquante centimetres est dessine sous le seuil,
 * avec sa marche: le bonhomme y monte d'un pas au moment ou il s'elance.
 */
export const HAUTEUR_DEPART_GLISSADE = HAUTEUR_ETAGE + 0.5

/**
 * Hauteur de l'arrivee: a fleur de l'eau du bassin, pas au fond.
 *
 * L'eau du bassin monte a 0,35 m; on s'arrete un peu au-dessus pour que la
 * derniere section du tube plonge DANS l'eau au lieu d'etre enterree dans le
 * dallage.
 */
export const HAUTEUR_ARRIVEE_GLISSADE = 0.5

/**
 * Part de la longueur pendant laquelle le toboggan reste a sa hauteur de
 * depart, avant de plonger: c'est la table d'elan qu'a toute vraie glissade,
 * et ici c'est aussi ce qui la sort de sous le plancher.
 */
const AMORCE_GLISSADE = 0.16

/**
 * HAUTEUR DU TOBOGGAN apres la fraction `t` de sa longueur (0 en haut, 1 en bas).
 *
 * ELLE N'EXISTE QUE POUR L'OEIL, et c'est ecrit noir sur blanc dans
 * shared/sim/batiments.ts: la simulation ne connait que des niveaux ENTIERS, et
 * le client est libre d'interpoler entre les deux tant qu'il ne touche pas a
 * (x, z). Rien de ce qui suit ne voyage sur le fil, rien ne se rejoue au
 * serveur.
 *
 * Mais elle doit etre LA MEME pour le toboggan dessine et pour le bonhomme qui
 * le descend, sinon on le verrait glisser a cote de sa glissade. D'ou une
 * fonction exportee, appelee par ce fichier ET par la boucle de jeu — deux
 * endroits qui diraient la meme chose chacun de son cote finiraient par ne plus
 * etre d'accord, c'est le defaut que ce projet paie le plus souvent.
 *
 * Le profil est un `smoothstep`: doux au depart, franc au milieu, doux a
 * l'arrivee. C'est la forme d'un vrai toboggan, et surtout une droite ferait
 * entrer le bonhomme dans l'eau en piquant du nez.
 */
export function hauteurGlissade(t: number): number {
  const u = clamp((clamp(t, 0, 1) - AMORCE_GLISSADE) / (1 - AMORCE_GLISSADE), 0, 1)
  const lisse = u * u * (3 - 2 * u)
  return HAUTEUR_DEPART_GLISSADE + (HAUTEUR_ARRIVEE_GLISSADE - HAUTEUR_DEPART_GLISSADE) * lisse
}

/** Les trois teintes des toboggans: on les distingue de loin, et on les nomme. */
const COULEURS_GLISSADE = [0xf2564a, 0x3fa9dc, 0xf5c53f] as const

/**
 * LES GLISSADES, du seuil sur la passerelle jusqu'a l'eau du bassin.
 *
 * ELLES NE SONT DANS AUCUN LOT DE FONDU, et c'est le point delicat. Une
 * glissade RELIE deux niveaux: son seuil n'existe que sur la passerelle, son
 * arrivee que dans le bassin d'en bas. La ranger avec l'etage l'effacerait des
 * qu'on marche au bord du bassin — c'est-a-dire a l'endroit precis d'ou l'on
 * regarde quelqu'un descendre; la ranger avec le rez-de-chaussee l'effacerait
 * des qu'on monte la prendre. Elle va donc dans `dehors`, le lot qui ne
 * s'efface jamais, comme les palmiers et la vasque de la cascade de l'hotel.
 *
 * Tout est en repere LOCAL: le groupe porte deja le quart de tour du batiment.
 * Les deux extremites viennent du PLAN (`glissadesDe`), les memes fractions que
 * `cheminGlissade` rend a la simulation: le toboggan dessine est exactement
 * celui qu'on descend.
 */
function glissadesDuParc(c: ChantierMaison): void {
  const lot = c.dehors
  const l = c.l
  const p = c.p
  const glissades = glissadesDe(c.b.kind)

  for (const [i, g] of glissades.entries()) {
    const couleur = COULEURS_GLISSADE[i % COULEURS_GLISSADE.length] as number
    const x0 = l * g.depart.x
    const z0 = p * g.depart.z
    const x1 = l * g.arrivee.x
    const z1 = p * g.arrivee.z
    const dx = x1 - x0
    const dz = z1 - z0
    const longueur = Math.hypot(dx, dz)
    if (longueur < 0.5) continue
    // Le sens du toboggan, et sa perpendiculaire: c'est avec elles qu'on pose
    // le seuil et les rambardes sans ecrire un seul cas de quart de tour.
    const ux = dx / longueur
    const uz = dz / longueur
    const capY = Math.atan2(ux, uz)

    // ------------------------------------------------------------- le seuil
    //
    // C'est LUI qu'on touche pour partir (`glissadeAuPoint` repond sur le
    // point de depart du plan): il doit donc se voir et se viser. Un carre de
    // bois clair, un dossier, et deux montants — assez pour qu'on comprenne
    // qu'on s'y assoit.
    const Y0 = HAUTEUR_DEPART_GLISSADE
    // Le podium qui rattrape la hauteur du seuil depuis le plancher, et sa
    // marche: sans lui, la plate-forme flotterait un demi-metre en l'air.
    pBoite(lot, teinter(BOIS, 0.9), [1.5, Y0 - HAUTEUR_ETAGE, 1.5], [x0, (Y0 + HAUTEUR_ETAGE) / 2 - 0.08, z0], [0, capY, 0])
    pBoite(
      lot,
      BOIS_CLAIR,
      [1.7, 0.12, 0.5],
      [x0 - ux * 1.05, HAUTEUR_ETAGE + (Y0 - HAUTEUR_ETAGE) / 2 - 0.08, z0 - uz * 1.05],
      [0, capY, 0],
    )
    pBoite(lot, BOIS_CLAIR, [1.7, 0.16, 1.7], [x0, Y0 - 0.08, z0], [0, capY, 0])
    pBoite(lot, teinter(couleur, 1.1), [1.5, 0.05, 1.5], [x0, Y0 + 0.01, z0], [0, capY, 0])
    for (const cote of [1, -1]) {
      // Les deux joues du depart, perpendiculaires au sens de la descente.
      pBoite(
        lot,
        couleur,
        [0.12, 0.62, 1.7],
        [x0 - uz * cote * 0.8, Y0 + 0.31, z0 + ux * cote * 0.8],
        [0, capY, 0],
      )
      pCyl(lot, METAL, 0.05, 0.05, 1.1, 6, [x0 - uz * cote * 0.85 - ux * 0.8, Y0 + 0.55, z0 + ux * cote * 0.85 - uz * 0.8])
    }
    // Le dossier, du cote d'ou l'on arrive: on ne tombe pas en arriere.
    pBoite(lot, couleur, [1.7, 0.85, 0.12], [x0 - ux * 0.82, Y0 + 0.42, z0 - uz * 0.82], [0, capY, 0])

    // -------------------------------------------------------------- la cuve
    //
    // Un tube par tronçon, tendu entre deux points du profil: `pTube` oriente
    // le cylindre tout seul, ce qui evite d'ecrire une rotation par segment.
    // Le tube est pose 35 cm SOUS le profil pour que le bonhomme, dessine a la
    // hauteur du profil, s'y trouve assis et non perche dessus.
    const SEGMENTS = 14
    const RAYON = 0.52
    for (let k = 0; k < SEGMENTS; k++) {
      const t0 = k / SEGMENTS
      const t1 = (k + 1) / SEGMENTS
      const a: [number, number, number] = [x0 + dx * t0, hauteurGlissade(t0) - 0.35, z0 + dz * t0]
      const b: [number, number, number] = [x0 + dx * t1, hauteurGlissade(t1) - 0.35, z0 + dz * t1]
      pTube(lot, k % 2 === 0 ? couleur : teinter(couleur, 1.12), RAYON, a, b, 8)
    }
    // L'eclaboussure a l'arrivee: sans elle, le toboggan s'arrete dans l'eau
    // sans rien dire, et on ne sait pas ou l'on va tomber.
    for (const cote of [1, -1]) {
      pSphere(lot, 0xdff4ff, 0.34, [x1 + uz * cote * 0.5, 0.42, z1 - ux * cote * 0.5], [1, 0.55, 1], 6, 4)
    }

    // ------------------------------------------------------------ les pieds
    //
    // Trois poteaux jusqu'au sol: sans eux, sept metres de tube flottent en
    // l'air et la structure ne tient a rien de visible.
    for (const t of [0.28, 0.55, 0.82]) {
      const hy = hauteurGlissade(t) - 0.35
      if (hy < 0.6) continue
      const px = x0 + dx * t
      const pz = z0 + dz * t
      pCyl(lot, METAL, 0.09, 0.11, hy, 6, [px, hy / 2, pz])
      // Une jambe de force de chaque cote: un poteau seul se lit comme un mat.
      for (const cote of [1, -1]) {
        pTube(
          lot,
          METAL,
          0.05,
          [px, hy * 0.7, pz],
          [px + uz * cote * hy * 0.35, 0, pz - ux * cote * hy * 0.35],
          5,
        )
      }
    }
  }
}

/**
 * LA PASSERELLE DU PARC AQUATIQUE: le quatrieme chemin d'etage du projet.
 *
 * C'est LE PLAN qui aiguille ici, jamais le nom du modele: ce modele-ci est le
 * seul dont le plan porte des `glissades`. La terrasse de la villa, les deux
 * niveaux de l'hotel et la salle de restaurant d'une aerogare ne changent pas
 * d'un iota.
 *
 * CE QU'ON NE DESSINE PAS, ET POURQUOI C'EST LE CHOIX CENTRAL.
 *
 * `murEtage` borde TOUTE l'emprise d'un acrotere a chaque niveau: le joueur
 * peut donc marcher partout la-haut, bassin compris. Une dalle pleine de
 * 18 x 10 m serait pourtant un COUVERCLE — de l'exterieur on ne verrait plus ni
 * l'eau, ni les fontaines, ni l'arrivee des glissades, c'est-a-dire tout ce qui
 * fait un parc aquatique; et le fondu ne les rendrait qu'a celui qui est deja
 * entre. On pose donc une COURSIVE de bois le long des quatre bords et une
 * ANTENNE au milieu qui porte les trois seuils, et l'on laisse le dessus du
 * bassin OUVERT.
 *
 * Le prix est ecrit ici pour qu'on ne le redecouvre pas en jouant: au-dessus du
 * bassin, la passerelle est marchable sans etre dessinee. C'est la meme famille
 * d'aveu que « on ne tombe pas de la terrasse » — le moteur n'a aucune
 * verticalite, un niveau est un entier a cote de la position, et il n'y a rien
 * a resoudre entre les deux. Entre montrer le parc et border un vide ou l'on
 * n'a rien a faire, on montre le parc.
 */
function ajouterPasserelle(c: ChantierMaison): void {
  const lot = c.etage
  const l = c.l
  const p = c.p
  const Y = HAUTEUR_ETAGE
  const cDalle = laver(c.cToit, 0.15)
  const cRampe = laver(c.cToit, 0.3)
  const ep = EPAISSEUR_PARAPET

  // L'acrotere SEUL: c'est la bande que `resoudreMurs` applique la-haut, et
  // elle doit border toute l'emprise meme la ou le plancher s'arrete.
  dalleEtAcrotere(lot, l, p, Y, cDalle, c.cMurs, cRampe, false)

  const EP_PONT = 0.18
  /** Un pan de plancher de bois a fleur de la passerelle, avec ses lames. */
  const pont = (cx: number, cz: number, hx: number, hz: number): void => {
    pBoite(lot, BOIS, [hx * 2, EP_PONT, hz * 2], [cx, Y - EP_PONT / 2, cz])
    // Les lames, un ton plus clair et posees dans le sens de la longueur: sans
    // elles, neuf metres de plancher se lisent comme une dalle de beton peinte.
    const long = hx >= hz
    const portee = (long ? hx : hz) * 2
    const n = Math.max(2, Math.round(portee / 1.3))
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n
      if (long) {
        pBoite(lot, BOIS_CLAIR, [(hx * 2) / n - 0.12, 0.03, hz * 2 - 0.14], [cx - hx + t * hx * 2, Y + 0.005, cz])
      } else {
        pBoite(lot, BOIS_CLAIR, [hx * 2 - 0.14, 0.03, (hz * 2) / n - 0.12], [cx, Y + 0.005, cz - hz + t * hz * 2])
      }
    }
  }

  /** Un garde-corps au bord du vide: deux lisses et des montants. */
  const rambarde = (x0: number, z0: number, x1: number, z1: number): void => {
    const dx = x1 - x0
    const dz = z1 - z0
    const longueur = Math.hypot(dx, dz)
    if (longueur < 0.4) return
    for (const hy of [0.52, 0.98]) {
      pBoite(lot, cRampe, [Math.abs(dx) + 0.08, 0.07, Math.abs(dz) + 0.08], [(x0 + x1) / 2, Y + hy, (z0 + z1) / 2])
    }
    // Les montants tombent PILE sur les extremites: repartis, jamais a pas
    // fixe depuis un bord — la lecon des poteaux d'enclos du zoo.
    const n = Math.max(2, Math.round(longueur / 2.2) + 1)
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1)
      pCyl(lot, METAL, 0.045, 0.045, 1.0, 6, [x0 + dx * t, Y + 0.5, z0 + dz * t])
    }
  }

  // ------------------------------------------------------------ la coursive
  //
  // Quatre bandes le long de l'acrotere, plus une antenne qui traverse et porte
  // les trois seuils de glissade. Les bornes sont calculees a partir de
  // l'emprise et de l'epaisseur de l'acrotere: rien n'est ecrit en dur, donc un
  // parc d'une autre taille se dessinerait tout seul.
  const bx = l / 2 - ep
  const bz = p / 2 - ep
  const LARGE = 1.7
  const ETROIT = 1.15
  /*
   * LA LARGEUR DE L'AILE OUEST SE DEDUIT DES SEUILS, elle ne s'ecrit pas.
   *
   * C'est elle qui porte les trois departs de glissade, et ils viennent du plan
   * (`glissadesDe`). Ecrire ici une largeur a la main, c'est laisser un seuil
   * suspendu dans le vide le jour ou le plan bouge d'un metre — la meme faute
   * que recopier les fractions d'un plan dans un test. On prend donc le depart
   * le plus a l'est et l'on pousse le plancher un demi-metre plus loin, juste
   * assez pour que la plate-forme repose entierement dessus.
   */
  let xSeuil = -bx
  for (const g of glissadesDe(c.b.kind)) xSeuil = Math.max(xSeuil, l * g.depart.x)
  const bordAile = clamp(xSeuil + 0.6, -bx + LARGE, bx - ETROIT - 1)

  // Nord et sud sur toute la largeur, l'aile ouest et une bande a l'est entre
  // les deux. Au milieu, RIEN: c'est le bassin qu'on veut voir.
  for (const signe of [1, -1]) {
    pont(0, signe * (bz - LARGE / 2), bx, LARGE / 2)
  }
  pont((-bx + bordAile) / 2, 0, (bordAile + bx) / 2, bz - LARGE)
  pont(bx - ETROIT / 2, 0, ETROIT / 2, bz - LARGE)

  // Les garde-corps du cote du vide: le bord interieur des bandes nord, sud et
  // est. L'acrotere s'occupe deja de l'exterieur, et le bord de l'aile ouest
  // reste ouvert — c'est par la qu'on part en glissade.
  const zInt = bz - LARGE
  rambarde(bordAile, -zInt, bx - ETROIT, -zInt)
  rambarde(bordAile, zInt, bx - ETROIT, zInt)
  rambarde(bx - ETROIT, -zInt, bx - ETROIT, zInt)

  // ------------------------------------------------------------- les pieds
  //
  // Les poteaux qui portent tout cela. Ils se tiennent sous les bandes nord et
  // sud et sous l'aile ouest: nulle part sous le bassin, ou ils sortiraient de
  // l'eau au milieu des nageurs.
  const zPied = bz - LARGE / 2
  const hPied = Y - EP_PONT
  for (const signe of [1, -1]) {
    for (const px of [-bx + 0.5, (-bx + bordAile) / 2, 0.5, bx - 0.6]) {
      pCyl(lot, BOIS_FONCE, 0.15, 0.18, hPied, 6, [px, hPied / 2, signe * zPied])
    }
    pCyl(lot, BOIS_FONCE, 0.15, 0.18, hPied, 6, [-bx + 0.6, hPied / 2, signe * (bz - LARGE) * 0.45])
  }

  // ------------------------------------------------------- la tete d'escalier
  //
  // Elle vient du plan du MODELE (`accesPos`), jamais du plan de la villa: le
  // debouche dessine doit etre celui que `positionEscalier` rend a la cible du
  // tap. C'est la meme table des deux cotes.
  const acces = planEtageDe(c.b.kind)?.accesPos ?? PLAN_ETAGE.escalier
  teteEscalier(lot, l * acces.x, p * acces.z, Y, cRampe)

  // ------------------------------------------------------------ un peu de vie
  //
  // Deux transats et un parasol sur la bande sud, du cote EST: une coursive nue
  // se lit comme un echafaudage, pas comme le haut d'un parc ou l'on vient
  // s'installer. A l'est parce que les toboggans occupent tout l'ouest — un
  // parasol plante au milieu d'une glissade se lit comme un bogue.
  const tz = bz - LARGE / 2
  for (const [i, dx] of [bx * 0.34, bx * 0.72].entries()) {
    pBoite(lot, BOIS_CLAIR, [1.7, 0.08, 0.58], [dx, Y + 0.32, tz], [0, 0, 0.06])
    pBoite(lot, teinter(TISSU_CLAIR, 1 + i * 0.08), [0.66, 0.06, 0.54], [dx - 0.66, Y + 0.5, tz], [0, 0, -0.5])
    for (const sx of [1, -1]) pCyl(lot, METAL, 0.04, 0.04, 0.28, 6, [dx + sx * 0.74, Y + 0.14, tz])
  }
  pCyl(lot, METAL, 0.05, 0.05, 2.0, 6, [bx * 0.53, Y + 1.0, tz])
  pCone(lot, 0xe8663f, 0.95, 0.45, 8, [bx * 0.53, Y + 2.15, tz])
}

/**
 * L'ETAGE D'UNE AEROGARE: la salle du restaurant, et sa terrasse sur la piste.
 *
 * Un troisieme chemin a cote de la terrasse de la villa et des deux niveaux de
 * l'hotel, et il est GENERIQUE: tout vient du plan du modele (`piecesEtage`,
 * `planEtageDe`), rien du nom. Les trois aeroports passent donc ici sans qu'on
 * les nomme, et le jour ou un quatrieme arrivera, il n'y aura rien a ecrire.
 */
function ajouterEtageAerogare(c: ChantierMaison): void {
  const lot = c.etage
  const Y = HAUTEUR_ETAGE
  const cDalle = laver(c.cToit, 0.15)
  const cRampe = laver(c.cToit, 0.3)
  const plan = planEtageDe(c.b.kind)
  dalleEtAcrotere(lot, c.l, c.p, Y, cDalle, c.cMurs, cRampe)

  const acces = plan?.accesPos ?? PLAN_ETAGE.escalier
  teteEscalier(lot, c.l * acces.x, c.p * acces.z, Y, cRampe)

  const salle = piecesEtage(c.b.kind, 1)[0]
  if (!salle) return
  const bo = boiteEtage(c.b.kind, salle)
  // Grandes baies: on vient manger ICI pour regarder les avions, donc le mur
  // qui donne sur la piste doit etre du verre jusqu'en bas.
  pieceVitreeEtage(c, lot, bo, Y, 2.5, salle.porte, true, true)
  pPlaque(lot, teinter(TAPIS, 1.05), bo.hx * 2 - 0.6, bo.hz * 2 - 0.6, [bo.cx, Y + 0.02, bo.cz])

  // ------------------------------------------------------------- le comptoir
  //
  // C'est LUI qu'on touche pour manger, et il est a la position que le plan
  // donne (`positionRestaurant`): le comptoir dessine et le comptoir qui sert
  // sont le meme comptoir.
  const co = plan?.restaurant
  if (co) {
    const cx = c.l * co.x
    const cz = c.p * co.z
    pBoite(lot, BOIS, [3.2, 1.05, 0.7], [cx, Y + 0.52, cz])
    pBoite(lot, BOIS_CLAIR, [3.4, 0.09, 0.9], [cx, Y + 1.08, cz])
    // La vitrine chauffante et deux tabourets: un comptoir nu ne dit pas qu'on
    // y mange.
    pBoite(lot, 0x9fd4e8, [1.2, 0.4, 0.5], [cx + 0.9, Y + 1.32, cz])
    pBoite(lot, teinter(METAL, 1.1), [1.2, 0.06, 0.5], [cx + 0.9, Y + 1.13, cz])
    for (const dx of [-1.1, -0.4]) {
      pCyl(lot, METAL, 0.05, 0.05, 0.75, 6, [cx + dx, Y + 0.38, cz - 0.95])
      pCyl(lot, teinter(TISSU, 1.1), 0.22, 0.22, 0.09, 10, [cx + dx, Y + 0.79, cz - 0.95])
    }
    // La carte, accrochee derriere: trois plats et pas un prix, puisque c'est
    // gratuit.
    pBoite(lot, 0x2f4a3a, [1.6, 0.7, 0.06], [cx - 1.1, Y + 1.85, cz + 0.3])
    for (let i = 0; i < 3; i++) {
      pBoite(lot, BLANC, [1.1, 0.07, 0.02], [cx - 1.1, Y + 2.05 - i * 0.2, cz + 0.26])
    }
  }

  // ------------------------------------------------------------- les tables
  //
  // Deux tables rondes, une de chaque cote de la salle, le long des baies: on
  // mange en regardant la piste. Deux et pas quatre — la salle fait 8 x 3,4 m,
  // et deux rangees s'y chevaucheraient.
  for (const sx of [-1, 1]) {
    {
      const tx = bo.cx + sx * (bo.hx - 1.5)
      const tz = bo.cz - 0.35
      pCyl(lot, BOIS_CLAIR, 0.55, 0.55, 0.07, 12, [tx, Y + 0.74, tz])
      pCyl(lot, METAL, 0.06, 0.1, 0.72, 6, [tx, Y + 0.37, tz])
      for (const cote of [-1, 1]) {
        pCyl(lot, teinter(TISSU, 0.95), 0.24, 0.24, 0.08, 10, [tx + cote * 0.85, Y + 0.46, tz])
        pCyl(lot, METAL, 0.05, 0.05, 0.42, 6, [tx + cote * 0.85, Y + 0.21, tz])
        pBoite(lot, TISSU, [0.42, 0.44, 0.07], [tx + cote * 1.02, Y + 0.68, tz])
      }
      // Une assiette et un verre: le detail qui fait « restaurant » et non
      // « salle d'attente avec des tables ».
      pCyl(lot, BLANC, 0.14, 0.14, 0.02, 8, [tx - 0.15, Y + 0.79, tz])
      pCyl(lot, 0xf5d13a, 0.06, 0.05, 0.14, 7, [tx + 0.2, Y + 0.85, tz + 0.1])
    }
  }
}

function ajouterNiveauxHotel(c: ChantierMaison): void {
  const l = c.l
  const p = c.p
  const Y1 = HAUTEUR_ETAGE
  const Y2 = HAUTEUR_ETAGE * 2
  const cDalle = laver(c.cToit, 0.15)
  const cRampe = laver(c.cToit, 0.3)
  // Le plan du MODELE, pas celui de l'hotel en dur: la cabine dessinee doit
  // etre a la colonne que `positionAcces` fait toucher, quel que soit le
  // modele que le repartiteur envoie ici.
  const plan = planEtageDe(c.b.kind) ?? PLAN_HOTEL
  const asc = { x: l * plan.accesPos.x, z: p * plan.accesPos.z }

  // ------------------------------------------------------------- niveau 1
  const lot1 = c.etage
  dalleEtAcrotere(lot1, l, p, Y1, cDalle, c.cMurs, cRampe)
  cabineAscenseur(lot1, asc.x, asc.z, Y1, 2.6, c.cMurs, 0.1)

  // Les pieces du niveau 1: la MEME hauteur que la grande piece de la villa
  // (2,4 m), et pour la meme raison — en vue a 45 degres, un mur plus haut
  // cacherait celui qui se tient dans la piece. La dalle du dessus est portee
  // par les colonnes; la bande libre au-dessus des pieces se lit comme une
  // imposte d'aeration, pas comme un trou.
  const hMur1 = 2.4
  const pieces1 = piecesEtage(c.b.kind, 1)
  const bureau = pieces1[0]
  const salle = pieces1[1]
  if (bureau) pieceVitreeEtage(c, lot1, boiteEtage(c.b.kind, bureau), Y1, hMur1, bureau.porte, false, true)
  if (salle) pieceVitreeEtage(c, lot1, boiteEtage(c.b.kind, salle), Y1, hMur1, salle.porte, true, true)

  // Le bureau: la meme table et le meme portable que la villa, a la position
  // que le plan donne — celle que `positionPortable` fait toucher.
  if (plan.portable) {
    const po = { x: l * plan.portable.x, z: p * plan.portable.z }
    pBoite(lot1, BOIS_CLAIR, [1.7, 0.07, 0.78], [po.x, Y1 + 0.74, po.z])
    for (const [sx, sz] of [
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ] as const) {
      pBoite(lot1, METAL, [0.06, 0.72, 0.06], [po.x + sx * 0.76, Y1 + 0.36, po.z + sz * 0.32])
    }
    pBoite(lot1, teinter(TISSU, 0.85), [0.48, 0.06, 0.46], [po.x, Y1 + 0.46, po.z - 0.75])
    pBoite(lot1, TISSU, [0.48, 0.5, 0.07], [po.x, Y1 + 0.72, po.z - 0.97])
    pBoite(lot1, teinter(METAL, 0.85), [0.4, 0.03, 0.28], [po.x, Y1 + 0.79, po.z])
    pBoite(lot1, teinter(METAL, 0.85), [0.4, 0.27, 0.02], [po.x, Y1 + 0.93, po.z + 0.14], [0.3, 0, 0])
    pBoite(lot1, 0x9fd4e8, [0.35, 0.22, 0.01], [po.x, Y1 + 0.93, po.z + 0.12], [0.3, 0, 0])
    pPlaque(lot1, TAPIS, 2.2, 1.8, [po.x + 0.3, Y1 + 0.02, po.z - 1.4])
  }

  // La petite terrasse du bureau: une table ronde, deux chaises, une plante.
  if (bureau) {
    const bo = boiteEtage(c.b.kind, bureau)
    const tx = bo.cx
    const tz = bo.cz - bo.hz - 2.0
    pCyl(lot1, BOIS_CLAIR, 0.5, 0.5, 0.05, 10, [tx, Y1 + 0.72, tz])
    pCyl(lot1, METAL, 0.05, 0.07, 0.7, 6, [tx, Y1 + 0.35, tz])
    for (const signe of [1, -1]) {
      pBoite(lot1, teinter(TISSU_CLAIR, 0.95), [0.44, 0.06, 0.42], [tx + signe * 0.95, Y1 + 0.45, tz])
      pBoite(lot1, teinter(TISSU_CLAIR, 0.85), [0.44, 0.45, 0.07], [tx + signe * 1.13, Y1 + 0.65, tz])
    }
    pCyl(lot1, 0xb0563f, 0.24, 0.19, 0.4, 8, [bo.cx - bo.hx + 0.55, Y1 + 0.2, tz - 1.0])
    pSphere(lot1, 0x3f8f4a, 0.34, [bo.cx - bo.hx + 0.55, Y1 + 0.62, tz - 1.0], [1, 1.15, 1], 7, 5)
  }

  // La salle a manger: la longue table, six chaises, un buffet sous la baie.
  if (salle) {
    const bo = boiteEtage(c.b.kind, salle)
    const tx = bo.cx
    const tz = bo.cz
    pPlaque(lot1, teinter(TAPIS, 0.85), 3.6, 2.6, [tx, Y1 + 0.02, tz])
    pBoite(lot1, BOIS_CLAIR, [2.7, 0.08, 1.1], [tx, Y1 + 0.78, tz])
    for (const [sx, sz] of [
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ] as const) {
      pBoite(lot1, BOIS, [0.09, 0.74, 0.09], [tx + sx * 1.22, Y1 + 0.37, tz + sz * 0.44])
    }
    for (const sx of [-1, 0, 1]) {
      for (const sz of [1, -1]) {
        pBoite(lot1, teinter(TISSU, 0.9), [0.42, 0.06, 0.4], [tx + sx * 0.85, Y1 + 0.48, tz + sz * 0.92])
        pBoite(lot1, TISSU, [0.42, 0.44, 0.07], [tx + sx * 0.85, Y1 + 0.68, tz + sz * 1.1])
      }
    }
    // Deux couverts et une carafe: une table nue ne raconte rien.
    pCyl(lot1, BLANC, 0.14, 0.14, 0.02, 8, [tx - 0.6, Y1 + 0.83, tz + 0.2])
    pCyl(lot1, BLANC, 0.14, 0.14, 0.02, 8, [tx + 0.6, Y1 + 0.83, tz - 0.2])
    pCyl(lot1, 0x9fd4e8, 0.09, 0.07, 0.26, 7, [tx, Y1 + 0.95, tz])
    // Le buffet, adosse au mur-fenetre.
    pBoite(lot1, BOIS, [1.6, 0.62, 0.42], [bo.cx + bo.hx - 0.45, Y1 + 0.31, bo.cz + bo.hz - 1.0])
    pCyl(lot1, 0xd45fa8, 0.1, 0.08, 0.24, 7, [bo.cx + bo.hx - 0.45, Y1 + 0.75, bo.cz + bo.hz - 1.0])
  }

  // ------------------------------------------------------------- niveau 2
  const lot2 = c.etage2
  // Les colonnes qui portent la dalle du haut au-dessus des terrasses: sans
  // elles, elle flotterait dans le vide au-dessus du pont.
  for (const [px, pz] of [
    [l / 2 - 0.42, p / 2 - 0.42],
    [-(l / 2 - 0.42), p / 2 - 0.42],
    [l / 2 - 0.42, -(p / 2 - 0.42)],
    [-(l / 2 - 0.42), -(p / 2 - 0.42)],
    [3.4, -(p / 2 - 0.42)],
    [-3.4, -(p / 2 - 0.42)],
  ] as const) {
    pBoite(lot2, teinter(c.cMurs, 0.94), [0.28, HAUTEUR_ETAGE, 0.28], [px, Y1 + HAUTEUR_ETAGE / 2, pz])
  }
  dalleEtAcrotere(lot2, l, p, Y2, cDalle, c.cMurs, cRampe)
  cabineAscenseur(lot2, asc.x, asc.z, Y2, 2.5, c.cMurs, 0)

  // Le salon de lecture: a ciel ouvert, avec sa corniche — le meme choix que
  // la piece de la villa, parce que le fondu efface l'etage quand on est EN
  // DESSOUS, jamais quand on y est.
  const salon = piecesEtage(c.b.kind, 2)[0]
  if (salon) {
    const bo = boiteEtage(c.b.kind, salon)
    pieceVitreeEtage(c, lot2, bo, Y2, 2.4, salon.porte, false, true)

    // Les rayonnages garnis, contre le mur du fond: les livres du soir.
    const COUVERTURES = [0xc94a3f, 0x3f6fa5, 0x4f8f5a, 0xd8a63a, 0x7b57c9]
    const zRayon = bo.cz + bo.hz - 0.42
    pBoite(lot2, 0x8a6a44, [bo.hx * 2 - 0.7, 1.9, 0.36], [bo.cx, Y2 + 0.95, zRayon])
    for (let n = 0; n < 3; n++) {
      const yPlanche = Y2 + 0.35 + n * 0.58
      pBoite(lot2, teinter(0x8a6a44, 1.15), [bo.hx * 2 - 0.76, 0.05, 0.34], [bo.cx, yPlanche, zRayon - 0.02])
      let t = -(bo.hx - 0.55)
      let i = 0
      while (t < bo.hx - 0.55) {
        const h = hache2(Math.round((bo.cx + t) * 41), n * 17 + 3, 0x2f6b1d)
        const ep = 0.06 + h * 0.07
        const couleur = COUVERTURES[Math.floor(h * COUVERTURES.length) % COUVERTURES.length] ?? 0xc94a3f
        pBoite(lot2, couleur, [ep, 0.32 + h * 0.14, 0.22], [bo.cx + t + ep / 2, yPlanche + (0.32 + h * 0.14) / 2, zRayon - 0.04])
        t += ep + 0.014
        if (++i > 40) break
      }
    }
    // Le coin ou l'on se pose: tapis, canape, lampe, plante.
    const sx = bo.cx
    const sz = bo.cz - 0.6
    pPlaque(lot2, TAPIS, 2.6, 2.0, [sx, Y2 + 0.02, sz])
    pBoite(lot2, teinter(TISSU, 0.8), [2.0, 0.34, 0.8], [sx, Y2 + 0.19, sz - 0.4])
    pBoite(lot2, TISSU, [2.0, 0.6, 0.22], [sx, Y2 + 0.6, sz - 0.7])
    for (const signe of [1, -1]) {
      pBoite(lot2, COUSSIN, [0.34, 0.3, 0.14], [sx + signe * 0.7, Y2 + 0.5, sz - 0.58], [0.3, 0, 0])
    }
    pBoite(lot2, BOIS_CLAIR, [1.0, 0.07, 0.55], [sx, Y2 + 0.4, sz + 0.5])
    pBoite(lot2, BLANC, [0.44, 0.04, 0.32], [sx - 0.1, Y2 + 0.45, sz + 0.5], [0, 0.25, 0])
    pCyl(lot2, METAL, 0.03, 0.03, 1.2, 6, [bo.cx - bo.hx + 0.5, Y2 + 0.6, sz])
    pCyl(lot2, ABAT_JOUR, 0.14, 0.22, 0.24, 8, [bo.cx - bo.hx + 0.5, Y2 + 1.3, sz])
    pCyl(lot2, 0xb0563f, 0.22, 0.18, 0.36, 8, [bo.cx + bo.hx - 0.5, Y2 + 0.18, sz + 0.9])
    pSphere(lot2, 0x2f7d3a, 0.3, [bo.cx + bo.hx - 0.5, Y2 + 0.56, sz + 0.9], [1, 1.2, 1], 7, 5)
  }

  // ------------------------- le bassin aux poissons, sur le pont de droite
  const et = boiteEtage(c.b.kind, ETANG_HOTEL)
  const fond = Y2 - 0.3
  pBoite(lot2, 0x2f4a3a, [et.hx * 2, 0.05, et.hz * 2], [et.cx, fond, et.cz])
  for (const signe of [1, -1]) {
    pBoite(lot2, 0x4a6a58, [et.hx * 2, 0.32, 0.1], [et.cx, Y2 - 0.16, et.cz + signe * (et.hz - 0.05)])
    pBoite(lot2, 0x4a6a58, [0.1, 0.32, et.hz * 2], [et.cx + signe * (et.hx - 0.05), Y2 - 0.16, et.cz])
  }
  // La margelle en pierres rondes: un bassin de jardin, pas un spa.
  const nPierres = 14
  for (let i = 0; i < nPierres; i++) {
    const a = (i / nPierres) * Math.PI * 2
    const px = et.cx + Math.cos(a) * (et.hx + 0.12)
    const pz = et.cz + Math.sin(a) * (et.hz + 0.12)
    const h = hache2(i * 7, 3, 0x1abc)
    pSphere(lot2, teinter(0x9a958a, 0.85 + h * 0.3), 0.16 + h * 0.08, [px, Y2 + 0.06, pz], [1, 0.55, 1], 6, 4)
  }
  // LES POISSONS: trois, sous la surface, chacun sa couleur et son cap.
  for (const [i, [dx, dz, cap, couleur]] of (
    [
      [-0.5, -0.4, 0.8, 0xef8f3f],
      [0.45, 0.3, -1.9, 0xf4f0e4],
      [0.1, -0.75, 2.6, 0x4f8fd4],
    ] as const
  ).entries()) {
    const fx = et.cx + dx
    const fz = et.cz + dz
    pSphere(lot2, couleur, 0.16, [fx, Y2 - 0.18, fz], [1.5, 0.6, 0.7], 7, 5)
    pCone(lot2, teinter(couleur, 0.85), 0.09, 0.18, 5, [fx - Math.cos(cap) * 0.3, Y2 - 0.18, fz - Math.sin(cap) * 0.3], [0, cap, Math.PI / 2 + i])
  }
  // Deux nenuphars a la surface.
  pCyl(lot2, 0x3f8f4a, 0.16, 0.16, 0.015, 8, [et.cx - 0.7, Y2 - 0.075, et.cz + 0.6])
  pCyl(lot2, 0x2f7d3a, 0.13, 0.13, 0.015, 8, [et.cx + 0.75, Y2 - 0.075, et.cz - 0.5])
  // L'eau, translucide et epaisse, dans le lot d'eau du niveau 2.
  pBoite(c.eau2, 0x4fc3e8, [et.hx * 2 - 0.2, 0.26, et.hz * 2 - 0.2], [et.cx, Y2 - 0.21, et.cz])

  // Le palmier du toit, plante dans son bac a cote du bassin.
  const pt = { x: l * PALMIER_TOIT_HOTEL.x, z: p * PALMIER_TOIT_HOTEL.z }
  pBoite(lot2, teinter(0x8a6a44, 0.9), [0.9, 0.5, 0.9], [pt.x, Y2 + 0.25, pt.z])
  palmier(lot2, pt.x, Y2 + 0.4, pt.z, 2.6, 0.7)

  // ------------------------------------------------ la cascade, et sa vasque
  //
  // Elle deborde du bassin par la face +x, franchit l'acrotere sur un bec de
  // pierre, et tombe le long de la facade jusqu'a la vasque du jardin. Le bec
  // et l'ecume vivent dans les lots opaques; la lame d'eau, translucide, dans
  // `c.eau2` — et la vasque dans `c.dehors`, qui ne s'efface jamais.
  const zC = p * CASCADE_HOTEL.z
  pBoite(lot2, 0x8a8578, [l / 2 - et.cx - et.hx + 0.7, 0.3, 0.9], [(l / 2 + et.cx + et.hx) / 2 + 0.1, Y2 + 0.98, zC])
  pBoite(c.eau2, 0x4fc3e8, [l / 2 - et.cx - et.hx + 0.5, 0.14, 0.6], [(l / 2 + et.cx + et.hx) / 2 + 0.1, Y2 + 1.14, zC])
  // La lame qui tombe: un voile mince sur toute la hauteur de la facade.
  const hChute = Y2 + 1.1
  pBoite(c.eau2, 0x6fd0f0, [0.3, hChute, 0.7], [l / 2 + 0.35, hChute / 2 + 0.15, zC])
  // La vasque au sol, et l'ecume au pied de la chute.
  pCyl(c.dehors, 0x8a8578, 1.35, 1.5, 0.5, 12, [l / 2 + 0.6, 0.25, zC])
  pCyl(c.dehors, 0x6a665c, 1.15, 1.15, 0.1, 12, [l / 2 + 0.6, 0.3, zC])
  pCyl(c.eau2, 0x4fc3e8, 1.12, 1.12, 0.14, 12, [l / 2 + 0.6, 0.42, zC])
  for (const [dx, dz, r] of [
    [0.15, 0.3, 0.16],
    [-0.2, -0.25, 0.13],
    [0.05, -0.05, 0.1],
  ] as const) {
    pSphere(c.dehors, 0xe8f6fc, r, [l / 2 + 0.35 + dx, 0.52, zC + dz], [1, 0.5, 1], 6, 4)
  }

  // ------------------------------------------- les palmiers autour de l'hotel
  //
  // « Quelques palmiers autour »: aux quatre coins, dans la marge de l'emprise,
  // la ou rien ne se construit jamais (MARGE_BATIMENT). Ils vivent dans
  // `c.dehors`: le jardin ne disparait pas parce qu'on est monte.
  for (const [i, [sx, sz]] of (
    [
      [1, 1],
      [-1, 1],
      [-1, -1],
      [1, -1],
    ] as const
  ).entries()) {
    const px = sx * (l / 2 + 0.35)
    const pz = sz * (p / 2 + 0.35)
    palmier(c.dehors, px, 0, pz, 3.4 + (i % 2) * 0.7, i * 1.7)
  }
}

function construireHabitation(m: Maillage, b: Batiment, def: BatimentDef, style: StyleBatiment, terrain: ITerrain): void {
  const l = def.taille.l
  const p = def.taille.p
  const h = style.hauteurMur
  const c: ChantierMaison = {
    m,
    b,
    style,
    terrain,
    l,
    p,
    sol: [],
    toit: [],
    etage: [],
    etage2: [],
    eau: [],
    eau2: [],
    eauSol: [],
    vitres: [],
    dehors: [],
    faces: { avant: [], arriere: [], gauche: [], droite: [] },
    cMurs: choisirCouleur('murs', b, def, style),
    cToit: choisirCouleur('toit', b, def, style),
    cPorte: choisirCouleur('porte', b, def, style),
  }

  const aEtage = def.etage === true

  if (style.plancher) pBoite(c.sol, 0xc9a97c, [l, 0.12, p], [0, 0.06, 0])
  ajouterMeubles(c.sol, b, l, p, style, style.plancher ? 0.12 : 0)

  // L'escalier du rez-de-chaussee: il monte vers la dalle, contre le mur de
  // gauche, a l'aplomb exact du debouche dessine sur la terrasse. L'hotel,
  // lui, monte par l'ASCENSEUR, que `EXTRAS.hotel` dessine dans le hall.
  if (aEtage && accesEtage(b.kind) === 'escalier') {
    /*
     * LE PLAN DU MODELE, PAS CELUI DE LA VILLA.
     *
     * Ces deux lignes lisaient `PLAN_ETAGE.escalier` en dur, c'est-a-dire le
     * plan de la villa a etage, pour TOUS les modeles: l'aerogare y a survecu
     * par chance (son `accesPos` est a deux centimetres de celui de la villa),
     * le parc aquatique n'y aurait pas survecu du tout — son escalier est dans
     * un coin, et les marches se seraient dressees a quatre metres du debouche
     * qu'on voit la-haut. `positionAcces` (donc la cible du tap) a toujours lu
     * `accesPos`: c'etait bien deux endroits qui repondaient a la meme
     * question, et un seul disait la verite.
     */
    const acces = planEtageDe(b.kind)?.accesPos ?? PLAN_ETAGE.escalier
    const esc = { x: l * acces.x, z: p * acces.z }
    const marches = 10
    for (let i = 0; i < marches; i++) {
      const hMarche = (HAUTEUR_ETAGE * (i + 1)) / marches
      pBoite(c.sol, BOIS_CLAIR, [0.32, hMarche, 1.5], [esc.x - 1.6 + i * 0.32, hMarche / 2, esc.z])
    }
    for (const signe of [1, -1]) {
      pTube(
        c.sol,
        METAL,
        0.045,
        [esc.x - 1.7, 0.9, esc.z + signe * 0.78],
        [esc.x + 1.5, HAUTEUR_ETAGE + 0.9, esc.z + signe * 0.78],
      )
    }
  }

  // La porte est toujours sur la face -z du modele; la rotation du batiment
  // l'oriente ensuite. `portePosition` de la simulation suit la meme regle.
  const zAvant = -p / 2 + EP_MUR / 2
  /*
   * LA LARGEUR DE L'OUVERTURE VIENT DE LA SIMULATION.
   *
   * `largeurPorte` est la MEME fonction que celle dont `resoudreMurs` se sert
   * pour laisser passer le bonhomme. La recopier ici, c'est un mur plein la ou
   * l'on voit une ouverture — ou l'inverse. Le garage en a besoin: sa porte
   * fait trois metres, parce qu'une voiture en fait deux de large et qu'une
   * porte de maison (1,1 m) l'aurait laissee dehors.
   */
  const lPorte = largeurPorte(c.b.kind)
  const hPorte = Math.min(H_PORTE, h - 0.12)
  const cote = Math.max(0, (l - lPorte) / 2)
  for (const signe of [1, -1]) {
    if (cote > 0.01) {
      pBoite(c.faces.avant, c.cMurs, [cote, h, EP_MUR], [(signe * (lPorte + cote)) / 2, h / 2, zAvant])
    }
  }
  if (h - hPorte > 0.05) {
    pBoite(c.faces.avant, c.cMurs, [lPorte, h - hPorte, EP_MUR], [0, hPorte + (h - hPorte) / 2, zAvant])
  }
  // Encadrement: sans lui l'ouverture n'est qu'un trou dans un mur.
  for (const signe of [1, -1]) {
    pBoite(c.faces.avant, c.cPorte, [0.1, hPorte, 0.11], [signe * (lPorte / 2 + 0.05), hPorte / 2, zAvant - 0.03])
  }
  pBoite(c.faces.avant, c.cPorte, [lPorte + 0.2, 0.11, 0.11], [0, hPorte + 0.05, zAvant - 0.03])
  /*
   * Le battant. Une porte ORDINAIRE est un panneau entrouvert avec sa poignee;
   * une ouverture large est un rideau a lames releve, comme un vrai garage. Un
   * battant de trois metres tournant sur ses gonds aurait balaye la moitie de
   * l'allee, et une poignee a hauteur d'homme sur trois metres de large se lit
   * comme une erreur d'echelle.
   */
  if (lPorte > LARGEUR_PORTE * 1.5) {
    for (let i = 0; i < 4; i++) {
      pBoite(
        c.faces.avant,
        teinter(c.cPorte, 1 + i * 0.04),
        [lPorte * 0.96, 0.12, 0.06],
        [0, hPorte - 0.1 - i * 0.15, zAvant - 0.02],
      )
    }
  } else {
    pBoite(
      c.faces.avant,
      teinter(c.cPorte, 1.1),
      [lPorte * 0.94, hPorte * 0.97, 0.07],
      [0, hPorte / 2, zAvant],
      [0, 0.35, 0],
    )
    pSphere(c.faces.avant, METAL, 0.05, [lPorte * 0.34, hPorte * 0.5, zAvant - 0.16], undefined, 6, 4)
  }

  pBoite(c.faces.arriere, c.cMurs, [l, h, EP_MUR], [0, h / 2, p / 2 - EP_MUR / 2])

  for (const signe of [1, -1]) {
    const face = signe > 0 ? c.faces.droite : c.faces.gauche
    pBoite(face, c.cMurs, [EP_MUR, h, p], [(signe * l) / 2 - (signe * EP_MUR) / 2, h / 2, 0])
    ajouterFenetres(
      c.vitres,
      style.fenetres,
      p,
      h,
      (dz, y) => [(signe * l) / 2 - signe * 0.04, y, dz],
      Math.PI / 2,
    )
  }
  ajouterFenetres(c.vitres, style.fenetres >= 2 ? 2 : 0, l, h, (dx, y) => [dx, y, p / 2 - 0.04], 0)

  // Un modele a etage n'a pas de toit: son toit EST la terrasse, et la terrasse
  // se dessine avec tout ce qu'elle porte. Plusieurs niveaux, plusieurs
  // dalles: c'est le plan qui decide, jamais le nom du modele.
  if (aEtage) {
    const plan = planEtageDe(b.kind)
    // C'est LE PLAN qui aiguille, jamais le nom du modele: deux niveaux ou
    // plus, c'est l'hotel; un restaurant la-haut, c'est une aerogare; des
    // glissades, c'est le parc aquatique; le reste, c'est la terrasse de la
    // villa.
    if (plan && plan.niveaux.length > 1) ajouterNiveauxHotel(c)
    else if (plan?.restaurant) ajouterEtageAerogare(c)
    // Des GLISSADES la-haut, c'est le parc aquatique: une coursive de bois
    // au-dessus du bassin, et rien d'une terrasse habitee.
    else if (plan?.glissades) ajouterPasserelle(c)
    else ajouterEtage(c)
  } else {
    ajouterToit(c.toit, style, l, p, c.cToit)
  }

  EXTRAS[b.kind]?.(c)

  m.veutLumiere = true
  m.veilleuse = style.veilleuse ?? 0
  if (m.posLumiere.lengthSq() === 0) {
    // Pour une villa a etage, la lampe eclaire la GRANDE PIECE: c'est la ou l'on
    // vient occuper, et la seule qui merite d'etre allumee dans la nuit. Une
    // seule source, prise dans le pool fixe: le nombre de lumieres de la scene
    // ne bouge pas d'un pouce.
    if (aEtage) {
      // Villa: la grande piece. Hotel: la salle a manger, au premier etage —
      // `positionTable` suit l'ancrage particulier du modele.
      const piece = accesEtage(b.kind) === 'ascenseur' ? positionTable(b) : positionPiece(b)
      m.posLumiere.set(piece.x, b.y + HAUTEUR_ETAGE + 1.9, piece.z)
    } else {
      m.posLumiere.set(b.pos.x, b.y + Math.max(1.6, h - 0.4), b.pos.z)
    }
  }

  // Le plancher du bas devient TRANSPARENT quand le modele a un etage: un
  // materiau cree opaque ne fond jamais, quoi qu'on ecrive ensuite dans son
  // opacite (voir `majFondu`).
  const interieur = poser(m, c.sol, aEtage ? { mat: { transparent: true } } : undefined)
  if (interieur) {
    interieur.castShadow = false
    if (aEtage) m.bas.push(interieur)
  }
  for (const [nom, normale] of [
    ['avant', new THREE.Vector3(0, 0, -1)],
    ['arriere', new THREE.Vector3(0, 0, 1)],
    ['gauche', new THREE.Vector3(-1, 0, 0)],
    ['droite', new THREE.Vector3(1, 0, 0)],
  ] as const) {
    // Un materiau par face: elles partageaient le meme, donc la derniere
    // ecrasait l'opacite des autres pendant le fondu.
    const maille = poser(m, c.faces[nom], { mat: { transparent: true } })
    if (maille) m.murs.push({ maillage: maille, normale })
  }
  const vitres = poser(m, c.vitres, { mat: { transparent: true, opacite: 0.94 }, ombre: false })
  if (vitres) {
    m.fenetres.push(vitres)
    // Les vitres suivent le toit, donc s'effacent quand on entre. Les quatre
    // murs tiennent en quatre mailles, mais les vitres n'en font qu'une pour
    // tout le batiment (elles ont besoin d'un materiau a part, c'est lui qui
    // s'allume la nuit): elles ne peuvent donc pas fondre mur par mur. Restees
    // opaques, elles flottaient au milieu de la piece une fois le mur efface,
    // et on croyait a une fenetre posee par terre.
    m.toit.push(vitres)
  }
  const toit = poser(m, c.toit, { mat: { transparent: true } })
  if (toit) m.toit.push(toit)
  // La terrasse en UN seul appel de dessin: une villa a etage coute donc
  // exactement ce que coute une maison ordinaire — sept appels. L'hotel en
  // ajoute trois: son deuxieme niveau, son eau, et son dehors.
  const etage = poser(m, c.etage, { mat: { transparent: true } })
  if (etage) m.etage.push(etage)
  const etage2 = poser(m, c.etage2, { mat: { transparent: true } })
  if (etage2) m.etage2.push(etage2)
  // Le dehors (palmiers, vasque de la cascade) ne rejoint AUCUNE liste de
  // fondu: il reste visible de partout, comme le decor du monde.
  poser(m, c.dehors)
  const eau2 = poser(m, c.eau2, { mat: { transparent: true, opacite: OPACITE_EAU }, ombre: false })
  if (eau2) m.etage2Eau.push(eau2)
  const eau = poser(m, c.eau, { mat: { transparent: true, opacite: OPACITE_EAU }, ombre: false })
  /*
   * SEULE L'EAU D'UN ETAGE SUIT LE FONDU.
   *
   * L'eau du spa fait partie de la terrasse: elle doit s'effacer avec elle
   * quand on est en dessous. Le bassin du zoo, lui, est au sol — le ranger dans
   * la meme liste le faisait DISPARAITRE des qu'on entrait dans l'emprise du
   * zoo, c'est-a-dire au moment precis ou l'on vient voir les canards, parce
   * que `majFondu` efface l'etage de tout batiment ou l'on entre.
   */
  if (eau && aEtage) m.etageEau.push(eau)
  /*
   * ET L'EAU DU SOL NE SUIT RIEN DU TOUT.
   *
   * Le grand bassin du parc est au rez-de-chaussee d'un modele qui a une
   * passerelle: la regle ci-dessus l'aurait range avec l'etage et l'aurait
   * efface des qu'on monte, c'est-a-dire au moment ou l'on part se jeter
   * dedans. Il ne rejoint donc AUCUNE liste de fondu — mais il garde son
   * materiau transparent, ce que `dehors` ne saurait pas lui donner.
   */
  poser(m, c.eauSol, { mat: { transparent: true, opacite: OPACITE_EAU }, ombre: false })
}

/**
 * UNE AEROGARE ET SA PISTE.
 *
 * DEUX MORCEAUX, ET ILS N'OBEISSENT PAS AUX MEMES REGLES:
 *
 *   - le HALL et la TOUR sont du batiment: ils vivent dans `sol`, s'effacent
 *     avec le rez-de-chaussee, se teintent avec le modele;
 *   - la PISTE est du SOL. Elle part donc dans `dehors`, le lot qui ne
 *     s'efface jamais — un ruban de bitume qui disparaitrait parce qu'on est
 *     monte au restaurant se lirait comme un bogue (la lecon des palmiers de
 *     l'hotel) — et elle EPOUSE LE TERRAIN, parce que l'avion, lui, roule a la
 *     hauteur du sol.
 *
 * Tout est dessine en repere LOCAL: le groupe porte deja la rotation du
 * batiment, et la piste part de la face -z comme `pisteDe` le dit. Recopier ici
 * un quart de tour serait la meilleure facon de faire atterrir les avions a
 * cote du bitume.
 */
function aerogare(c: ChantierMaison): void {
  const BITUME = 0x4a4a4e
  const MARQUAGE = 0xf2f2ea
  const def = batimentDef(c.b.kind)
  const piste = def.piste
  const e = echelleMeuble(c.l, c.p)

  // ---------------------------------------------------------------- le hall
  //
  // Un comptoir d'enregistrement, deux rangees de sieges, un tableau des
  // departs: on doit lire « aeroport » sans avoir a monter.
  const debut = c.sol.length
  const zFond = c.p / 2 - 1.2
  pBoite(c.sol, BOIS, [4.2, 1.1, 0.8], [-1.5, 0.55, zFond])
  pBoite(c.sol, teinter(c.cMurs, 1.08), [4.4, 0.1, 1.0], [-1.5, 1.12, zFond])
  // Le tableau des departs, au-dessus du comptoir: des lignes claires sur fond
  // sombre, ce qui suffit a le reconnaitre de loin.
  pBoite(c.sol, 0x22262b, [3.0, 1.1, 0.12], [-1.5, 2.4, zFond + 0.5])
  for (let i = 0; i < 4; i++) {
    pBoite(c.vitres, 0xffd88a, [2.4, 0.09, 0.02], [-1.5, 2.75 - i * 0.24, zFond + 0.43])
  }
  // Les sieges d'attente, face a la piste.
  for (const rangee of [0, 1]) {
    const z = -c.p / 2 + 2.2 + rangee * 1.9
    for (let i = -2; i <= 2; i++) {
      pBoite(c.sol, teinter(TISSU, 1.05), [0.7 * e, 0.09, 0.62 * e], [i * 0.86 * e + 2.2, 0.46, z])
      pBoite(c.sol, TISSU, [0.7 * e, 0.5, 0.08], [i * 0.86 * e + 2.2, 0.7, z + 0.3 * e])
    }
    pBoite(c.sol, METAL, [4.6 * e, 0.08, 0.1], [2.2, 0.4, z])
  }
  // Un tapis a bagages, deux valises: le detail qui raconte le voyage.
  pBoite(c.sol, 0x33383d, [2.4, 0.35, 0.9], [-3.4, 0.18, -1.2])
  pBoite(c.sol, teinter(0x33383d, 1.4), [2.4, 0.06, 0.9], [-3.4, 0.38, -1.2])
  pBoite(c.sol, 0xc94a3f, [0.62, 0.44, 0.32], [-3.9, 0.63, -1.2], [0, 0.2, 0])
  pBoite(c.sol, 0x3f6fa5, [0.5, 0.36, 0.28], [-2.9, 0.59, -1.35], [0, -0.35, 0])
  grouper(c.sol, debut, tr([0, c.style.plancher ? 0.12 : 0, 0]))

  // ---------------------------------------------------------------- la tour
  //
  // La silhouette qui dit « aeroport » de loin, comme le chevalement dit
  // « mine ». Elle est dans `dehors`: elle depasse du toit, donc elle ne doit
  // pas s'effacer quand on entre.
  /*
   * LA TOUR EST SUR LE PONT AVANT, hors de la salle du restaurant.
   *
   * Au fond a droite, elle traversait la salle de part en part: on mangeait
   * dans un pilier. Devant, elle regarde la piste — ce qui est d'ailleurs le
   * seul endroit sense pour une vigie.
   */
  const tx = c.l / 2 - 1.5
  const tz = -(c.p / 2 - 1.5)
  const hTour = HAUTEUR_ETAGE * 2 + 1.6
  pCyl(c.dehors, teinter(c.cMurs, 0.96), 0.62, 0.78, hTour, 10, [tx, hTour / 2, tz])
  pCyl(c.dehors, laver(c.cToit, 0.2), 1.25, 1.05, 1.5, 10, [tx, hTour + 0.55, tz])
  pCyl(c.vitres, 0x9fd4e8, 1.12, 1.12, 0.95, 10, [tx, hTour + 0.5, tz])
  pCyl(c.dehors, laver(c.cToit, 0.35), 1.35, 1.35, 0.14, 10, [tx, hTour + 1.35, tz])
  // Le mat et son feu: c'est lui qu'on voit clignoter la nuit.
  pCyl(c.dehors, METAL, 0.05, 0.05, 1.4, 6, [tx, hTour + 2.1, tz])
  pSphere(c.vitres, 0xff5a3a, 0.16, [tx, hTour + 2.85, tz], undefined, 7, 5)

  // --------------------------------------------------------------- la piste
  if (!piste) return
  const debutPiste = -c.p / 2 - piste.ecart
  const finPiste = debutPiste - piste.longueur
  const demi = piste.largeur / 2

  /**
   * La hauteur du sol sous un point LOCAL, ramenee dans le repere du batiment.
   *
   * Le groupe est pose a `b.y`: pour qu'un ruban colle au terrain, chaque point
   * doit donc valoir « hauteur du terrain la-bas moins la hauteur du groupe ».
   * C'est ce qui fait que la piste monte et descend avec la prairie au lieu de
   * la trancher.
   */
  const solLocal = (lx: number, lz: number): number => {
    const v = tourner(lx, lz, c.b.rot)
    return c.terrain.hauteur(c.b.pos.x + v.x, c.b.pos.z + v.z) - c.b.y
  }

  /*
   * LE RUBAN EST UNE SEULE SURFACE, cousue sur le terrain — ET IL DOIT LE
   * COUVRIR.
   *
   * Une premiere version le posait en tranches — vingt boites successives, une
   * par metre. De loin ca passait; de pres, chaque tranche montrait sa marche,
   * et la piste ressemblait a un escalier. Les sommets ont donc pris la hauteur
   * du sol, et la bande a monte et descendu d'un trait.
   *
   * Restait le defaut qui se voyait en jeu: LE SABLE PERCAIT LE BITUME. Deux
   * causes, la meme erreur de raisonnement — un sommet ne dit la hauteur du sol
   * qu'EN UN POINT:
   *   - dans la largeur, il n'y avait que les deux bords: une piste bombee en
   *     son milieu (six metres de large) passait sous le terrain entre les deux;
   *   - dans la longueur, une bosse entre deux sommets passait au-dessus de la
   *     corde qui les relie.
   * Mesure sur soixante emplacements valides du vrai monde: le terrain
   * ressortait sur 18 pistes, jusqu'a 19 cm.
   *
   * On quadrille donc (largeur COMPRISE), et surtout chaque sommet prend le
   * MAXIMUM du sol dans sa maille au lieu de sa valeur au point: une surface
   * lineaire tendue sur des maxima passe au-dessus de tout ce qu'elle
   * enjambe. C'est le meme raisonnement que `pointsPiste`, qui sonde un
   * quadrillage et non quatre coins — « une bosse de deux metres au milieu
   * d'une piste de trente passerait entre les coins sans etre vue ».
   */
  const tranches = TRANCHES_RUBAN
  const colonnes = COLONNES_RUBAN
  const pasZ = (finPiste - debutPiste) / tranches
  const pasX = (2 * demi) / colonnes
  /*
   * LES HAUTEURS VIENNENT DE `shared`, et ce n'est pas de la coquetterie: la
   * question « a quelle hauteur poser le tapis pour qu'il couvre le sol ? » est
   * de la geometrie pure, donc elle se VERROUILLE PAR UN TEST — ce qu'un
   * maillage three.js bati ici ne permettrait pas. Voir `hauteursRuban`, et
   * tests/unit/piste-ruban.test.ts, qui balaie le ruban sur de vrais
   * emplacements et exige que le terrain ne ressorte nulle part.
   */
  const pisteMonde = pisteDe(c.b)
  const grille = pisteMonde ? hauteursRuban(pisteMonde, c.terrain) : null
  const sommets: number[] = []
  const triangles: number[] = []
  const hauts: number[][] = []
  for (let i = 0; i <= tranches; i++) {
    const z = debutPiste + pasZ * i
    const ligne: number[] = []
    for (let j = 0; j <= colonnes; j++) {
      const x = -demi + pasX * j
      // Le repere de `hauteursRuban` est celui de la PISTE (i depuis le seuil
      // proche, j depuis lat = +largeur/2, hauteurs en monde); le notre est
      // local, ou x = -lat et ou le groupe est deja pose a `b.y`.
      const y = grille ? (grille[i]?.[j] ?? 0) - c.b.y : solLocal(x, z) + EPAISSEUR_RUBAN
      ligne.push(y)
      sommets.push(x, y, z)
    }
    hauts.push(ligne)
    if (i > 0) {
      for (let j = 0; j < colonnes; j++) {
        const b = (i - 1) * (colonnes + 1) + j
        const n = colonnes + 1
        triangles.push(b, b + 1, b + n, b + 1, b + n + 1, b + n)
      }
    }
  }
  /*
   * LA BORDURE, et elle n'est pas decorative: le ruban, souleve au maximum du
   * sol, decolle forcement du terrain la ou celui-ci redescend. Une surface
   * sans epaisseur montrerait alors, par la tranche, un liseré de vide. Une
   * jupe de trente centimetres plantee dans le sol ferme la question, et se lit
   * comme le rebord d'un tapis de bitume.
   */
  const JUPE = 0.3
  const ENFOUISSEMENT = 0.15
  const bord = (x: number, y: number, z: number): number => {
    const n = sommets.length / 3
    /*
     * Le bas de la jupe est pris SOUS LE SOL REEL de ce sommet, jamais a une
     * profondeur ronde. Le ruban est souleve au MAXIMUM de sa maille: la ou le
     * terrain redescend, la tranche a combler n'est pas 30 cm mais ce que le
     * relief a decide — mesure sur huit aerodromes, jusqu'a 51 cm, c'est-a-dire
     * un filet de jour qu'une valeur fixe laissait passer.
     */
    sommets.push(x, y, z, x, Math.min(y - JUPE, solLocal(x, z) - ENFOUISSEMENT), z)
    return n
  }
  for (let i = 0; i < tranches; i++) {
    for (const [j, sens] of [
      [0, 1],
      [colonnes, -1],
    ] as const) {
      const x = -demi + pasX * j
      const a = bord(x, hauts[i]![j]!, debutPiste + pasZ * i)
      const b = bord(x, hauts[i + 1]![j]!, debutPiste + pasZ * (i + 1))
      if (sens > 0) triangles.push(a, a + 1, b, a + 1, b + 1, b)
      else triangles.push(a, b, a + 1, a + 1, b, b + 1)
    }
  }
  for (const [i, sens] of [
    [0, -1],
    [tranches, 1],
  ] as const) {
    const z = debutPiste + pasZ * i
    for (let j = 0; j < colonnes; j++) {
      const a = bord(-demi + pasX * j, hauts[i]![j]!, z)
      const b = bord(-demi + pasX * (j + 1), hauts[i]![j + 1]!, z)
      if (sens > 0) triangles.push(a, a + 1, b, a + 1, b + 1, b)
      else triangles.push(a, b, a + 1, a + 1, b, b + 1)
    }
  }
  const ruban = new THREE.BufferGeometry()
  ruban.setAttribute('position', new THREE.Float32BufferAttribute(sommets, 3))
  ruban.setIndex(triangles)
  ruban.computeVertexNormals()
  pGeo(c.dehors, BITUME, ruban)

  /** La hauteur du RUBAN (et non du sol) au milieu de la piste. */
  const surRuban = (lz: number): number => {
    const t = clamp((lz - debutPiste) / pasZ, 0, tranches)
    const i = Math.min(tranches - 1, Math.floor(t))
    const f = t - i
    const m = colonnes / 2
    return hauts[i]![m]! * (1 - f) + hauts[i + 1]![m]! * f
  }

  // L'axe blanc, pointille: c'est ce qui donne son sens a « s'aligner ». Il
  // suit le meme relief, un centimetre au-dessus du bitume.
  for (let i = 0; i < tranches; i += 2) {
    const z0 = debutPiste + pasZ * i
    const z1 = debutPiste + pasZ * (i + 1)
    const zc = (z0 + z1) / 2
    // Sur le RUBAN, pas sur le sol: depuis que le bitume prend le maximum de sa
    // maille, les deux ne sont plus a la meme hauteur et l'axe s'y noierait.
    pPlaque(c.dehors, MARQUAGE, 0.34, Math.abs(z1 - z0) * 0.62, [0, surRuban(zc) + 0.02, zc])
  }
  /*
   * Les deux seuils, barres en travers: le lointain est celui ou l'on se pose,
   * le proche celui d'ou l'on part.
   *
   * ELLES DEBORDAIENT. Longues de `demi * 1,7` (5,1 m) et centrees a 0,9 m du
   * bout, elles depassaient de 1,65 m EN DEHORS du ruban et flottaient sur le
   * sable — visible sur toutes les captures d'ensemble. Une marque peinte au
   * sol ne peut pas sortir de ce qui est peint: chaque barre tient donc
   * entierement entre son bout de piste et sa longueur.
   */
  const LONGUEUR_SEUIL = 3
  const RETRAIT_SEUIL = 0.3
  for (const [z, largeurBarre] of [
    [finPiste + RETRAIT_SEUIL + LONGUEUR_SEUIL / 2, LONGUEUR_SEUIL],
    [debutPiste - RETRAIT_SEUIL - LONGUEUR_SEUIL / 2, LONGUEUR_SEUIL],
  ] as const) {
    for (const cote of [-1, 1]) {
      for (let k = 0; k < 3; k++) {
        pPlaque(
          c.dehors,
          MARQUAGE,
          0.28,
          largeurBarre,
          [cote * (0.55 + k * 0.5), surRuban(z) + 0.055, z],
        )
      }
    }
  }
  // Le tablier devant l'aerogare: c'est la que les appareils se garent.
  const yTablier = surRuban(debutPiste)
  pBoite(c.dehors, teinter(BITUME, 1.15), [piste.largeur + 3.4, 0.16, piste.ecart + 1.6], [
    0,
    yTablier + 0.02,
    -c.p / 2 - piste.ecart / 2 + 0.2,
  ])
  // Les feux de bord de piste, un tous les huit metres, de chaque cote.
  for (let i = 1; i < 5; i++) {
    const z = debutPiste - (piste.longueur * i) / 4
    for (const cote of [-1, 1]) {
      const y = solLocal(cote * (demi + 0.5), z)
      pCyl(c.dehors, METAL, 0.07, 0.09, 0.34, 6, [cote * (demi + 0.5), y + 0.17, z])
      pSphere(c.vitres, 0xffd88a, 0.11, [cote * (demi + 0.5), y + 0.4, z], undefined, 6, 4)
    }
  }
}

/** Ce que chaque modele habitable ajoute au chantier commun. */
const EXTRAS: Partial<Record<BatimentKind, (c: ChantierMaison) => void>> = {
  /**
   * LE REZ-DE-CHAUSSEE DE L'HOTEL: les deux chambres, le hall, l'ascenseur.
   *
   * Les cloisons des chambres viennent de `cloisonsInterieures` — la MEME
   * table que `resoudreMurs` applique au sol, celle du zoo. Le mur qu'on voit
   * est celui qui repousse, et la porte dessinee est le trou de la collision.
   * Le lit de la chambre de droite est pose par `ajouterMeubles` (c'est LUI
   * qu'on touche pour dormir); celui de gauche est dessine ici, a l'identique.
   */
  hotel: (c) => {
    const e = echelleMeuble(c.l, c.p)
    const debut = c.sol.length
    const hCloison = 2.7
    const hPorte = 2.0
    const cCloison = teinter(c.cMurs, 0.96)

    for (const piece of cloisonsInterieures(c.b.kind)) {
      const bo = boiteEtage(c.b.kind, piece)
      // Le fond et les deux cotes, pleins.
      pBoite(c.sol, cCloison, [bo.hx * 2, hCloison, 0.14], [bo.cx, hCloison / 2, bo.cz + bo.hz - 0.07])
      for (const signe of [1, -1]) {
        pBoite(c.sol, cCloison, [0.14, hCloison, bo.hz * 2], [bo.cx + signe * (bo.hx - 0.07), hCloison / 2, bo.cz])
      }
      // La facade -z: deux pans de part et d'autre de la porte, le linteau,
      // et l'encadrement — la largeur du trou est CELLE de la collision.
      const cote = (bo.hx * 2 - piece.porte) / 2
      const zAvant = bo.cz - bo.hz + 0.07
      for (const signe of [1, -1]) {
        pBoite(c.sol, cCloison, [cote, hCloison, 0.14], [bo.cx + (signe * (piece.porte + cote)) / 2, hCloison / 2, zAvant])
      }
      pBoite(c.sol, cCloison, [piece.porte, hCloison - hPorte, 0.14], [bo.cx, hPorte + (hCloison - hPorte) / 2, zAvant])
      for (const signe of [1, -1]) {
        pBoite(c.sol, c.cPorte, [0.1, hPorte, 0.12], [bo.cx + signe * (piece.porte / 2 + 0.05), hPorte / 2, zAvant - 0.02])
      }
      pBoite(c.sol, c.cPorte, [piece.porte + 0.2, 0.1, 0.12], [bo.cx, hPorte + 0.05, zAvant - 0.02])
      // Un tapis et une table de chevet: une chambre nue ne raconte rien.
      pPlaque(c.sol, teinter(TAPIS, 1.1), 1.6, 1.2, [bo.cx, 0.015, bo.cz - 0.9])
      pBoite(c.sol, BOIS, [0.42, 0.44, 0.4], [bo.cx + bo.hx - 0.55, 0.22, bo.cz + bo.hz - 0.45])
      pCyl(c.sol, ABAT_JOUR, 0.09, 0.13, 0.16, 8, [bo.cx + bo.hx - 0.55, 0.55, bo.cz + bo.hz - 0.45])
    }

    // Le lit de la chambre de GAUCHE, jumeau dessine de celui qu'on touche —
    // au centre de SA chambre, derive de la meme table que les cloisons: la
    // deplacer deplace le lit avec elle. Celui de droite vient de
    // `ajouterMeubles` (ancrage `lit`), et c'est lui qu'on touche pour dormir.
    const gauche = cloisonsInterieures(c.b.kind)[0]
    if (gauche) {
      const boG = boiteEtage(c.b.kind, gauche)
      ajouterLit(c.sol, boG.cx, boG.cz, e)
    }

    // L'ASCENSEUR du hall, portes entrouvertes: c'est lui qu'on vient toucher.
    const acces = planEtageDe(c.b.kind)?.accesPos ?? PLAN_HOTEL.accesPos
    const asc = { x: c.l * acces.x, z: c.p * acces.z }
    cabineAscenseur(c.sol, asc.x, asc.z, 0, 3.1, c.cMurs, 0.34)

    /*
     * La reception: comptoir, sonnette, tabouret — un hall, pas un salon.
     *
     * Elle etait a 2,2 m de la facade, et ON NE LA VOYAIT PAS: en vue de dessus
     * a 45 degres, un mur de 2,6 m masque tout ce qui se tient a moins de deux
     * metres derriere lui, et le comptoir, haut d'un metre, tombait pile dans
     * cette bande. Elle recule donc au milieu du hall — largement devant les
     * chambres (z = +0,36 m) et l'ascenseur (z = +3,24 m), qui ne bougent pas.
     */
    const rz = -c.p / 2 + 4.4
    pBoite(c.sol, BOIS, [2.4, 1.0, 0.6], [0, 0.5, rz])
    pBoite(c.sol, BOIS_CLAIR, [2.6, 0.08, 0.8], [0, 1.04, rz])
    pCyl(c.sol, 0xf5d13a, 0.07, 0.09, 0.08, 8, [0.7, 1.12, rz])
    pSphere(c.sol, 0xf5d13a, 0.055, [0.7, 1.18, rz], undefined, 6, 4)
    pCyl(c.sol, BOIS_FONCE, 0.2, 0.22, 0.55, 8, [0, 0.28, rz + 0.75])
    // Le tapis de l'entree, dans l'axe de la porte.
    pPlaque(c.sol, teinter(TAPIS, 0.9), 2.2, 1.6, [0, 0.015, -c.p / 2 + 1.0])
    // Deux plantes en pot, aux angles du hall.
    for (const signe of [1, -1]) {
      pCyl(c.sol, 0xb0563f, 0.22, 0.18, 0.4, 8, [signe * 1.9, 0.2, rz - 0.9])
      pSphere(c.sol, 0x3f8f4a, 0.3, [signe * 1.9, 0.62, rz - 0.9], [1, 1.2, 1], 7, 5)
    }

    // Tout le rez-de-chaussee monte sur le plancher, comme les meubles.
    grouper(c.sol, debut, tr([0, 0.12, 0]))
  },

  /**
   * LE MUSEE: des cadres accroches aux murs, et un banc au milieu.
   *
   * Les cadres sont VIDES, et c'est un choix, pas un oubli. Y peindre les
   * dessins des joueurs demanderait des TEXTURES — une par dessin, refaite a
   * chaque accrochage — alors que le projet n'en a pas une seule et que tout y
   * est procedural. Les toiles se regardent dans la galerie, en DOM, ou elles
   * coutent zero appel de dessin et se lisent sur un telephone. Ce que la scene
   * doit dire, c'est « il y a quelque chose a voir ici », et un mur de cadres
   * le dit.
   */
  musee: (c) => {
    const CADRE = 0xb08a4a
    const TOILE = 0xfdfaf2
    const hCadre = 1.35
    const y = 1.85
    // Trois cadres par mur lateral, deux au fond: les positions sont deduites
    // de l'emprise, jamais ecrites en dur, pour que le modele suive sa taille.
    for (const signe of [1, -1]) {
      const x = (signe * (c.l - 0.4)) / 2
      for (let i = 0; i < 3; i++) {
        const z = (i - 1) * (c.p / 3.4)
        pBoite(c.sol, CADRE, [0.1, hCadre + 0.18, 1.05], [x, y, z])
        pBoite(c.sol, TOILE, [0.05, hCadre, 0.88], [x - signe * 0.06, y, z])
      }
    }
    for (const signe of [1, -1]) {
      const x = signe * (c.l / 5)
      pBoite(c.sol, CADRE, [1.25, hCadre + 0.18, 0.1], [x, y, c.p / 2 - 0.2])
      pBoite(c.sol, TOILE, [1.08, hCadre, 0.05], [x, y, c.p / 2 - 0.26])
    }
    // Le banc du milieu: c'est lui qui fait la salle de musee plutot que le
    // couloir. Il ne bloque rien, comme tout le mobilier.
    pBoite(c.sol, BOIS_CLAIR, [0.7, 0.1, 2.2], [0, 0.46, 0])
    for (const sz of [1, -1]) {
      pBoite(c.sol, BOIS_FONCE, [0.5, 0.42, 0.16], [0, 0.21, sz * 0.85])
    }
    // Un socle et une sculpture, pour qu'il y ait aussi du volume a regarder.
    pCyl(c.sol, 0xdcd6c8, 0.42, 0.46, 0.9, 10, [-c.l / 3.2, 0.45, -c.p / 3.4])
    pSphere(c.sol, 0xc9c2b2, 0.34, [-c.l / 3.2, 1.18, -c.p / 3.4], [1, 1.3, 1], 8, 6)
  },

  /*
   * LES TROIS AEROGARES partagent le MEME dessin: c'est `aerogare` ci-dessous,
   * appelee par les trois entrees. Elles ne different que par leurs couleurs
   * (voir STYLES) — « fonctionnellement identiques » va jusque-la.
   */
  aerodrome: (c) => aerogare(c),
  aeroport: (c) => aerogare(c),
  aeroport_verre: (c) => aerogare(c),

  /**
   * LA MINE: une galerie de roche, ses cadres de bois, ses rails et son wagon.
   *
   * ELLE N'EST PAS SOUS TERRE, ET ELLE NE PEUT PAS L'ETRE. Le moteur n'a
   * AUCUNE verticalite: un joueur n'a que (x, z), la navigation est une seule
   * couche, et le seul « en dessus » du jeu est le NIVEAU discret des etages
   * (voir « Les etages »). Un vrai puits demanderait un niveau NEGATIF, un
   * terrain perce et une camera qui sait descendre — un chantier entier. Ce
   * qu'on dessine ici est donc la galerie telle qu'on la voit en coupe, a 45
   * degres, exactement comme on voit le salon d'une maison dont le toit s'est
   * efface: sol de roche, cadres de soutenement, lanternes, et la roche vive au
   * fond. C'est ce que la vue permet de raconter, et ca se lit tout de suite.
   *
   * Tout part dans `c.sol`, donc dans la MEME maille que le reste de
   * l'interieur: la mine coute exactement ce que coute une maison ordinaire.
   */
  mine: (c) => {
    const ROCHE_SOL = 0x4a443c
    const ROCHE_MUR = 0x5a5148
    const RAIL = 0x8f959c

    // Le sol de la galerie: de la roche, pas du parquet (`plancher: false`).
    pPlaque(c.sol, ROCHE_SOL, c.l - 0.3, c.p - 0.3, [0, 0.02, 0])

    // Les CADRES DE SOUTENEMENT, tous les deux metres: deux montants et un
    // linteau. Ce sont eux qui font lire une galerie plutot qu'un hangar vide,
    // et ils ne bloquent rien — comme tout le mobilier.
    const hCadre = Math.min(2.7, c.style.hauteurMur - 0.4)
    const demiL = c.l / 2 - 0.55
    for (let i = 0; i < 3; i++) {
      const z = -c.p / 2 + 1.6 + i * ((c.p - 2.6) / 2)
      for (const signe of [1, -1]) {
        pBoite(c.sol, BOIS_FONCE, [0.22, hCadre, 0.22], [signe * demiL, hCadre / 2, z])
      }
      pBoite(c.sol, BOIS, [demiL * 2 + 0.22, 0.22, 0.26], [0, hCadre, z])
    }

    // Les parois laterales, sous les cadres: de la roche brute, en retrait des
    // murs de bois pour qu'on voie les deux.
    for (const signe of [1, -1]) {
      pBoite(c.sol, ROCHE_MUR, [0.5, 2.1, c.p - 1.4], [signe * (c.l / 2 - 0.6), 1.05, 0.2], [0, signe * 0.02, 0])
    }

    // LES RAILS, dans l'axe de la porte, et le wagonnet pousse contre le fond:
    // c'est ce qui dit ou l'on va en entrant — vers le front de taille.
    for (const signe of [1, -1]) {
      pBoite(c.sol, RAIL, [0.08, 0.07, c.p - 2.2], [signe * 0.42, 0.06, 0.2])
    }
    for (let i = 0; i < 7; i++) {
      const z = -c.p / 2 + 1.4 + i * ((c.p - 2.8) / 6)
      pBoite(c.sol, BOIS_FONCE, [1.3, 0.06, 0.16], [0, 0.03, z])
    }
    const wz = -c.p / 2 + 2.6
    pBoite(c.sol, 0x6d5a44, [1.1, 0.62, 1.0], [0, 0.45, wz])
    pBoite(c.sol, teinter(ROCHE_MUR, 1.2), [0.9, 0.16, 0.8], [0, 0.8, wz])
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pCyl(c.sol, METAL, 0.16, 0.16, 0.07, 8, [sx * 0.5, 0.16, wz + sz * 0.34], [0, 0, Math.PI / 2])
      }
    }

    // Les lanternes accrochees aux cadres. Elles vont dans `nuit` par le biais
    // de la veilleuse du style (une seule source, pool fixe): ici on ne pose
    // que le verre, qui brille dans la maille des fenetres.
    for (const signe of [1, -1]) {
      pCyl(c.vitres, 0xffd88a, 0.11, 0.11, 0.22, 6, [signe * (demiL - 0.2), hCadre - 0.4, -c.p / 2 + 1.6])
      pTube(c.sol, METAL, 0.02, [signe * (demiL - 0.2), hCadre, -c.p / 2 + 1.6], [signe * (demiL - 0.2), hCadre - 0.28, -c.p / 2 + 1.6])
    }

    /*
     * LE CHEVALEMENT, DEHORS: deux jambes de bois, une traverse et la GRANDE
     * ROUE. C'est la silhouette qui dit « mine » de loin — sans elle, le modele
     * se lit comme un hangar a outils de plus, et un batiment qu'on ne
     * reconnait pas est un batiment qu'on ne visite pas.
     *
     * Il va dans `dehors`, le lot qui ne s'efface JAMAIS: il est au-dessus du
     * toit, donc entrer dans la galerie ne doit pas le faire disparaitre — un
     * decor exterieur qui s'evanouit se lit comme un bogue (la lecon des
     * palmiers de l'hotel).
     */
    const zTour = -c.p / 2 - 0.9
    const hTour = c.style.hauteurMur + 2.6
    for (const signe of [1, -1]) {
      pTube(c.dehors, BOIS_FONCE, 0.12, [signe * 1.5, 0, zTour + 0.7], [signe * 0.55, hTour, zTour])
      pTube(c.dehors, BOIS, 0.09, [signe * 1.2, 1.5, zTour + 0.5], [signe * 0.9, hTour - 1.2, zTour + 0.2])
    }
    pBoite(c.dehors, BOIS, [2.4, 0.2, 0.24], [0, hTour - 0.1, zTour])
    pCyl(c.dehors, METAL, 0.62, 0.62, 0.14, 12, [0, hTour + 0.35, zTour], [0, 0, Math.PI / 2])
    pCyl(c.dehors, teinter(METAL, 0.8), 0.18, 0.18, 0.2, 8, [0, hTour + 0.35, zTour], [0, 0, Math.PI / 2])
    // Le cable, de la roue jusqu'au seuil: c'est lui qui raconte la descente.
    pTube(c.dehors, 0x4a4f55, 0.035, [0, hTour + 0.3, zTour], [0, 0.4, zTour + 0.9])

    // La lampe de la piece eclaire le FOND, la ou l'on pioche, et pas le
    // centre geometrique: c'est le seul endroit de la galerie ou l'on s'arrete.
    const filon = positionFilon(c.b)
    c.m.posLumiere.set(
      c.b.pos.x + (filon.x - c.b.pos.x) * 0.7,
      c.b.y + 2.4,
      c.b.pos.z + (filon.z - c.b.pos.z) * 0.7,
    )
  },

  /**
   * LA BIBLIOTHEQUE: des rayonnages garnis, une table et sa lampe.
   *
   * Les livres sont des boites minces de largeurs et de couleurs variees, tirees
   * d'un hachage de leur position: deux rayons ne se ressemblent donc jamais, et
   * pourtant le meme batiment repose a la meme place rend exactement la meme
   * chose. Un `Math.random` ici aurait fait scintiller les livres a chaque
   * reconstruction du maillage.
   */
  bibliotheque: (c) => {
    const RAYON = 0x8a6a44
    const COUVERTURES = [0xc94a3f, 0x3f6fa5, 0x4f8f5a, 0xd8a63a, 0x7b57c9, 0xd45fa8]
    const hRayon = 2.5
    const profRayon = 0.42

    /**
     * Une etagere garnie sur QUATRE planches — il y en avait trois, et le
     * proprietaire a demande beaucoup plus de livres: une planche de plus par
     * rayon, et des rayons en ilot au milieu de la salle (plus bas).
     */
    const etagere = (x: number, z: number, largeur: number, versX: boolean): void => {
      const dl = versX ? profRayon : largeur
      const dp = versX ? largeur : profRayon
      pBoite(c.sol, RAYON, [dl, hRayon, dp], [x, hRayon / 2, z])
      for (let n = 0; n < 4; n++) {
        const yPlanche = 0.38 + n * 0.52
        // Les livres, ranges le long de la planche.
        let t = -largeur / 2 + 0.12
        let i = 0
        while (t < largeur / 2 - 0.12) {
          // Largeur et couleur DETERMINISTES: `hache2` sur la position.
          const h = hache2(Math.round((x + t) * 37), Math.round((z + n) * 53), 0x5f2a1b)
          const ep = 0.05 + h * 0.07
          if (t + ep > largeur / 2 - 0.12) break
          const couleur = COUVERTURES[Math.floor(h * COUVERTURES.length) % COUVERTURES.length] ?? COUVERTURES[0]
          const hLivre = 0.34 + h * 0.16
          const px = versX ? x - profRayon * 0.15 : x + t + ep / 2
          const pz = versX ? z + t + ep / 2 : z - profRayon * 0.15
          pBoite(
            c.sol,
            couleur ?? 0xc94a3f,
            versX ? [profRayon * 0.6, hLivre, ep] : [ep, hLivre, profRayon * 0.6],
            [px, yPlanche + hLivre / 2, pz],
          )
          t += ep + 0.012
          i++
          if (i > 60) break
        }
        pBoite(
          c.sol,
          teinter(RAYON, 1.15),
          versX ? [profRayon, 0.05, largeur] : [largeur, 0.05, profRayon],
          [x, yPlanche, z],
        )
      }
    }

    // Deux murs de rayons, le fond, et DEUX ILOTS au milieu: une vraie
    // bibliotheque a des allees, pas trois murs garnis autour d'un vide.
    for (const signe of [1, -1]) {
      etagere((signe * (c.l - profRayon)) / 2, 0, c.p - 1.6, true)
    }
    etagere(0, c.p / 2 - profRayon / 2, c.l - 1.6, false)
    for (const signe of [1, -1]) {
      etagere(signe * 2.1, 0.7, 2.6, false)
    }

    /*
     * LE PRESENTOIR DES BANDES DESSINEES, face a la porte: des couvertures
     * inclinees, grandes et colorees — c'est le rayon qu'on vient lire dans le
     * panneau, il merite d'etre le premier qu'on voit en entrant.
     */
    const pz = -c.p / 2 + 1.1
    pBoite(c.sol, teinter(RAYON, 1.1), [2.2, 0.9, 0.5], [-c.l / 2 + 1.5, 0.45, pz + 0.4])
    for (const [i, couleur] of [0xc94a3f, 0xf5d13a, 0x3f6fa5, 0xd45fa8].entries()) {
      pBoite(c.sol, couleur, [0.42, 0.56, 0.04], [-c.l / 2 + 0.72 + i * 0.52, 0.92, pz + 0.32], [-0.35, 0, 0])
      pBoite(c.sol, BLANC, [0.3, 0.3, 0.02], [-c.l / 2 + 0.72 + i * 0.52, 0.94, pz + 0.29], [-0.35, 0, 0])
    }

    // Le coin de lecture des petits: un tapis rond, trois coussins, une pile
    // d'albums — on s'assoit par terre, comme devant les bandes dessinees.
    const cx = c.l / 2 - 1.6
    pCyl(c.sol, teinter(TAPIS, 1.1), 1.0, 1.0, 0.03, 14, [cx, 0.015, pz + 0.5])
    for (const [i, a] of [0.6, 2.4, 4.2].entries()) {
      pBoite(c.sol, i % 2 === 0 ? COUSSIN : TISSU_CLAIR, [0.44, 0.16, 0.4], [
        cx + Math.cos(a) * 0.72,
        0.08,
        pz + 0.5 + Math.sin(a) * 0.6,
      ], [0, a, 0])
    }
    for (let i = 0; i < 3; i++) {
      pBoite(c.sol, COUVERTURES[(i * 2) % COUVERTURES.length] ?? 0xc94a3f, [0.34, 0.05, 0.26], [cx, 0.03 + i * 0.05, pz + 0.5], [0, i * 0.4, 0])
    }

    // La table de lecture et ses deux chaises, a l'avant, pres de la porte.
    const tz = -c.p / 5
    pBoite(c.sol, BOIS_CLAIR, [2.1, 0.08, 1.1], [0, 0.76, tz])
    for (const [sx, sz] of [
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ] as const) {
      pBoite(c.sol, BOIS, [0.1, 0.72, 0.1], [sx * 0.92, 0.36, tz + sz * 0.44])
    }
    for (const sx of [1, -1]) {
      pBoite(c.sol, BOIS, [0.46, 0.07, 0.44], [sx * 1.5, 0.46, tz])
      pBoite(c.sol, BOIS, [0.07, 0.5, 0.44], [sx * 1.72, 0.72, tz])
    }
    // Un livre ouvert, une lampe, et deux piles d'emprunts qui attendent.
    pBoite(c.sol, BLANC, [0.5, 0.03, 0.36], [-0.2, 0.81, tz], [0, 0.2, 0])
    for (let i = 0; i < 4; i++) {
      pBoite(c.sol, COUVERTURES[i % COUVERTURES.length] ?? 0xc94a3f, [0.3, 0.045, 0.22], [-0.75, 0.82 + i * 0.045, tz + 0.3], [0, i * 0.3, 0])
    }
    for (let i = 0; i < 3; i++) {
      pBoite(c.sol, COUVERTURES[(i + 3) % COUVERTURES.length] ?? 0x3f6fa5, [0.28, 0.045, 0.2], [0.35, 0.82 + i * 0.045, tz - 0.32], [0, 0.5 + i * 0.35, 0])
    }
    pCyl(c.sol, METAL, 0.12, 0.12, 0.04, 8, [0.72, 0.82, tz])
    pCyl(c.sol, METAL, 0.03, 0.03, 0.4, 6, [0.72, 1.02, tz])
    pCyl(c.sol, ABAT_JOUR, 0.1, 0.18, 0.2, 8, [0.72, 1.3, tz])
  },

  /**
   * LE GARAGE: un etabli, des outils au mur, et la trace d'huile au sol.
   *
   * Rien au MILIEU: c'est la place de la voiture, et la remplir de mobilier
   * reviendrait a batir un garage ou l'on ne peut pas se garer. Le mobilier ne
   * bloque pourtant rien (aucun meuble n'a de collision), mais il se verrait
   * a travers la carrosserie, et un garage qui a l'air occupe n'invite pas a y
   * entrer.
   */
  garage: (c) => {
    const ETABLI = 0x7a5a3a
    const zEtabli = c.p / 2 - 0.55
    pBoite(c.sol, ETABLI, [c.l - 1.4, 0.1, 0.6], [0, 0.92, zEtabli])
    for (const sx of [1, -1]) {
      pBoite(c.sol, teinter(ETABLI, 0.8), [0.12, 0.88, 0.5], [sx * (c.l / 2 - 1.0), 0.44, zEtabli])
    }
    // Le panneau d'outils au-dessus de l'etabli.
    pBoite(c.sol, teinter(ETABLI, 1.2), [c.l - 2.0, 0.9, 0.06], [0, 1.75, zEtabli + 0.28])
    for (const [i, dx] of [-1.1, -0.4, 0.4, 1.1].entries()) {
      pBoite(c.sol, METAL, [0.07, i % 2 === 0 ? 0.5 : 0.36, 0.05], [dx, 1.75, zEtabli + 0.24])
      pBoite(c.sol, i % 2 === 0 ? 0xd45f3f : METAL, [0.16, 0.12, 0.05], [dx, 1.52, zEtabli + 0.24])
    }
    // Deux bidons dans un coin, et la tache d'huile ou la voiture se gare.
    for (const [i, sx] of [1, -1].entries()) {
      pCyl(c.sol, i === 0 ? 0xd8a63a : 0x4f7d8f, 0.2, 0.2, 0.46, 8, [sx * (c.l / 2 - 0.45), 0.23, zEtabli - 1.1])
    }
    pPlaque(c.sol, 0x3a3a3a, 1.9, 3.6, [0, 0.02, -0.4])
  },

  /**
   * LE ZOO: son enceinte et ses trois enclos.
   *
   * Tout vient de `PLAN_ZOO` (shared/data/zoo.ts), qui sert AUSSI a la
   * collision (`cloisonsInterieures` -> `resoudreMurs`) et a la reconnaissance
   * d'un enclos. La barriere qu'on voit est donc celle qui repousse, et
   * l'enclos qu'on regarde celui ou la bete s'installe. Recopier ces nombres
   * ici serait la meilleure facon de les faire diverger — le piege paye trois
   * fois dans ce projet.
   *
   * Tout part dans `c.sol`, le lot qui ne s'efface JAMAIS: le toit et les murs
   * disparaissent quand on entre dans l'emprise, et des barrieres posees dans
   * ces lots-la s'evanouiraient au moment precis ou l'on vient voir les betes.
   */
  zoo: (c) => {
    const BARRIERE = 0x8a6a44
    const BARRIERE_CLAIRE = 0xb08a5c
    const hPoteau = 1.15

    for (const piece of cloisonsInterieures(c.b.kind)) {
      const b = boiteEtage(c.b.kind, piece)

      // Un enclos: une barriere a claire-voie, ouverte sur sa face -z.
      const zAvant = b.cz - b.hz
      const demiPorte = piece.porte / 2
      /** Une lisse horizontale entre deux points, plus ses poteaux. */
      const pan = (x0: number, z0: number, x1: number, z1: number): void => {
        const dx = x1 - x0
        const dz = z1 - z0
        const longueur = Math.hypot(dx, dz)
        if (longueur < 0.05) return
        const mx = (x0 + x1) / 2
        const mz = (z0 + z1) / 2
        // Deux lisses: une barriere a un seul barreau se lit comme un trait.
        for (const hy of [0.45, 0.95]) {
          pBoite(c.sol, BARRIERE_CLAIRE, [Math.abs(dx) + 0.08, 0.1, Math.abs(dz) + 0.08], [mx, hy, mz])
        }
        // Poteaux repartis, jamais a pas fixe depuis un bord: les extremites
        // doivent tomber PILE sur les coins, sinon la barriere parait ouverte.
        const n = Math.max(2, Math.round(longueur / 1.6) + 1)
        for (let i = 0; i < n; i++) {
          const t = i / (n - 1)
          pBoite(c.sol, BARRIERE, [0.16, hPoteau, 0.16], [x0 + dx * t, hPoteau / 2, z0 + dz * t])
        }
      }

      pan(b.cx - b.hx, b.cz + b.hz, b.cx + b.hx, b.cz + b.hz)
      pan(b.cx - b.hx, b.cz - b.hz, b.cx - b.hx, b.cz + b.hz)
      pan(b.cx + b.hx, b.cz - b.hz, b.cx + b.hx, b.cz + b.hz)
      // La facade avant, avec son ouverture au milieu: c'est par la qu'on entre
      // s'occuper des betes, et c'est exactement le trou que `resoudreMurs`
      // laisse passer.
      pan(b.cx - b.hx, zAvant, b.cx - demiPorte, zAvant)
      pan(b.cx + demiPorte, zAvant, b.cx + b.hx, zAvant)
      // Deux montants plus hauts de chaque cote de l'ouverture: sans eux, une
      // barriere interrompue se lit comme une barriere cassee.
      for (const signe of [1, -1]) {
        pBoite(c.sol, BARRIERE, [0.2, hPoteau + 0.45, 0.2], [b.cx + signe * demiPorte, (hPoteau + 0.45) / 2, zAvant])
      }
      // Un peu d'herbe rase au sol: un enclos vide doit se lire comme un pre,
      // pas comme une case du plan.
      pPlaque(c.sol, 0x7fae55, b.hx * 2 - 0.3, b.hz * 2 - 0.3, [b.cx, 0.015, b.cz])

      /*
       * LE BASSIN, quand cet enclos en a un.
       *
       * Il n'est pas decoratif: c'est LUI que `peutInstallerDansEnclos`
       * interroge pour accepter un canard, exactement comme le spa de la
       * terrasse est de l'eau sans que le relief le soit. On le dessine donc
       * dans le lot translucide, avec sa margelle, pour qu'on voie du premier
       * coup d'oeil lequel des trois enclos accueille les oiseaux d'eau.
       */
      if (piece.bassin) {
        const rx = b.hx * 0.62
        const rz = b.hz * 0.5
        pBoite(c.sol, 0xb8a888, [rx * 2 + 0.3, 0.14, rz * 2 + 0.3], [b.cx, 0.07, b.cz])
        pBoite(c.sol, 0x2f6b7a, [rx * 2, 0.05, rz * 2], [b.cx, 0.03, b.cz])
        pBoite(c.eau, 0x4fc3e8, [rx * 2 - 0.1, 0.16, rz * 2 - 0.1], [b.cx, 0.11, b.cz])
      }
    }
  },

  /**
   * LE PARC AQUATIQUE AU SOL: le dallage, le grand bassin, le pavillon, les
   * trois fontaines — et les glissades, qui traversent les deux niveaux.
   *
   * OU VA QUOI, ET POURQUOI. C'est la seule question difficile de ce modele.
   *
   *   - `c.dehors` (ne s'efface JAMAIS) porte presque tout: le dallage, la cuve
   *     du bassin, les fontaines, les palmiers, les glissades. La passerelle
   *     est AJOUREE — on voit le parc a travers — donc effacer le sol quand on
   *     monte laisserait une eau qui flotte au-dessus du vide. C'est l'inverse
   *     exact de la villa, dont la dalle pleine oblige a effacer le salon.
   *   - `c.eauSol` porte l'eau, et lui seul: elle est translucide, et
   *     `appliquerOpacite` ecraserait son opacite si elle voyageait avec le
   *     reste.
   *   - `c.sol` (donc `bas`, qui s'efface quand on monte) ne porte que le
   *     PAVILLON. C'est une piece fermee, exactement comme les chambres de
   *     l'hotel, et elle suit la meme regle qu'elles.
   *
   * Tout est en repere LOCAL: le groupe porte deja le quart de tour, et les
   * boites viennent des memes tables que la collision (`bassinsDe`,
   * `cloisonsInterieures`, `FONTAINES_PARC`). Le bassin ou l'on nage est celui
   * qu'on voit, et le mur du pavillon qui repousse est celui qui est dessine.
   */
  parc_aquatique: (c) => {
    const DALLAGE = 0xd9d2c4
    const DALLAGE_CLAIR = 0xece6da
    const CUVE = 0x2f8fa8
    const MARGELLE = 0xeae0cc
    const l = c.l
    const p = c.p

    // ------------------------------------------------------------ le dallage
    //
    // Le sol du parc, avec ses joints: sans lui on marche sur l'herbe entre les
    // bassins, ce qui se lit comme un terrain vague clôturé.
    pPlaque(c.dehors, DALLAGE, l - 0.4, p - 0.4, [0, 0.02, 0])
    for (let i = 1; i < 6; i++) {
      pBoite(c.dehors, DALLAGE_CLAIR, [0.08, 0.02, p - 0.4], [-l / 2 + (i * l) / 6, 0.03, 0])
    }
    for (let i = 1; i < 4; i++) {
      pBoite(c.dehors, DALLAGE_CLAIR, [l - 0.4, 0.02, 0.08], [0, 0.03, -p / 2 + (i * p) / 4])
    }

    // ------------------------------------------------------- le grand bassin
    //
    // Sa boite vient de `bassinsDe`, la table que `dansPiscine` interroge: le
    // bord dessine et le bord ou l'on se met a nager sont le meme bord. Une
    // cuve creuse (fond carrele, quatre parois) et non un bloc plein, parce que
    // l'eau est translucide et qu'on voit le fond a travers.
    for (const bassin of bassinsDe(c.b.kind)) {
      const bo = boiteEtage(c.b.kind, bassin)
      const hx = bo.hx
      const hz = bo.hz
      // La margelle, tout autour, assez basse pour s'enjamber.
      pBoite(c.dehors, MARGELLE, [hx * 2 + 0.8, 0.32, hz * 2 + 0.8], [bo.cx, 0.16, bo.cz])
      // Le fond, et ses lignes de nage: un rectangle bleu uni se lit comme une
      // bache, pas comme un bassin.
      pBoite(c.dehors, CUVE, [hx * 2, 0.1, hz * 2], [bo.cx, 0.06, bo.cz])
      for (let i = 1; i < 4; i++) {
        pBoite(c.dehors, teinter(CUVE, 0.72), [hx * 2 - 0.6, 0.02, 0.16], [bo.cx, 0.12, bo.cz - hz + (i * hz * 2) / 4])
      }
      // L'EAU: un volume, dans le lot qui garde sa transparence.
      pBoite(c.eauSol, 0x4fc3e8, [hx * 2, 0.3, hz * 2], [bo.cx, 0.2, bo.cz])
      // L'echelle du petit bain, contre le bord ouest: on entre par la, et un
      // bassin sans echelle ne dit pas comment on en sort.
      for (const cote of [1, -1]) {
        pTube(
          c.dehors,
          METAL,
          0.045,
          [bo.cx - hx + 0.25, 0.9, bo.cz + cote * 0.42],
          [bo.cx - hx - 0.35, 0.05, bo.cz + cote * 0.42],
        )
        pCyl(c.dehors, METAL, 0.045, 0.045, 0.9, 6, [bo.cx - hx + 0.25, 0.45, bo.cz + cote * 0.42])
      }
    }

    // ------------------------------------------------------------ le pavillon
    //
    // Une CLOISON INTERIEURE, le mecanisme des enclos du zoo et des chambres de
    // l'hotel: la porte dessinee est exactement le trou que `resoudreMurs`
    // laisse passer, et sa largeur vient de la table, pas d'ici.
    const hCloison = 2.6
    const hPorte = 2.0
    // Nettement plus sombre que l'enceinte: sans ca, le pavillon se confond avec
    // le mur du parc et l'on ne voit plus qu'on peut y entrer.
    const cCloison = teinter(c.cMurs, 0.84)
    for (const piece of cloisonsInterieures(c.b.kind)) {
      const bo = boiteEtage(c.b.kind, piece)
      pBoite(c.sol, cCloison, [bo.hx * 2, hCloison, 0.14], [bo.cx, hCloison / 2, bo.cz + bo.hz - 0.07])
      for (const signe of [1, -1]) {
        pBoite(c.sol, cCloison, [0.14, hCloison, bo.hz * 2], [bo.cx + signe * (bo.hx - 0.07), hCloison / 2, bo.cz])
      }
      const cote = (bo.hx * 2 - piece.porte) / 2
      const zAvant = bo.cz - bo.hz + 0.07
      for (const signe of [1, -1]) {
        pBoite(c.sol, cCloison, [cote, hCloison, 0.14], [bo.cx + (signe * (piece.porte + cote)) / 2, hCloison / 2, zAvant])
      }
      pBoite(c.sol, cCloison, [piece.porte, hCloison - hPorte, 0.14], [bo.cx, hPorte + (hCloison - hPorte) / 2, zAvant])
      for (const signe of [1, -1]) {
        pBoite(c.sol, c.cPorte, [0.1, hPorte, 0.12], [bo.cx + signe * (piece.porte / 2 + 0.05), hPorte / 2, zAvant - 0.02])
      }
      pBoite(c.sol, c.cPorte, [piece.porte + 0.2, 0.1, 0.12], [bo.cx, hPorte + 0.05, zAvant - 0.02])
      // Une corniche, jamais un toit: le fondu efface le rez-de-chaussee quand
      // on est en HAUT, jamais quand on y est — un toit ferme cacherait donc
      // pour toujours ce qu'on vient de mettre dedans. Le meme choix que la
      // grande piece de la villa.
      for (const signe of [1, -1]) {
        pBoite(c.sol, c.cToit, [bo.hx * 2 + 0.26, 0.13, 0.26], [bo.cx, hCloison + 0.06, bo.cz + signe * bo.hz])
        pBoite(c.sol, c.cToit, [0.26, 0.13, bo.hz * 2 + 0.26], [bo.cx + signe * bo.hx, hCloison + 0.06, bo.cz])
      }
      // DEDANS: un banc, une rangee de casiers, une douche. Un vestiaire vide
      // se lit comme une piece qu'on a oublie de finir.
      pBoite(c.sol, BOIS_CLAIR, [bo.hx * 1.3, 0.1, 0.42], [bo.cx, 0.46, bo.cz + bo.hz * 0.45])
      for (const sx of [1, -1]) {
        pBoite(c.sol, BOIS_FONCE, [0.1, 0.42, 0.36], [bo.cx + sx * bo.hx * 0.6, 0.21, bo.cz + bo.hz * 0.45])
      }
      for (let i = 0; i < 4; i++) {
        const cx = bo.cx - bo.hx + 0.55 + i * 0.62
        pBoite(c.sol, i % 2 === 0 ? 0x4fa8c4 : 0x3f8fa5, [0.56, 1.85, 0.44], [cx, 0.93, bo.cz - bo.hz + 0.42])
        pSphere(c.sol, METAL, 0.05, [cx + 0.2, 1.0, bo.cz - bo.hz + 0.2], undefined, 5, 3)
      }
      const dx = bo.cx + bo.hx - 0.6
      pCyl(c.sol, METAL, 0.05, 0.05, 2.2, 6, [dx, 1.1, bo.cz - bo.hz * 0.5])
      pCyl(c.sol, METAL, 0.24, 0.2, 0.09, 10, [dx, 2.16, bo.cz - bo.hz * 0.5 + 0.16])
    }

    // ----------------------------------------------------------- les fontaines
    //
    // « Nice fountains ». Du DECOR: aucune ne repousse personne et aucune ne
    // declenche rien, exactement comme le palmier du toit de l'hotel. Leurs
    // positions viennent quand meme du plan (`FONTAINES_PARC`) et non d'ici:
    // deux ecritures des memes points finiraient par ne plus etre d'accord, et
    // les tests ne pourraient plus verifier qu'aucune n'est plantee dans un mur.
    for (const [i, f] of FONTAINES_PARC.entries()) {
      const fx = l * f.x
      const fz = p * f.z
      // Celle du milieu jaillit DE L'EAU: pas de vasque, on nage autour.
      const dansLEau = i === 1
      const r = dansLEau ? 0.7 : 0.9
      if (!dansLEau) {
        pCyl(c.dehors, MARGELLE, r, r * 1.06, 0.42, 14, [fx, 0.21, fz])
        pCyl(c.dehors, CUVE, r - 0.16, r - 0.16, 0.1, 14, [fx, 0.36, fz])
        pCyl(c.eauSol, 0x4fc3e8, r - 0.16, r - 0.16, 0.2, 14, [fx, 0.42, fz])
      } else {
        pCyl(c.dehors, teinter(MARGELLE, 0.88), r, r * 1.08, 0.46, 14, [fx, 0.23, fz])
      }
      // Le fut et la vasque haute: c'est la silhouette qui dit « fontaine » de
      // loin, et c'est ce qui se lit dans une vue de dessus a 45 degres.
      const hFut = dansLEau ? 1.9 : 1.15
      pCyl(c.dehors, MARGELLE, 0.16, 0.24, hFut, 10, [fx, 0.4 + hFut / 2, fz])
      pCyl(c.dehors, teinter(MARGELLE, 0.92), 0.55, 0.38, 0.16, 12, [fx, 0.4 + hFut, fz])
      /*
       * LE JET, ET C'EST UNE NAPPE, PAS DES AIGUILLES.
       *
       * Premiere version: six tubes fins tires du sommet vers l'exterieur. En
       * vue de dessus a 45 degres, ils se lisaient comme les baleines d'un
       * parapluie casse — parce qu'un trait vu de dessus reste un trait, alors
       * qu'une nappe d'eau se lit a son VOLUME. C'est la lecon du front de
       * taille de la mine, dans une autre matiere: ce qui se lit toujours dans
       * cette vue, c'est un dessus. On dessine donc la gerbe: une colonne, un
       * bouillon, et un cone renverse — l'eau qui retombe tout autour.
       *
       * Tout part dans `eauSol`, avec le bassin: l'eau est translucide, et un
       * cone opaque ferait une bougie de pierre (le piege deja paye par le spa
       * de la villa). Une seule maille pour toute l'eau du modele.
       */
      /*
       * ET ELLE RESTE PETITE. La premiere nappe montait a 2,8 m sur 3 m de
       * large: au sol, elle avalait la moitie du bassin et se lisait comme une
       * tente blanche plantee dans l'eau. Une fontaine est un DETAIL du parc,
       * pas son sujet — et une eau translucide de cette taille cache tout ce
       * qui est derriere elle sans qu'on comprenne pourquoi.
       */
      const hJet = dansLEau ? 1.8 : 1.0
      const yJet = 0.4 + hFut + 0.16
      const rNappe = dansLEau ? 0.95 : 0.55
      // Un bleu tres pale plutot qu'un blanc: du blanc translucide se lit comme
      // de la brume, ce bleu-la se lit comme de l'eau — la meme teinte que le
      // bassin, en beaucoup plus clair.
      const GERBE = 0xc8eefc
      pCyl(c.eauSol, GERBE, 0.08, 0.17, hJet, 8, [fx, yJet + hJet / 2, fz])
      pSphere(c.eauSol, GERBE, dansLEau ? 0.24 : 0.17, [fx, yJet + hJet, fz], [1, 0.75, 1], 8, 5)
      // La nappe: un cone qui s'evase VERS LE BAS, de la crete jusqu'a l'eau.
      pCyl(c.eauSol, GERBE, 0.2, rNappe, hJet * 0.72, 12, [fx, yJet + hJet * 0.6, fz])
      // Et quelques gouttes sur son pourtour, pour que la nappe ait un bord.
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * Math.PI * 2 + i
        pSphere(
          c.eauSol,
          GERBE,
          dansLEau ? 0.13 : 0.1,
          [fx + Math.cos(a) * rNappe * 0.96, yJet + hJet * 0.24, fz + Math.sin(a) * rNappe * 0.96],
          [1, 0.7, 1],
          6,
          4,
        )
      }
    }

    // ------------------------------------------------------ palmiers et bains
    //
    // Trois palmiers dans les coins libres et deux transats au bord de l'eau:
    // « a big place » se lit a ce qui l'habite, pas a son emprise. Ils sont
    // dans `dehors` — un jardin qui disparait parce qu'on est monte se lirait
    // comme un bogue, la lecon deja payee par l'hotel.
    for (const [k, [px, pz]] of ([
      [-l * 0.44, p * 0.42],
      [l * 0.44, -p * 0.43],
      [-l * 0.05, -p * 0.44],
    ] as const).entries()) {
      palmier(c.dehors, px, 0.05, pz, 3.6 + k * 0.5, phaseDe(c.b.id) + k)
    }
    for (const [k, pz] of [-p * 0.34, p * 0.3].entries()) {
      const tx = -l * 0.06
      pBoite(c.dehors, BOIS_CLAIR, [1.8, 0.08, 0.6], [tx, 0.36, pz], [0, 0, 0.06])
      pBoite(c.dehors, teinter(TISSU_CLAIR, 1 + k * 0.1), [0.7, 0.06, 0.56], [tx - 0.7, 0.55, pz], [0, 0, -0.5])
      for (const sx of [1, -1]) pCyl(c.dehors, METAL, 0.04, 0.04, 0.3, 6, [tx + sx * 0.78, 0.16, pz])
    }

    // Et les trois toboggans, qui traversent les deux niveaux.
    glissadesDuParc(c)
  },

  cabane_arbre: (c) => {
    const hTronc = c.style.hauteurMur + c.style.hauteurToit + 2.6
    const zT = c.p / 2 - 0.8
    // Le tronc traverse la piece: il part avec le toit quand on entre, sinon il
    // masquerait la moitie de la cabane.
    pCyl(c.toit, BOIS_FONCE, 0.32, 0.52, hTronc, 8, [0, hTronc / 2, zT])
    pTube(c.toit, BOIS_FONCE, 0.11, [0, hTronc - 1.5, zT], [-1.7, hTronc - 0.9, zT - 0.4])
    pTube(c.toit, BOIS_FONCE, 0.1, [0, hTronc - 1.1, zT], [1.5, hTronc - 0.4, zT + 0.5])
    for (const [dx, dy, dz, r] of [
      [0, 0.5, 0, 1.7],
      [-1.5, -0.1, -0.5, 1.2],
      [1.4, 0.0, 0.6, 1.25],
      [0.1, 0.2, -1.4, 1.1],
    ] as const) {
      const geo = bosseler(new THREE.SphereGeometry(r, 8, 6), 0.28, r * 100)
      pGeo(c.toit, dx < 0 ? FEUILLE : FEUILLE_FONCE, geo, [dx, hTronc + dy, zT + dz], undefined, [1, 0.72, 1])
    }
    // Perchee: pilotis visibles sous le plancher et une echelle a la porte.
    for (const [sx, sz] of [
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ] as const) {
      pBoite(c.sol, BOIS_FONCE, [0.22, 0.7, 0.22], [(sx * (c.l - 0.5)) / 2, -0.35, (sz * (c.p - 0.5)) / 2])
    }
    pEchelle(c.sol, BOIS_CLAIR, 0.5, 2.5, tr([c.l / 4 + 0.3, 0, -c.p / 2 - 0.25], [-0.22, 0, 0]))
  },

  /*
   * LA VILLA SE MEUBLE DE PLANTES.
   *
   * Elle etait la plus chere du catalogue et la plus nue: une boite blanche a
   * toit plat, sans un seul EXTRAS. On y ajoute de la verdure, et rien d'autre.
   *
   * Tout part dans `c.sol`, le lot qui ne s'efface JAMAIS: le toit et les murs
   * disparaissent quand on entre, et des plantes posees dans ces lots-la
   * s'evanouiraient au moment precis ou on vient les voir.
   *
   * Les emplacements evitent les cinq ancrages de meubles (ANCRAGE_MEUBLE, en
   * FRACTIONS de l'emprise) et le passage de la porte, qui est toujours au
   * milieu de la face -z. On travaille en coordonnees LOCALES: le groupe entier
   * est tourne ensuite, donc il n'y a aucun cas de quart de tour a ecrire — la
   * regle du projet, et un bogue deja paye.
   */
  villa: (c) => {
    const POT = 0xb5643c
    const POT_SOMBRE = 0x8f4a2c
    const demiL = c.l / 2
    const demiP = c.p / 2

    /** Un pot et sa touffe: le motif se repete, autant l'ecrire une fois. */
    const enPot = (x: number, z: number, ech: number, sombre: boolean): void => {
      const rPot = 0.26 * ech
      pCyl(c.sol, sombre ? POT_SOMBRE : POT, rPot, rPot * 0.78, 0.34 * ech, 8, [x, 0.17 * ech, z])
      pCyl(c.sol, teinter(sombre ? POT_SOMBRE : POT, 0.88), rPot * 1.08, rPot * 1.08, 0.05 * ech, 8, [
        x,
        0.35 * ech,
        z,
      ])
      // Terre visible: sans elle, le feuillage flotte au-dessus du pot.
      pCyl(c.sol, 0x3b2a1c, rPot * 0.9, rPot * 0.9, 0.04 * ech, 8, [x, 0.36 * ech, z])
      pSphere(c.sol, FEUILLE, 0.34 * ech, [x, 0.62 * ech, z], [1, 0.86, 1], 6, 4)
      pSphere(c.sol, FEUILLE_FONCE, 0.24 * ech, [x + 0.14 * ech, 0.82 * ech, z - 0.08 * ech], undefined, 6, 4)
      pSphere(c.sol, FEUILLE, 0.19 * ech, [x - 0.16 * ech, 0.8 * ech, z + 0.1 * ech], undefined, 6, 4)
    }

    // Les quatre coins de la piece: loin des meubles, et c'est la que la piece
    // parait la plus vide.
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        enPot(sx * (demiL - 0.75), sz * (demiP - 0.75), 1, sz < 0)
      }
    }

    // Un grand sujet contre le mur de gauche, au milieu: un tronc nu et une
    // couronne de palmes, pour casser la hauteur des 3,2 m de mur.
    const xPalmier = -demiL + 0.85
    pCyl(c.sol, POT_SOMBRE, 0.4, 0.32, 0.5, 8, [xPalmier, 0.25, 0])
    pCyl(c.sol, BOIS_CLAIR, 0.09, 0.12, 1.9, 6, [xPalmier, 1.45, 0])
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2
      pBoite(
        c.sol,
        i % 2 === 0 ? FEUILLE : FEUILLE_FONCE,
        [0.9, 0.05, 0.24],
        [xPalmier + Math.cos(a) * 0.42, 2.42 - (i % 3) * 0.09, Math.sin(a) * 0.42],
        [0, -a, -0.22],
      )
    }

    // Une jardiniere sous chaque fenetre de la face droite, a l'INTERIEUR: la
    // face elle-meme s'efface quand on entre, la jardiniere doit rester.
    const xBac = demiL - 0.3
    for (const dz of [-c.p / 4, c.p / 4]) {
      pBoite(c.sol, laver(POT, 0.35), [0.34, 0.26, 1.5], [xBac, 0.13, dz])
      pBoite(c.sol, 0x3b2a1c, [0.26, 0.04, 1.36], [xBac, 0.26, dz])
      for (let i = 0; i < 5; i++) {
        const z = dz - 0.55 + (i / 4) * 1.1
        pSphere(c.sol, i % 2 === 0 ? FEUILLE : FEUILLE_FONCE, 0.17, [xBac, 0.36, z], [1, 0.8, 1], 6, 4)
      }
    }

    // Deux vasques encadrent la porte, ecartees de la largeur du passage pour
    // qu'on n'ait jamais a les traverser en entrant.
    for (const sx of [1, -1]) {
      const x = sx * (L_PORTE / 2 + 0.75)
      pCyl(c.sol, POT, 0.3, 0.2, 0.42, 8, [x, 0.21, -demiP + 0.45])
      pSphere(c.sol, FEUILLE_FONCE, 0.3, [x, 0.6, -demiP + 0.45], [1, 0.78, 1], 6, 4)
    }
  },

  grange: (c) => {
    const h = c.style.hauteurMur
    // Rechampis blancs: c'est ce qui fait lire "grange" et pas "hangar".
    const trim = laver(c.cMurs, 0.86)
    for (const signe of [1, -1]) {
      pBoite(c.faces.avant, trim, [0.16, h, 0.1], [(signe * (c.l - 0.3)) / 2, h / 2, -c.p / 2 + 0.05])
      pBoite(c.faces.arriere, trim, [0.16, h, 0.1], [(signe * (c.l - 0.3)) / 2, h / 2, c.p / 2 - 0.05])
    }
    pBoite(c.faces.avant, trim, [c.l, 0.18, 0.1], [0, h - 0.12, -c.p / 2 + 0.05])
    for (const signe of [1, -1]) {
      const face = signe > 0 ? c.faces.droite : c.faces.gauche
      for (const dz of [-c.p / 3, 0, c.p / 3]) {
        pBoite(face, trim, [0.1, h, 0.16], [(signe * (c.l - 0.2)) / 2, h / 2, dz])
      }
    }
    // Porte de fenil dans le pignon, poulie, et girouette sur le faitage.
    pBoite(c.toit, c.cPorte, [1.5, 1.1, 0.12], [0, h + 0.75, -c.p / 2 - 0.02])
    pBoite(c.toit, trim, [1.5, 0.12, 0.16], [0, h + 1.34, -c.p / 2 - 0.04])
    pTube(c.toit, BOIS_FONCE, 0.07, [0, h + 1.5, -c.p / 2 - 0.05], [0, h + 1.5, -c.p / 2 - 0.6])
    pCyl(c.toit, METAL, 0.13, 0.13, 0.08, 8, [0, h + 1.42, -c.p / 2 - 0.55], [0, 0, Math.PI / 2])
    const hFaite = h + c.style.hauteurToit
    pBoite(c.toit, trim, [0.8, 0.6, 0.8], [0, hFaite + 0.3, 0])
    pCone(c.toit, c.cToit, 0.7, 0.5, 4, [0, hFaite + 0.85, 0], [0, Math.PI / 4, 0])
    pBoite(c.toit, METAL, [0.06, 0.7, 0.06], [0, hFaite + 1.4, 0])
    pBoite(c.toit, METAL, [0.5, 0.22, 0.03], [0.2, hFaite + 1.65, 0])
    // Bottes de paille contre le mur de gauche, entre le salon et la cuisine:
    // le fond au milieu est le coin de l'animal (positionAnimal), et le centre
    // de la piece l'allee qui mene de la porte au reste.
    const xPaille = -c.l / 2 + 0.9
    for (const [dz, dy] of [
      [-0.35, 0],
      [0.35, 0],
      [0, 0.62],
    ] as const) {
      pCyl(c.sol, PAILLE, 0.3, 0.3, 0.58, 8, [xPaille, 0.42 + dy, dz], [0, 0, Math.PI / 2])
    }
  },

  phare: (c) => {
    // Hublots peints dans la maille des murs: ils s'effacent donc avec elle.
    for (const signe of [1, -1]) {
      const face = signe > 0 ? c.faces.droite : c.faces.gauche
      for (const dz of [-c.p / 5, c.p / 5]) {
        pCyl(face, 0x9fd4e8, 0.26, 0.26, 0.08, 10, [(signe * c.l) / 2 - signe * 0.04, 1.75, dz], [0, 0, Math.PI / 2])
        pCyl(face, METAL, 0.32, 0.32, 0.05, 10, [(signe * c.l) / 2 - signe * 0.01, 1.75, dz], [0, 0, Math.PI / 2])
      }
    }
    const base = c.style.hauteurMur + 0.35
    const bandes = 6
    const hBande = 1.08
    // Tour a bandes: la couleur des murs et celle du toit alternent, donc les
    // deux reglages du joueur servent vraiment a quelque chose.
    for (let i = 0; i < bandes; i++) {
      const rBas = 1.02 - (i * 0.42) / bandes
      const rHaut = 1.02 - ((i + 1) * 0.42) / bandes
      pCyl(
        c.toit,
        i % 2 === 0 ? c.cMurs : c.cToit,
        rHaut,
        rBas,
        hBande,
        12,
        [0, base + i * hBande + hBande / 2, 0],
      )
    }
    const yGalerie = base + bandes * hBande
    pCyl(c.toit, teinter(c.cMurs, 0.8), 1.02, 1.02, 0.16, 12, [0, yGalerie + 0.08, 0])
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2
      pBoite(c.toit, METAL, [0.06, 0.42, 0.06], [Math.cos(a) * 0.92, yGalerie + 0.37, Math.sin(a) * 0.92])
    }
    pCyl(c.toit, METAL, 0.9, 0.9, 0.06, 12, [0, yGalerie + 0.58, 0])
    // Cage de la lanterne, puis la lanterne elle-meme.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2
      pBoite(c.toit, METAL, [0.08, 1.0, 0.08], [Math.cos(a) * 0.55, yGalerie + 0.75, Math.sin(a) * 0.55])
    }
    pCone(c.toit, c.cToit, 0.78, 0.72, 12, [0, yGalerie + 1.55, 0])
    pSphere(c.toit, METAL, 0.12, [0, yGalerie + 1.95, 0], undefined, 6, 4)

    const lanterne: Lot = []
    pCyl(lanterne, 0xfff0c0, 0.46, 0.46, 0.8, 10, [0, yGalerie + 0.72, 0])
    pCyl(lanterne, laver(0xfff0c0, 0.3), 0.5, 0.5, 0.1, 10, [0, yGalerie + 1.14, 0])
    // La lanterne fond avec le toit ET s'allume la nuit: son propre materiau
    // permet aux deux mecanismes de vivre ensemble.
    const maille = poser(c.m, lanterne, { mat: { transparent: true }, ombre: false })
    if (maille) {
      c.m.toit.push(maille)
      c.m.fenetres.push(maille)
    }
    c.m.posLumiere.set(c.b.pos.x, c.b.y + yGalerie + 0.8, c.b.pos.z)
  },
}

/**
 * La tente: pas de murs droits, deux pans de toile et deux pignons.
 *
 * Elle passe par un constructeur a part, mais respecte les memes contrats:
 * porte sur la face -z, pans dans `toit` (donc effaces quand on entre),
 * pignons dans `murs` (donc effaces selon l'angle de la camera), mobilier aux
 * positions de la simulation.
 */
function construireTente(m: Maillage, b: Batiment, def: BatimentDef, style: StyleBatiment): void {
  const l = def.taille.l
  const p = def.taille.p
  const faite = style.hauteurMur
  const cToile = choisirCouleur('murs', b, def, style)
  const cPan = choisirCouleur('toit', b, def, style)
  const cPorte = choisirCouleur('porte', b, def, style)

  const sol: Lot = []
  const toit: Lot = []
  const avant: Lot = []
  const arriere: Lot = []

  pBoite(sol, 0x7a6a52, [l - 0.3, 0.08, p - 0.3], [0, 0.04, 0])
  ajouterMeubles(sol, b, l, p, style, 0.08)

  // Les deux pans, poses sur le faitage.
  const pente = Math.atan2(faite, l / 2)
  const longPan = Math.hypot(l / 2, faite) + 0.12
  for (const signe of [1, -1]) {
    pBoite(toit, signe > 0 ? cPan : teinter(cPan, 0.9), [longPan, 0.08, p + 0.12], [(signe * l) / 4, faite / 2, 0], [0, 0, -signe * pente])
  }
  pBoite(toit, BOIS_CLAIR, [0.09, 0.09, p + 0.5], [0, faite + 0.03, 0])
  // Haubans et piquets: c'est ce qui donne l'air "tendu" d'une tente.
  for (const sx of [1, -1]) {
    for (const sz of [1, -1]) {
      pTube(toit, 0xe8e0cc, 0.02, [0, faite, (sz * (p + 0.4)) / 2], [(sx * l) / 2.6, 0.05, (sz * (p + 1.1)) / 2])
      pBoite(toit, BOIS_FONCE, [0.05, 0.22, 0.05], [(sx * l) / 2.6, 0.1, (sz * (p + 1.1)) / 2], [sx * 0.2, 0, 0])
    }
  }

  // Pignon avant, perce de l'ouverture; pignon arriere, plein.
  const hOuverture = 1.6
  const forme = new THREE.Shape()
  forme.moveTo(-l / 2, 0)
  forme.lineTo(l / 2, 0)
  forme.lineTo(0, faite)
  forme.closePath()
  const trou = new THREE.Path()
  trou.moveTo(-L_PORTE / 2, 0)
  trou.lineTo(L_PORTE / 2, 0)
  trou.lineTo(L_PORTE / 2, hOuverture)
  trou.lineTo(-L_PORTE / 2, hOuverture)
  trou.closePath()
  forme.holes.push(trou)
  pGeo(avant, cToile, new THREE.ExtrudeGeometry(forme, { depth: 0.07, bevelEnabled: false }), [0, 0, -p / 2])
  // Le rabat roule au-dessus de l'entree, tenu par deux liens.
  pCyl(avant, cPorte, 0.12, 0.12, L_PORTE, 8, [0, hOuverture + 0.16, -p / 2 - 0.06], [0, 0, Math.PI / 2])
  for (const signe of [1, -1]) {
    pTube(avant, cPorte, 0.02, [signe * 0.4, hOuverture + 0.16, -p / 2 - 0.12], [signe * 0.4, hOuverture - 0.3, -p / 2 - 0.1])
  }

  const pignon = new THREE.Shape()
  pignon.moveTo(-l / 2, 0)
  pignon.lineTo(l / 2, 0)
  pignon.lineTo(0, faite)
  pignon.closePath()
  pGeo(arriere, teinter(cToile, 0.94), new THREE.ExtrudeGeometry(pignon, { depth: 0.07, bevelEnabled: false }), [0, 0, p / 2 - 0.07])

  const interieur = poser(m, sol)
  if (interieur) interieur.castShadow = false
  for (const [lot, normale] of [
    [avant, new THREE.Vector3(0, 0, -1)],
    [arriere, new THREE.Vector3(0, 0, 1)],
  ] as const) {
    const maille = poser(m, lot, { mat: { transparent: true } })
    if (maille) m.murs.push({ maillage: maille, normale })
  }
  const pans = poser(m, toit, { mat: { transparent: true } })
  if (pans) m.toit.push(pans)

  m.veutLumiere = true
  m.posLumiere.set(b.pos.x, b.y + 1.4, b.pos.z)
}

// ------------------------------------------------------------ amenagements

/**
 * Chantier d'un amenagement.
 *
 * `corps` est la maille opaque (presque tout tient dedans), `translucide`
 * l'eau et le verre, `lueur` ce qui brille jour et nuit (flammes, braises),
 * `nuit` ce qui ne s'allume qu'a la tombee du jour (globes, ampoules).
 */
interface Chantier {
  m: Maillage
  b: Batiment
  l: number
  p: number
  h: number
  style: StyleBatiment
  /** Couleur principale, accent, et menuiserie. */
  c1: number
  c2: number
  c3: number
  corps: Lot
  translucide: Lot
  lueur: Lot
  nuit: Lot
  /** Pieces de `corps` qui suivent la maturite du jardin (voir Pousse). */
  pousses: Pousse[]
}

type Constructeur = (c: Chantier) => void

/** Marque la derniere piece deposee dans `corps` comme une pousse. */
function pousser(c: Chantier, ancre: [number, number, number], seuil: number): void {
  c.pousses.push({ piece: c.corps.length - 1, ancre, seuil })
}

const MODELES: Partial<Record<BatimentKind, Constructeur>> = {
  /**
   * La cabane a collations de la plage: on ne rentre pas, on se sert au
   * comptoir. Le monde la plante lui-meme sur la plage ouest, et c'est une
   * destination: elle merite son auvent raye, son ecriteau et ses tabourets.
   */
  cabane_collations: (c) => {
    const h = c.h
    const zFond = c.p / 2 - 0.08
    pBoite(c.corps, teinter(c.c1, 0.72), [c.l, 0.1, c.p], [0, 0.05, 0])
    // Fond et joues seulement: la facade reste ouverte, on se sert au comptoir.
    pBoite(c.corps, c.c1, [c.l, h, 0.16], [0, h / 2, zFond])
    for (const sx of [1, -1]) {
      pBoite(c.corps, c.c1, [0.16, h, c.p - 0.16], [(sx * (c.l - 0.16)) / 2, h / 2, 0])
      pBoite(c.corps, teinter(c.c1, 0.85), [0.18, h, 0.18], [(sx * (c.l - 0.16)) / 2, h / 2, -c.p / 2 + 0.12])
    }
    // Etagere garnie de bocaux, derriere le comptoir.
    pBoite(c.corps, teinter(c.c1, 0.8), [c.l - 0.6, 0.08, 0.3], [0, 1.45, zFond - 0.25])
    for (let i = 0; i < 5; i++) {
      const x = -1.3 + i * 0.65
      pCyl(c.corps, i % 2 === 0 ? 0xefb45a : 0xd8748a, 0.1, 0.1, 0.24, 8, [x, 1.61, zFond - 0.25])
      pCyl(c.corps, teinter(METAL, 1.1), 0.11, 0.11, 0.04, 8, [x, 1.75, zFond - 0.25])
    }
    // Comptoir et son bandeau raye vertical.
    pBoite(c.corps, teinter(c.c1, 1.06), [c.l - 0.3, 0.14, 0.66], [0, 1.06, -0.95])
    pBoite(c.corps, c.c1, [c.l - 0.3, 0.98, 0.14], [0, 0.5, -1.24])
    for (let i = 0; i < 7; i++) {
      pBoite(c.corps, i % 2 === 0 ? c.c2 : BLANC, [0.26, 0.9, 0.05], [-1.56 + i * 0.52, 0.5, -1.32])
    }
    // LA piece maitresse: la toiture-auvent rayee, en pente vers le client.
    // Vue de 45 degres c'est tout ce qu'on voit d'abord, donc c'est elle qui
    // doit dire "guinguette de plage"; un toit plat par-dessus cachait tout.
    const zHaut = c.p / 2 + 0.2
    const zBas = -c.p / 2 - 0.55
    const profondeur = zHaut - zBas
    const angle = -Math.atan2(0.55, profondeur)
    const yMid = h + 0.12
    const largeurBande = (c.l + 0.5) / 9
    for (let i = 0; i < 9; i++) {
      pBoite(
        c.corps,
        i % 2 === 0 ? c.c2 : BLANC,
        [largeurBande - 0.015, 0.1, Math.hypot(profondeur, 0.55)],
        [-(c.l + 0.5) / 2 + largeurBande / 2 + i * largeurBande, yMid, (zHaut + zBas) / 2],
        [angle, 0, 0],
      )
      // Feston au bord bas: le petit arrondi des toiles de plage.
      pCyl(
        c.corps,
        i % 2 === 0 ? c.c2 : BLANC,
        0.12,
        0.12,
        largeurBande - 0.015,
        6,
        [-(c.l + 0.5) / 2 + largeurBande / 2 + i * largeurBande, yMid - 0.34, zBas],
        [0, 0, Math.PI / 2],
      )
    }
    // Ecriteau sur le faitage, avec un beigne et une creme glacee DEBOUT
    // dessus: poses a plat contre la planche, on ne les voyait pas d'en haut.
    const ySigne = yMid + 0.42
    pBoite(c.corps, BLANC, [2.3, 0.5, 0.1], [0, ySigne, zHaut - 0.35])
    pBoite(c.corps, c.c2, [2.42, 0.09, 0.14], [0, ySigne + 0.28, zHaut - 0.35])
    pBoite(c.corps, c.c2, [2.42, 0.09, 0.14], [0, ySigne - 0.28, zHaut - 0.35])
    for (const sx of [1, -1]) {
      pBoite(c.corps, teinter(c.c1, 0.8), [0.09, 0.5, 0.09], [sx * 1.2, ySigne, zHaut - 0.35])
    }
    pGeo(c.corps, 0xf0a8c0, new THREE.TorusGeometry(0.2, 0.075, 6, 10), [-0.72, ySigne + 0.62, zHaut - 0.35], [Math.PI / 2, 0, 0])
    pCone(c.corps, 0xd8a05a, 0.17, 0.4, 8, [0.72, ySigne + 0.5, zHaut - 0.35], [Math.PI, 0, 0])
    pSphere(c.corps, 0xf6e8d0, 0.17, [0.72, ySigne + 0.76, zHaut - 0.35], undefined, 8, 6)
    pSphere(c.corps, 0xef8fa8, 0.13, [0.72, ySigne + 0.94, zHaut - 0.35], undefined, 6, 4)
    // Tabourets, glaciere, ardoise des saveurs.
    for (const sx of [1, -1]) {
      const x = sx * 1.05
      pCyl(c.corps, c.c3, 0.24, 0.24, 0.09, 10, [x, 0.72, -1.72])
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2
        pTube(c.corps, METAL, 0.03, [x + Math.cos(a) * 0.06, 0.68, -1.72 + Math.sin(a) * 0.06], [
          x + Math.cos(a) * 0.19,
          0.02,
          -1.72 + Math.sin(a) * 0.19,
        ])
      }
    }
    pBoite(c.corps, c.c3, [0.72, 0.46, 0.5], [-1.3, 0.33, 0.5])
    pBoite(c.corps, BLANC, [0.78, 0.09, 0.56], [-1.3, 0.6, 0.5])
    pBoite(c.corps, teinter(METAL, 0.8), [0.24, 0.05, 0.05], [-1.3, 0.69, 0.5])
    pBoite(c.corps, NOIR, [0.06, 0.85, 0.62], [1.72, 0.55, -0.5], [0, 0, 0.12])
    for (let i = 0; i < 3; i++) {
      pBoite(c.corps, BLANC, [0.03, 0.05, 0.4 - i * 0.08], [1.68, 0.78 - i * 0.19, -0.5], [0, 0, 0.12])
    }
    // Ce qui s'allume le soir: la vitrine et cinq ampoules sous l'auvent.
    pBoite(c.nuit, 0xffe6b0, [c.l - 0.5, 0.52, 0.06], [0, 1.72, -0.95])
    for (let i = 0; i < 5; i++) {
      pSphere(c.nuit, 0xffd98a, 0.085, [-1.4 + i * 0.7, yMid - 0.5, zBas + 0.06], [1, 1.2, 1], 6, 4)
    }
  },

  // ------------------------------------------------------- eau et jardin
  piscine: (c) => {
    // `murs` est la couleur de l'EAU et `toit` celle de la margelle: c'est ce
    // que disent les couleurs par defaut de la simulation (bleu, puis blanc).
    pBoite(c.corps, c.c2, [c.l, 0.3, c.p], [0, 0.15, 0])
    pBoite(c.translucide, c.c1, [c.l - 0.7, 0.26, c.p - 0.7], [0, 0.19, 0])
    for (const signe of [1, -1]) {
      pBoite(c.corps, 0xdadfe3, [0.06, 0.75, 0.06], [c.l / 2 - 0.5, 0.5, (signe * (c.p - 1)) / 2])
    }
    pBoite(c.corps, 0xdadfe3, [0.06, 0.06, c.p - 1], [c.l / 2 - 0.5, 0.82, 0])
    pCyl(c.corps, laver(c.c2, 0.2), 0.28, 0.3, 0.1, 10, [-c.l / 2 + 0.55, 0.36, -c.p / 2 + 0.55])
  },

  /**
   * Le jardin, dans ses deux etats.
   *
   * Ce qui suit la maturite (voir Pousse) est tout ce qui fait sa MASSE verte
   * et ses points de couleur: de loin, un jardin mur est une touffe verte
   * mouchetee de fruits, un jardin cueilli un rectangle de terre brune ou ne
   * restent que les chicots. Bordure, terre et rangs, eux, ne bougent jamais:
   * c'est le fond qui apparait quand la recolte est faite.
   */
  jardin: (c) => {
    pBoite(c.corps, c.c1, [c.l, c.h, c.p], [0, c.h / 2, 0])
    const sol = c.h + 0.02
    pBoite(c.corps, TERRE, [c.l - 0.25, 0.08, c.p - 0.25], [0, sol, 0])

    const teintes = [0xef5f5f, 0xffb43f, 0xa78bfa, 0xfff0d0, 0xff8fc4]
    const rangs = 3
    const parRang = 3
    const pasX = (c.l - 0.5) / parRang
    const pasZ = (c.p - 0.5) / rangs

    for (let r = 0; r < rangs; r++) {
      const z = (r - (rangs - 1) / 2) * pasZ
      // Butte de terre retournee: elle porte le rang et le montre encore nu.
      pBoite(c.corps, teinter(TERRE, 1.25), [c.l - 0.5, 0.1, 0.44], [0, sol + 0.05, z])

      for (let i = 0; i < parRang; i++) {
        const x = (i - (parRang - 1) / 2) * pasX
        const pied: [number, number, number] = [x, sol, z]

        // Le chicot reste apres la cueillette: sans lui, le carre de terre ne
        // ressemblait plus a un jardin mais a un chantier.
        pBoite(c.corps, teinter(FEUILLE_FONCE, 0.9), [0.18, 0.08, 0.18], [x, sol + 0.08, z])

        pBoite(c.corps, teinter(c.c2, 0.7), [0.07, 0.36, 0.07], [x, sol + 0.2, z])
        pousser(c, pied, 0)
        pSphere(c.corps, c.c2, 0.34, [x, sol + 0.46, z], [1.15, 0.9, 1.15], 6, 4)
        pousser(c, pied, 0.04)
        for (const signe of [1, -1]) {
          pBoite(c.corps, teinter(c.c2, 0.78), [0.44, 0.05, 0.16], [x, sol + 0.2, z], [0, signe * 0.7 + r, signe * 0.18])
          pousser(c, pied, 0.16)
        }

        // Les fruits n'arrivent qu'au bout de la repousse: ce sont eux qui
        // annoncent « c'est pret », et ils sortent du feuillage pour se voir.
        const couleur = teintes[(i + r) % teintes.length] as number
        for (const signe of [1, -1]) {
          pSphere(c.corps, couleur, 0.15, [x + signe * 0.3, sol + 0.52, z - signe * 0.12], undefined, 5, 3)
          // Ancres au pied comme le reste: un jardin cueilli doit etre plat,
          // sans fruits reduits a un point qui flottent au-dessus de la terre.
          pousser(c, pied, 0.62)
        }
      }
    }
  },

  potager: (c) => {
    // Bordure, terre, trois buttes et des legumes en rangs.
    for (const [dx, dz, lx, lz] of [
      [0, -c.p / 2 + 0.08, c.l, 0.16],
      [0, c.p / 2 - 0.08, c.l, 0.16],
      [-c.l / 2 + 0.08, 0, 0.16, c.p],
      [c.l / 2 - 0.08, 0, 0.16, c.p],
    ] as const) {
      pBoite(c.corps, c.c1, [lx, 0.3, lz], [dx, 0.15, dz])
    }
    pBoite(c.corps, TERRE, [c.l - 0.3, 0.24, c.p - 0.3], [0, 0.12, 0])
    for (let r = 0; r < 3; r++) {
      const z = -c.p / 3 + (r * c.p) / 3
      pBoite(c.corps, teinter(TERRE, 1.2), [c.l - 0.5, 0.1, 0.34], [0, 0.28, z])
      for (let i = 0; i < 4; i++) {
        const x = -c.l / 2 + 0.6 + (i * (c.l - 1.2)) / 3
        // Les rangs suivent la maturite, comme ceux du jardin. Sans ces
        // ancrages, un potager cueilli et un potager mur se ressemblaient trait
        // pour trait: on ne pouvait pas savoir s'il y avait quelque chose a
        // prendre autrement qu'en allant taper dessus.
        const pied: [number, number, number] = [x, 0.3, z]
        pBoite(c.corps, FEUILLE_FONCE, [0.3, 0.03, 0.06], [x, 0.36, z], [0, r + i, 0])
        pousser(c, pied, 0.08)
        pSphere(c.corps, r === 1 ? teinter(c.c2, 0.85) : c.c2, 0.16, [x, 0.36, z], [1, 0.7, 1], 6, 4)
        pousser(c, pied, 0.5)
      }
    }
    // Deux tuteurs et leur ficelle.
    for (const signe of [1, -1]) {
      pBoite(c.corps, BOIS_CLAIR, [0.05, 1.0, 0.05], [(signe * (c.l - 0.9)) / 2, 0.5, -c.p / 2 + 0.35])
    }
    pTube(c.corps, 0xe8e0cc, 0.015, [-(c.l - 0.9) / 2, 0.92, -c.p / 2 + 0.35], [(c.l - 0.9) / 2, 0.92, -c.p / 2 + 0.35])
  },

  serre: (c) => {
    const h = c.h
    const cadre = teinter(c.c1, 0.72)
    // Ossature: sabliere, poteaux, faitage.
    pBoite(c.corps, cadre, [c.l, 0.18, c.p], [0, 0.09, 0])
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pBoite(c.corps, cadre, [0.12, h, 0.12], [(sx * (c.l - 0.12)) / 2, h / 2, (sz * (c.p - 0.12)) / 2])
      }
    }
    pBoite(c.corps, cadre, [c.l, 0.12, 0.12], [0, h, -c.p / 2 + 0.06])
    pBoite(c.corps, cadre, [c.l, 0.12, 0.12], [0, h, c.p / 2 - 0.06])
    pBoite(c.corps, cadre, [0.12, 0.12, c.p], [0, h + 0.55, 0])
    for (const sz of [1, -1]) {
      pTube(c.corps, cadre, 0.05, [-c.l / 2, h, (sz * c.p) / 2], [0, h + 0.55, (sz * c.p) / 2])
      pTube(c.corps, cadre, 0.05, [c.l / 2, h, (sz * c.p) / 2], [0, h + 0.55, (sz * c.p) / 2])
    }
    // Deux bacs de culture a l'interieur.
    for (const sz of [1, -1]) {
      pBoite(c.corps, BOIS, [c.l - 0.6, 0.4, 0.6], [0, 0.28, (sz * (c.p - 1)) / 2])
      pBoite(c.corps, TERRE, [c.l - 0.75, 0.08, 0.48], [0, 0.5, (sz * (c.p - 1)) / 2])
      for (let i = 0; i < 4; i++) {
        const x = -c.l / 2 + 0.6 + (i * (c.l - 1.2)) / 3
        // Le pied est la terre du bac, pas le sol: une pousse de serre se
        // retracte dans son bac, pas au travers.
        const pied: [number, number, number] = [x, 0.54, (sz * (c.p - 1)) / 2]
        pBoite(c.corps, c.c2, [0.05, 0.4, 0.05], [x, 0.72, (sz * (c.p - 1)) / 2])
        pousser(c, pied, 0.05)
        pSphere(c.corps, c.c2, 0.15, [x, 0.98, (sz * (c.p - 1)) / 2], [1, 0.8, 1], 6, 4)
        pousser(c, pied, 0.5)
      }
    }
    // Verre: parois et deux pans de toiture.
    const verre = laver(0xbfe4f0, 0.15)
    for (const sz of [1, -1]) {
      pBoite(c.translucide, verre, [c.l - 0.2, h - 0.2, 0.05], [0, h / 2, (sz * c.p) / 2])
    }
    for (const sx of [1, -1]) {
      pBoite(c.translucide, verre, [0.05, h - 0.2, c.p - 0.2], [(sx * c.l) / 2, h / 2, 0])
    }
    const penteToit = Math.atan2(0.55, c.l / 2)
    for (const sx of [1, -1]) {
      pBoite(
        c.translucide,
        verre,
        [Math.hypot(c.l / 2, 0.55), 0.05, c.p],
        [(sx * c.l) / 4, h + 0.28, 0],
        [0, 0, -sx * penteToit],
      )
    }
  },

  fontaine: (c) => {
    const r = Math.min(c.l, c.p) / 2 - 0.1
    pCyl(c.corps, c.c1, r, r + 0.06, 0.55, 8, [0, 0.275, 0])
    // Margelle en huit dalles et non en disque plein: un disque recouvrait le
    // bassin, et vue de 45 degres la fontaine n'etait qu'un octogone gris.
    for (let i = 0; i < 8; i++) {
      const a2 = (i / 8) * Math.PI * 2 + Math.PI / 8
      // Dalles TANGENTES au bassin: posees en rayons, elles dessinaient un
      // engrenage au lieu d'une margelle.
      pBoite(
        c.corps,
        i % 2 === 0 ? laver(c.c1, 0.18) : laver(c.c1, 0.1),
        [2 * r * Math.sin(Math.PI / 8) + 0.04, 0.14, 0.34],
        [Math.cos(a2) * (r - 0.1), 0.6, Math.sin(a2) * (r - 0.1)],
        [0, -a2 + Math.PI / 2, 0],
      )
    }
    // Pilier, vasque et jet: c'est la verticale qui fait lire une fontaine.
    pCyl(c.corps, teinter(c.c1, 0.9), 0.22, 0.34, 1.15, 8, [0, 0.62, 0])
    pCyl(c.corps, laver(c.c1, 0.1), 0.68, 0.26, 0.18, 8, [0, 1.28, 0])
    pCyl(c.corps, teinter(c.c1, 0.95), 0.12, 0.14, 0.42, 8, [0, 1.55, 0])
    pSphere(c.corps, laver(c.c1, 0.25), 0.14, [0, 1.82, 0], undefined, 8, 6)
    // Eau: la nappe du bassin, celle de la vasque, le jet et trois gouttes.
    // La nappe se pose SUR la face haute du bassin (0.55): plus bas, c'est le
    // bassin lui-meme qui la cachait et le bassin paraissait vide.
    pCyl(c.translucide, c.c2, r - 0.2, r - 0.2, 0.07, 8, [0, 0.57, 0])
    pCyl(c.translucide, laver(c.c2, 0.25), 0.6, 0.55, 0.06, 8, [0, 1.38, 0])
    pCyl(c.translucide, laver(c.c2, 0.4), 0.06, 0.1, 0.5, 6, [0, 2.05, 0])
    for (const [dx, dz] of [
      [0.5, 0.12],
      [-0.36, -0.42],
      [0.06, 0.54],
    ] as const) {
      pSphere(c.translucide, laver(c.c2, 0.4), 0.08, [dx, 1.6, dz], undefined, 6, 4)
    }
  },

  puits: (c) => {
    pCyl(c.corps, c.c1, 0.78, 0.86, 0.85, 10, [0, 0.42, 0])
    pCyl(c.corps, laver(c.c1, 0.2), 0.9, 0.9, 0.12, 10, [0, 0.9, 0])
    pCyl(c.translucide, teinter(EAU, 0.7), 0.68, 0.68, 0.05, 10, [0, 0.55, 0])
    for (const sx of [1, -1]) {
      pBoite(c.corps, c.c2, [0.13, 1.5, 0.13], [sx * 0.66, 1.6, 0])
    }
    // Petit toit a deux pans, treuil et seau.
    const pente = Math.atan2(0.45, 0.95)
    for (const sx of [1, -1]) {
      pBoite(c.corps, teinter(c.c2, 0.85), [1.05, 0.08, 2.05], [sx * 0.5, 2.5, 0], [0, 0, -sx * pente])
    }
    pCyl(c.corps, BOIS_CLAIR, 0.09, 0.09, 1.2, 8, [0, 2.15, 0], [0, 0, Math.PI / 2])
    pBoite(c.corps, BOIS_FONCE, [0.05, 0.3, 0.05], [0.68, 2.0, 0])
    pBoite(c.corps, BOIS_FONCE, [0.22, 0.05, 0.05], [0.78, 1.86, 0])
    pTube(c.corps, 0x6b5136, 0.02, [0, 2.1, 0], [0, 1.25, 0])
    pCyl(c.corps, METAL, 0.17, 0.14, 0.24, 8, [0, 1.12, 0])
  },

  ruche: (c) => {
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pBoite(c.corps, c.c2, [0.1, 0.16, 0.1], [sx * 0.38, 0.08, sz * 0.38])
      }
    }
    pBoite(c.corps, teinter(c.c1, 0.9), [1.02, 0.06, 1.02], [0, 0.19, 0])
    for (let i = 0; i < 3; i++) {
      const t = 0.92 - i * 0.06
      pBoite(c.corps, i % 2 === 0 ? c.c1 : teinter(c.c1, 0.92), [t, 0.24, t], [0, 0.34 + i * 0.26, 0])
    }
    pBoite(c.corps, NOIR, [0.34, 0.06, 0.04], [0, 0.3, -0.44])
    pBoite(c.corps, teinter(c.c1, 0.8), [0.5, 0.05, 0.16], [0, 0.26, -0.5])
    pBoite(c.corps, c.c2, [1.06, 0.08, 1.06], [0, 1.14, 0])
    pCone(c.corps, teinter(c.c2, 0.9), 0.72, 0.24, 4, [0, 1.28, 0], [0, Math.PI / 4, 0])
    // Deux abeilles, pour qu'on voie que la ruche est habitee.
    for (const [dx, dy, dz] of [
      [0.5, 0.75, -0.55],
      [-0.42, 0.95, -0.4],
    ] as const) {
      pSphere(c.corps, 0xf0c020, 0.07, [dx, dy, dz], [1.4, 1, 1], 6, 4)
      pSphere(c.corps, 0xf6f2e8, 0.05, [dx, dy + 0.05, dz], [1.6, 0.3, 1], 6, 4)
    }
  },

  // --------------------------------------------- cloisonnement, circulation
  cloture: (c) => {
    for (const y of [c.h * 0.75, c.h * 0.42]) {
      pBoite(c.corps, c.c1, [c.l, 0.1, 0.06], [0, y, 0])
    }
    for (const sx of [-1, 0, 1]) {
      pBoite(c.corps, teinter(c.c1, 0.92), [0.12, c.h, 0.12], [(sx * c.l) / 2.2, c.h / 2, 0])
      pCone(c.corps, teinter(c.c1, 0.92), 0.11, 0.16, 4, [(sx * c.l) / 2.2, c.h + 0.08, 0], [0, Math.PI / 4, 0])
    }
  },

  haie: (c) => {
    // Un buisson taille: une boite bosselee, quelques touffes, un pied sombre.
    const geo = bosseler(new THREE.BoxGeometry(c.l, c.h, c.p, 3, 2, 2), 0.16, 7)
    pGeo(c.corps, c.c1, geo, [0, c.h / 2 + 0.06, 0])
    for (let i = 0; i < 3; i++) {
      const x = -c.l / 3 + (i * c.l) / 3
      pSphere(c.corps, i === 1 ? c.c1 : teinter(c.c1, 1.12), c.p * 0.42, [x, c.h + 0.04, 0], [1, 0.5, 1], 6, 4)
    }
    pBoite(c.corps, teinter(c.c1, 0.55), [c.l - 0.2, 0.16, c.p - 0.16], [0, 0.08, 0])
  },

  muret: (c) => {
    // Deux assises de moellons decales, puis un chaperon.
    for (let rangee = 0; rangee < 2; rangee++) {
      const n = 3 + rangee
      for (let i = 0; i < n; i++) {
        const x = -c.l / 2 + (c.l * (i + 0.5)) / n
        const teinte = (i + rangee) % 2 === 0 ? c.c1 : teinter(c.c1, 0.86)
        pBoite(
          c.corps,
          teinte,
          [c.l / n - 0.04, 0.32, c.p - 0.06],
          [x, 0.16 + rangee * 0.32, 0],
          [0, (i - rangee) * 0.03, 0],
        )
      }
    }
    pBoite(c.corps, laver(c.c1, 0.2), [c.l, 0.1, c.p], [0, 0.69, 0])
  },

  portail: (c) => {
    const h = c.h
    for (const sx of [1, -1]) {
      pBoite(c.corps, c.c1, [0.18, h, 0.18], [(sx * (c.l - 0.18)) / 2, h / 2, 0])
      pCone(c.corps, c.c1, 0.16, 0.2, 4, [(sx * (c.l - 0.18)) / 2, h + 0.1, 0], [0, Math.PI / 4, 0])
    }
    // Deux battants sur leurs gonds, l'un ouvert: on lit tout de suite que ca
    // s'ouvre. Chaque battant est dessine a plat puis pivote d'un bloc.
    const largeur = (c.l - 0.36) / 2
    for (const [sx, angle] of [
      [-1, -0.55],
      [1, 0.12],
    ] as const) {
      const debut = c.corps.length
      pBoite(c.corps, c.c3, [largeur, 0.12, 0.08], [(sx * largeur) / 2, h * 0.78, 0])
      pBoite(c.corps, c.c3, [largeur, 0.12, 0.08], [(sx * largeur) / 2, h * 0.34, 0])
      pBoite(c.corps, c.c3, [0.1, h * 0.82, 0.08], [sx * 0.05, h * 0.45, 0])
      pBoite(c.corps, c.c3, [0.1, h * 0.82, 0.08], [sx * (largeur - 0.05), h * 0.45, 0])
      pBoite(
        c.corps,
        teinter(c.c3, 1.1),
        [Math.hypot(largeur, h * 0.44), 0.09, 0.06],
        [(sx * largeur) / 2, h * 0.56, 0],
        [0, 0, sx * Math.atan2(h * 0.44, largeur)],
      )
      grouper(c.corps, debut, tr([(sx * (c.l - 0.36)) / 2, 0, 0], [0, angle * sx, 0]))
    }
  },

  chemin_pierre: (c) => {
    // Le plus economique du catalogue: cinq dalles, deux triangles chacune.
    // Les dalles suivent l'axe z avec un peu de jeu: deux tuiles a la suite
    // forment un sentier, la ou une rosace ne formait qu'une croix.
    const dalles: [number, number, number, number][] = [
      [0.06, -0.62, 0.62, 0.12],
      [-0.1, -0.2, 0.7, -0.3],
      [0.12, 0.24, 0.66, 0.35],
      [-0.06, 0.66, 0.58, -0.15],
      [0.52, 0.02, 0.34, 0.7],
    ]
    for (const [dx, dz, taille, rot] of dalles) {
      pPlaque(c.corps, c.c1, taille * c.l * 0.62, taille * c.p * 0.62, [dx * c.l * 0.5, 0.05, dz * c.p * 0.5], rot)
    }
  },

  route: (c) => {
    // La chaussee depasse de la marge reglementaire entre batiments (0.5 m),
    // pour que deux tuiles posees a la suite forment une route continue.
    pBoite(c.corps, c.c1, [c.l + 0.55, 0.12, c.p + 0.55], [0, 0.06, 0])
    for (const dz of [-c.p / 3, 0, c.p / 3]) {
      pPlaque(c.corps, laver(c.c1, 0.75), 0.16, c.p / 5, [0, 0.13, dz])
    }
    for (const sx of [1, -1]) {
      pBoite(c.corps, teinter(c.c1, 0.8), [0.1, 0.16, c.p + 0.55], [(sx * (c.l + 0.35)) / 2, 0.08, 0])
    }
  },

  pont_bois: (c) => {
    const n = 9
    const arc = 0.32
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1)
      const z = -c.p / 2 + t * c.p
      const y = 0.18 + Math.sin(Math.PI * t) * arc
      pBoite(c.corps, i % 2 === 0 ? c.c1 : teinter(c.c1, 0.92), [c.l - 0.2, 0.12, c.p / n - 0.04], [0, y, z])
    }
    // Longrines, poteaux et lisses de chaque cote.
    for (const sx of [1, -1]) {
      const x = (sx * (c.l - 0.3)) / 2
      pBoite(c.corps, teinter(c.c1, 0.8), [0.14, 0.16, c.p], [x, 0.12, 0])
      for (let i = 0; i < 4; i++) {
        const t = 0.1 + (i * 0.8) / 3
        const z = -c.p / 2 + t * c.p
        const y = 0.18 + Math.sin(Math.PI * t) * arc
        pBoite(c.corps, teinter(c.c1, 0.86), [0.11, 0.9, 0.11], [x, y + 0.45, z])
      }
      pTube(c.corps, c.c1, 0.06, [x, 0.72, -c.p / 2], [x, 0.62 + arc + 0.3, 0])
      pTube(c.corps, c.c1, 0.06, [x, 0.62 + arc + 0.3, 0], [x, 0.72, c.p / 2])
    }
  },

  quai: (c) => {
    // Modele plat et bon marche: un tablier, quatre pieux, deux bittes.
    pBoite(c.corps, c.c1, [c.l + 0.55, 0.16, c.p + 0.55], [0, 0.12, 0])
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pBoite(c.corps, teinter(c.c1, 0.75), [0.18, 0.9, 0.18], [(sx * (c.l - 0.2)) / 2, -0.3, (sz * (c.p - 0.3)) / 2])
      }
    }
    for (const sx of [1, -1]) {
      pCyl(c.corps, teinter(c.c1, 0.72), 0.11, 0.13, 0.44, 8, [(sx * (c.l - 0.5)) / 2, 0.4, -c.p / 2 + 0.4])
    }
  },

  escalier: (c) => {
    const n = 5
    const marche = c.h / n
    const profondeur = c.p / n
    for (let i = 0; i < n; i++) {
      const haut = (i + 1) * marche
      pBoite(
        c.corps,
        i % 2 === 0 ? c.c1 : teinter(c.c1, 0.93),
        [c.l - 0.2, haut, profondeur],
        [0, haut / 2, -c.p / 2 + profondeur / 2 + i * profondeur],
      )
    }
    const pente = Math.atan2(c.h, c.p)
    for (const sx of [1, -1]) {
      pBoite(
        c.corps,
        teinter(c.c1, 0.82),
        [0.16, 0.22, Math.hypot(c.p, c.h)],
        [(sx * (c.l - 0.1)) / 2, c.h / 2 + 0.12, 0],
        [-pente, 0, 0],
      )
    }
  },

  // ------------------------------------------------------------- mobilier
  banc: (c) => {
    const yAssise = 0.46
    pBoite(c.corps, c.c1, [c.l - 0.1, 0.09, 0.42], [0, yAssise, -0.04])
    pBoite(c.corps, teinter(c.c1, 0.94), [c.l - 0.1, 0.13, 0.11], [0, yAssise + 0.28, 0.24], [-0.16, 0, 0])
    pBoite(c.corps, teinter(c.c1, 0.94), [c.l - 0.1, 0.13, 0.11], [0, yAssise + 0.5, 0.29], [-0.16, 0, 0])
    for (const sx of [1, -1]) {
      const x = (sx * (c.l - 0.4)) / 2
      pBoite(c.corps, teinter(c.c1, 0.85), [0.09, yAssise, 0.09], [x, yAssise / 2, -0.16])
      pBoite(c.corps, teinter(c.c1, 0.85), [0.09, yAssise + 0.6, 0.09], [x, (yAssise + 0.6) / 2, 0.18])
      pBoite(c.corps, teinter(c.c1, 0.85), [0.08, 0.08, 0.42], [x, yAssise + 0.32, 0.02])
    }
    pBoite(c.corps, teinter(c.c1, 0.8), [c.l - 0.5, 0.07, 0.07], [0, 0.1, -0.16])
  },

  table_picnic: (c) => {
    const yPlateau = 0.75
    for (let i = 0; i < 4; i++) {
      pBoite(c.corps, i % 2 === 0 ? c.c1 : teinter(c.c1, 0.95), [c.l - 0.2, 0.08, 0.24], [0, yPlateau, -0.42 + i * 0.28])
    }
    for (const sz of [1, -1]) {
      pBoite(c.corps, teinter(c.c1, 0.92), [c.l - 0.4, 0.08, 0.34], [0, 0.45, (sz * (c.p - 0.5)) / 2])
    }
    // Pietement en X, une paire a chaque bout.
    for (const sx of [1, -1]) {
      const x = (sx * (c.l - 0.7)) / 2
      for (const sens of [1, -1]) {
        pBoite(
          c.corps,
          teinter(c.c1, 0.85),
          [0.09, 1.05, 0.09],
          [x, 0.42, 0],
          [sens * 0.62, 0, 0],
        )
      }
      pBoite(c.corps, teinter(c.c1, 0.85), [0.08, 0.08, c.p - 0.4], [x, 0.5, 0])
    }
  },

  hamac: (c) => {
    // Deux chevalets, une toile en chainette, deux cordes.
    for (const sx of [1, -1]) {
      const x = (sx * (c.l - 0.3)) / 2
      for (const sz of [1, -1]) {
        pBoite(c.corps, c.c1, [0.13, 1.75, 0.13], [x, 0.85, (sz * (c.p - 0.4)) / 2], [sz * 0.18, 0, 0])
      }
      pBoite(c.corps, teinter(c.c1, 0.85), [0.11, 0.11, c.p - 0.2], [x, 0.3, 0])
    }
    const n = 7
    const yBord = 1.05
    const creux = 0.45
    for (let i = 0; i < n; i++) {
      const t = -1 + (2 * i) / (n - 1)
      const x = (t * (c.l - 1.1)) / 2
      const y = yBord - creux * (1 - t * t)
      const pente = ((2 * creux * t) / ((c.l - 1.1) / 2)) * 0.5
      pBoite(c.corps, i % 2 === 0 ? c.c2 : laver(c.c2, 0.22), [(c.l - 1.0) / n, 0.06, c.p * 0.5], [x, y, 0], [0, 0, pente])
    }
    for (const sx of [1, -1]) {
      pTube(c.corps, 0xe8e0cc, 0.02, [(sx * (c.l - 0.3)) / 2, 1.55, 0], [(sx * (c.l - 1.0)) / 2, yBord, 0])
    }
    pBoite(c.corps, BLANC, [0.34, 0.12, 0.3], [(c.l - 1.6) / 2, yBord - 0.05, 0])
  },

  parasol: (c) => {
    pCyl(c.corps, teinter(c.c1, 0.8), 0.34, 0.4, 0.16, 10, [0, 0.08, 0])
    pCyl(c.corps, c.c1, 0.05, 0.06, c.h, 8, [0, c.h / 2, 0])
    // Toile en huit quartiers alternes: c'est ce qui fait un parasol vu d'en
    // haut, et ca ne coute que trois triangles par quartier.
    const r = Math.min(c.l, c.p) / 2
    for (let i = 0; i < 8; i++) {
      pCone(
        c.corps,
        i % 2 === 0 ? c.c2 : BLANC,
        r,
        0.5,
        3,
        [0, c.h - 0.18, 0],
        undefined,
        [(i * Math.PI) / 4, Math.PI / 4],
      )
    }
    pSphere(c.corps, laver(c.c2, 0.3), 0.09, [0, c.h + 0.14, 0], undefined, 6, 4)
  },

  chaise_longue: (c) => {
    const yAssise = 0.34
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pBoite(c.corps, c.c1, [0.08, yAssise, 0.08], [(sx * (c.l - 0.2)) / 2, yAssise / 2, (sz * (c.p - 0.5)) / 2])
      }
      pBoite(c.corps, c.c1, [0.09, 0.09, c.p - 0.3], [(sx * (c.l - 0.2)) / 2, yAssise, 0.1])
    }
    pBoite(c.corps, c.c2, [c.l - 0.24, 0.08, c.p * 0.62], [0, yAssise + 0.06, c.p * 0.16])
    // Dossier redresse.
    pBoite(c.corps, c.c2, [c.l - 0.24, 0.08, c.p * 0.44], [0, yAssise + 0.36, -c.p * 0.3], [0.85, 0, 0])
    for (const sx of [1, -1]) {
      pBoite(c.corps, c.c1, [0.07, 0.07, c.p * 0.46], [(sx * (c.l - 0.22)) / 2, yAssise + 0.36, -c.p * 0.3], [0.85, 0, 0])
    }
    pBoite(c.corps, BLANC, [c.l - 0.34, 0.07, 0.4], [0, yAssise + 0.14, c.p * 0.1])
  },

  balancoire: (c) => {
    const h = c.h
    // Deux chevalets et une poutre.
    for (const sx of [1, -1]) {
      const x = (sx * (c.l - 0.2)) / 2
      for (const sz of [1, -1]) {
        pBoite(c.corps, c.c1, [0.14, h + 0.2, 0.14], [x, h / 2, (sz * (c.p - 0.2)) / 2], [sz * 0.24, 0, 0])
      }
    }
    pBoite(c.corps, teinter(c.c1, 0.9), [c.l, 0.16, 0.16], [0, h, 0])
    // La balancelle est une maille a part: elle se balance autour de la poutre.
    const nacelle: Lot = []
    for (const sx of [1, -1]) {
      pTube(nacelle, 0x8a7250, 0.025, [sx * 0.34, 0, 0], [sx * 0.34, -h * 0.62, 0])
    }
    pBoite(nacelle, c.c2, [0.86, 0.07, 0.32], [0, -h * 0.62, 0])
    pBoite(nacelle, teinter(c.c2, 0.9), [0.86, 0.2, 0.06], [0, -h * 0.62 + 0.13, 0.14])
    const maille = poser(c.m, nacelle, { recoit: false })
    if (maille) {
      maille.position.y = h
      c.m.animes.push({ objet: maille, genre: 'balancement', vitesse: 1.7, phase: phaseDe(c.b.id) })
    }
  },

  toboggan: (c) => {
    const hPlate = c.h - 0.3
    const zHaut = c.p / 2 - 0.55
    // Plateforme et ses quatre pieds.
    pBoite(c.corps, c.c1, [c.l - 0.3, 0.12, 0.8], [0, hPlate, zHaut])
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pBoite(c.corps, c.c1, [0.12, hPlate, 0.12], [(sx * (c.l - 0.4)) / 2, hPlate / 2, zHaut + sz * 0.32])
      }
      pBoite(c.corps, c.c1, [0.1, 0.62, 0.1], [(sx * (c.l - 0.4)) / 2, hPlate + 0.4, zHaut])
    }
    pBoite(c.corps, c.c1, [c.l - 0.3, 0.09, 0.09], [0, hPlate + 0.7, zHaut + 0.3])
    pEchelle(c.corps, teinter(c.c1, 0.9), 0.6, hPlate, tr([0, 0, c.p / 2 - 0.02], [0.12, 0, 0]))
    // La glisse: une planche inclinee et ses deux bords.
    const zBas = -c.p / 2 + 0.25
    const dz = zHaut - 0.35 - zBas
    const dy = hPlate - 0.12
    const angle = -Math.atan2(dy, dz)
    const longueur = Math.hypot(dy, dz)
    const centre: [number, number, number] = [0, 0.12 + dy / 2, zBas + dz / 2]
    pBoite(c.corps, c.c2, [c.l - 0.44, 0.1, longueur], centre, [angle, 0, 0])
    for (const sx of [1, -1]) {
      pBoite(c.corps, teinter(c.c2, 0.85), [0.1, 0.24, longueur], [(sx * (c.l - 0.38)) / 2, centre[1] + 0.1, centre[2]], [angle, 0, 0])
    }
    pBoite(c.corps, c.c2, [c.l - 0.44, 0.1, 0.5], [0, 0.1, zBas - 0.2], [-0.12, 0, 0])
  },

  bac_sable: (c) => {
    for (const [dx, dz, lx, lz] of [
      [0, -c.p / 2 + 0.12, c.l, 0.24],
      [0, c.p / 2 - 0.12, c.l, 0.24],
      [-c.l / 2 + 0.12, 0, 0.24, c.p],
      [c.l / 2 - 0.12, 0, 0.24, c.p],
    ] as const) {
      pBoite(c.corps, c.c1, [lx, 0.34, lz], [dx, 0.17, dz])
    }
    pBoite(c.corps, c.c2, [c.l - 0.4, 0.26, c.p - 0.4], [0, 0.15, 0])
    // Coins assis, un tas, un seau et une pelle.
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pBoite(c.corps, teinter(c.c1, 1.06), [0.55, 0.1, 0.55], [(sx * (c.l - 0.6)) / 2, 0.37, (sz * (c.p - 0.6)) / 2], [0, Math.PI / 4, 0])
      }
    }
    pCone(c.corps, teinter(c.c2, 0.94), 0.42, 0.3, 8, [0.3, 0.42, -0.3])
    pCyl(c.corps, 0xef5f5f, 0.16, 0.12, 0.22, 8, [-0.5, 0.39, 0.4])
    pBoite(c.corps, 0x3f7cc4, [0.05, 0.05, 0.5], [-0.1, 0.32, 0.62], [0.3, 0.4, 0])
    pBoite(c.corps, 0x3f7cc4, [0.14, 0.03, 0.18], [-0.02, 0.4, 0.84])
  },

  barbecue: (c) => {
    pBoite(c.corps, c.c1, [1.0, 0.72, 0.72], [-0.18, 0.36, 0])
    pBoite(c.corps, teinter(c.c1, 0.85), [1.06, 0.1, 0.78], [-0.18, 0.75, 0])
    for (let i = 0; i < 6; i++) {
      pBoite(c.corps, METAL, [0.86, 0.03, 0.04], [-0.18, 0.82, -0.3 + i * 0.12])
    }
    pBoite(c.corps, teinter(c.c1, 0.9), [0.94, 0.42, 0.1], [-0.18, 1.02, 0.35])
    pBoite(c.corps, METAL, [0.44, 0.06, 0.44], [0.52, 0.72, 0])
    for (const sz of [1, -1]) {
      pBoite(c.corps, METAL, [0.05, 0.72, 0.05], [0.52, 0.36, sz * 0.18])
    }
    pBoite(c.lueur, BRAISE, [0.84, 0.05, 0.58], [-0.18, 0.74, 0])
  },

  feu_camp: (c) => {
    const r = Math.min(c.l, c.p) / 2 - 0.22
    pCyl(c.corps, teinter(TERRE, 1.1), r + 0.1, r + 0.1, 0.06, 10, [0, 0.03, 0])
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2
      const geo = bosseler(new THREE.SphereGeometry(0.2, 6, 5), 0.35, i + 3)
      pGeo(c.corps, i % 2 === 0 ? c.c1 : teinter(c.c1, 0.85), geo, [Math.cos(a) * r, 0.09, Math.sin(a) * r], undefined, [1, 0.7, 1])
    }
    // Quatre buches dressees en tipi.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.4
      pTube(c.corps, i % 2 === 0 ? BOIS_FONCE : BOIS, 0.07, [Math.cos(a) * 0.34, 0.02, Math.sin(a) * 0.34], [Math.cos(a) * -0.06, 0.62, Math.sin(a) * -0.06])
    }
    pCone(c.lueur, FLAMME, 0.24, 0.62, 6, [0, 0.32, 0])
    pCone(c.lueur, laver(FLAMME, 0.4), 0.13, 0.34, 6, [0.06, 0.62, -0.04])
    pCone(c.lueur, BRAISE, 0.3, 0.16, 8, [0, 0.1, 0])
  },

  // ------------------------------------------------------------- eclairage
  lampadaire: (c) => {
    pCyl(c.corps, teinter(c.c1, 0.8), 0.22, 0.26, 0.16, 8, [0, 0.08, 0])
    pCyl(c.corps, c.c1, 0.08, 0.11, c.h, 8, [0, c.h / 2, 0])
    pBoite(c.corps, c.c1, [0.09, 0.09, 0.4], [0, c.h + 0.02, -0.18])
    pCone(c.corps, teinter(c.c1, 0.9), 0.3, 0.22, 8, [0, c.h + 0.3, -0.34])
    pCyl(c.nuit, 0xfff3d0, 0.2, 0.24, 0.34, 10, [0, c.h + 0.05, -0.34])
    c.m.posLumiere.set(c.b.pos.x, c.b.y + c.h + 0.05, c.b.pos.z)
  },

  torche: (c) => {
    pCyl(c.corps, teinter(PIERRE, 0.9), 0.19, 0.24, 0.14, 8, [0, 0.07, 0])
    pCyl(c.corps, c.c1, 0.055, 0.075, c.h - 0.2, 6, [0, (c.h - 0.2) / 2 + 0.1, 0])
    // Panier a feu: quatre montants et un cerceau.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2
      pBoite(c.corps, METAL, [0.04, 0.26, 0.04], [Math.cos(a) * 0.11, c.h - 0.02, Math.sin(a) * 0.11], [0, -a, 0.16])
    }
    pCyl(c.corps, METAL, 0.15, 0.11, 0.05, 8, [0, c.h + 0.08, 0])
    pCone(c.lueur, FLAMME, 0.13, 0.38, 6, [0, c.h + 0.24, 0])
    pSphere(c.lueur, laver(FLAMME, 0.35), 0.08, [0, c.h + 0.42, 0], undefined, 6, 4)
    c.m.posLumiere.set(c.b.pos.x, c.b.y + c.h + 0.2, c.b.pos.z)
  },

  guirlande: (c) => {
    const demi = c.l / 2 - 0.15
    const yHaut = c.h
    const creux = 0.55
    for (const sx of [1, -1]) {
      pCyl(c.corps, teinter(c.c1, 0.85), 0.2, 0.24, 0.12, 8, [sx * demi, 0.06, 0])
      pCyl(c.corps, c.c1, 0.06, 0.08, yHaut, 8, [sx * demi, yHaut / 2, 0])
      pSphere(c.corps, teinter(c.c1, 0.7), 0.08, [sx * demi, yHaut + 0.04, 0], undefined, 6, 4)
    }
    const n = 8
    const y = (t: number) => yHaut - creux * (1 - t * t)
    for (let i = 0; i < n; i++) {
      const t0 = -1 + (2 * i) / n
      const t1 = -1 + (2 * (i + 1)) / n
      pTube(c.corps, 0x4a4238, 0.018, [t0 * demi, y(t0), 0], [t1 * demi, y(t1), 0])
    }
    const teintes = [0xffd98a, 0xff9a8a, 0x8ad8ff, 0xa8f0a0, 0xffd98a, 0xff9a8a, 0x8ad8ff]
    for (let i = 0; i < 7; i++) {
      const t = -0.86 + (i * 1.72) / 6
      pSphere(c.nuit, teintes[i] as number, 0.085, [t * demi, y(t) - 0.11, 0], [1, 1.25, 1], 6, 4)
    }
    c.m.posLumiere.set(c.b.pos.x, c.b.y + yHaut - 0.3, c.b.pos.z)
  },

  rampe_feux: (c) => {
    pBoite(c.corps, c.c1, [c.l, 0.25, c.p], [0, 0.125, 0])
    for (const [sx, sz] of [
      [-0.4, -0.4],
      [0.4, -0.4],
      [-0.4, 0.4],
      [0.4, 0.4],
    ] as const) {
      pCyl(c.corps, c.c2, 0.09, 0.09, 0.55, 8, [sx, 0.5, sz])
      pCyl(c.corps, teinter(c.c2, 0.7), 0.07, 0.07, 0.06, 8, [sx, 0.79, sz])
    }
  },

  // --------------------------------------------------- utilitaire et decor
  coffre: (c) => {
    pBoite(c.corps, c.c1, [c.l - 0.15, 0.48, c.p - 0.15], [0, 0.24, 0])
    // Couvercle bombe: trois planches, ca suffit a le lire comme un coffre.
    for (const [dz, angle] of [
      [-0.26, 0.5],
      [0, 0],
      [0.26, -0.5],
    ] as const) {
      pBoite(c.corps, c.c2, [c.l - 0.12, 0.09, 0.3], [0, 0.55 + (angle === 0 ? 0.06 : 0), dz * (c.p / 1.0)], [angle, 0, 0])
    }
    for (const sx of [1, -1]) {
      pBoite(c.corps, METAL, [0.06, 0.52, c.p - 0.1], [(sx * (c.l - 0.5)) / 2, 0.26, 0])
    }
    pBoite(c.corps, METAL, [0.2, 0.18, 0.06], [0, 0.44, -c.p / 2 + 0.04])
    pSphere(c.corps, 0xe8c169, 0.05, [0, 0.44, -c.p / 2 - 0.01], undefined, 6, 4)
  },

  atelier: (c) => {
    const yPlan = 0.88
    pBoite(c.corps, c.c1, [c.l - 0.1, 0.12, 0.9], [0, yPlan, 0.3])
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pBoite(c.corps, teinter(c.c1, 0.88), [0.12, yPlan, 0.12], [(sx * (c.l - 0.4)) / 2, yPlan / 2, 0.3 + sz * 0.34])
      }
      pBoite(c.corps, teinter(c.c1, 0.88), [0.09, 0.09, 0.8], [(sx * (c.l - 0.4)) / 2, 0.24, 0.3])
    }
    // Panneau a outils.
    pBoite(c.corps, teinter(c.c1, 0.95), [c.l - 0.2, 1.0, 0.08], [0, yPlan + 0.55, -c.p / 2 + 0.12])
    const outils = -c.p / 2 + 0.2
    pBoite(c.corps, 0x6b4a2f, [0.05, 0.34, 0.05], [-0.9, yPlan + 0.5, outils])
    pBoite(c.corps, c.c2, [0.22, 0.09, 0.09], [-0.9, yPlan + 0.7, outils])
    pBoite(c.corps, c.c2, [0.36, 0.16, 0.03], [-0.3, yPlan + 0.62, outils], [0, 0, 0.35])
    pBoite(c.corps, 0x6b4a2f, [0.16, 0.06, 0.05], [-0.52, yPlan + 0.5, outils])
    pCyl(c.corps, c.c2, 0.04, 0.04, 0.34, 6, [0.3, yPlan + 0.6, outils])
    pCyl(c.corps, c.c2, 0.09, 0.09, 0.05, 6, [0.3, yPlan + 0.78, outils])
    pBoite(c.corps, c.c2, [0.3, 0.06, 0.06], [0.85, yPlan + 0.72, outils])
    pBoite(c.corps, c.c2, [0.06, 0.24, 0.06], [0.85, yPlan + 0.56, outils])
    // Etau et copeaux sur l'etabli, caisse dessous.
    pBoite(c.corps, c.c2, [0.24, 0.16, 0.3], [c.l / 2 - 0.5, yPlan + 0.14, 0.3])
    pCyl(c.corps, METAL, 0.03, 0.03, 0.3, 6, [c.l / 2 - 0.5, yPlan + 0.14, 0.52], [Math.PI / 2, 0, 0])
    pBoite(c.corps, BOIS_CLAIR, [0.7, 0.4, 0.5], [-c.l / 2 + 0.55, 0.2, 0.35])
    for (let i = 0; i < 3; i++) {
      pBoite(c.corps, BOIS_CLAIR, [0.9, 0.07, 0.22], [0.2, yPlan + 0.1 + i * 0.08, 0.55], [0, i * 0.1, 0])
    }
  },

  four_pain: (c) => {
    pBoite(c.corps, teinter(c.c1, 0.82), [c.l - 0.2, 0.66, c.p - 0.2], [0, 0.33, 0])
    pBoite(c.corps, laver(c.c1, 0.12), [c.l, 0.12, c.p], [0, 0.72, 0])
    const geo = new THREE.SphereGeometry(0.78, 12, 7, 0, Math.PI * 2, 0, Math.PI / 2)
    pGeo(c.corps, c.c1, geo, [0, 0.76, 0.05], undefined, [1, 0.95, 1])
    // Gueule voutee, encadree de pierre.
    pBoite(c.corps, NOIR, [0.52, 0.42, 0.5], [0, 0.98, -c.p / 2 + 0.3])
    pCyl(c.corps, NOIR, 0.26, 0.26, 0.5, 8, [0, 1.19, -c.p / 2 + 0.3], [Math.PI / 2, 0, 0], [0, Math.PI])
    for (const sx of [1, -1]) {
      pBoite(c.corps, laver(c.c1, 0.2), [0.12, 0.6, 0.16], [sx * 0.34, 1.04, -c.p / 2 + 0.06])
    }
    pBoite(c.corps, laver(c.c1, 0.2), [0.8, 0.12, 0.16], [0, 1.4, -c.p / 2 + 0.06])
    pCyl(c.corps, c.c2, 0.15, 0.17, 0.55, 8, [0, 1.72, 0.35])
    // Pelle a pain appuyee sur le four.
    pBoite(c.corps, BOIS_CLAIR, [0.06, 1.4, 0.06], [c.l / 2 - 0.2, 0.7, -0.2], [0, 0, 0.22])
    pBoite(c.corps, BOIS_CLAIR, [0.3, 0.3, 0.04], [c.l / 2 - 0.35, 1.4, -0.2], [0, 0, 0.22])
    pBoite(c.lueur, BRAISE, [0.44, 0.06, 0.4], [0, 0.8, -c.p / 2 + 0.34])
  },

  moulin: (c) => {
    const hTour = c.h - 1.0
    pCyl(c.corps, teinter(c.c1, 0.85), 1.72, 1.8, 0.4, 10, [0, 0.2, 0])
    pCyl(c.corps, c.c1, 1.05, 1.62, hTour, 10, [0, hTour / 2 + 0.3, 0])
    pCone(c.corps, c.c2, 1.32, 1.1, 10, [0, hTour + 0.85, 0])
    pSphere(c.corps, teinter(c.c2, 0.8), 0.16, [0, hTour + 1.42, 0], undefined, 6, 4)
    // Porte, deux lucarnes, galerie.
    pBoite(c.corps, c.c3, [1.0, 1.9, 0.14], [0, 0.95, -1.56])
    pBoite(c.corps, teinter(c.c3, 1.15), [0.86, 1.76, 0.06], [0, 0.92, -1.62])
    for (const [dx, dz, ry] of [
      [1.2, 0.5, -1.1],
      [-1.2, 0.5, 1.1],
    ] as const) {
      pBoite(c.corps, 0x9fd4e8, [0.5, 0.5, 0.08], [dx, 2.4, dz], [0, ry, 0])
    }
    pCyl(c.corps, teinter(c.c1, 0.75), 1.5, 1.5, 0.12, 10, [0, 2.05, 0])
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2
      pBoite(c.corps, teinter(c.c2, 0.9), [0.06, 0.4, 0.06], [Math.cos(a) * 1.38, 2.28, Math.sin(a) * 1.38])
    }
    // Les ailes: une maille a part, qui tourne.
    const yAxe = hTour + 0.35
    const zAxe = -1.35
    pCyl(c.corps, teinter(c.c2, 0.8), 0.2, 0.2, 0.5, 8, [0, yAxe, zAxe + 0.1], [Math.PI / 2, 0, 0])
    const ailes: Lot = []
    for (let i = 0; i < 4; i++) {
      const debut = ailes.length
      pBoite(ailes, BOIS_FONCE, [0.16, 2.5, 0.1], [0, 1.35, 0])
      for (let j = 0; j < 6; j++) {
        pBoite(ailes, j % 2 === 0 ? BLANC : teinter(c.c2, 1.2), [0.46, 0.32, 0.05], [0.34, 0.4 + j * 0.38, 0])
      }
      grouper(ailes, debut, tr([0, 0, 0], [0, 0, (i * Math.PI) / 2]))
    }
    const maille = poser(c.m, ailes, { recoit: false })
    if (maille) {
      maille.position.set(0, yAxe, zAxe - 0.16)
      c.m.animes.push({ objet: maille, genre: 'rotation', vitesse: 0.6, phase: phaseDe(c.b.id) })
    }
  },

  tour_guet: (c) => {
    const hPlate = c.h - 1.5
    const demi = c.l / 2 - 0.25
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pBoite(c.corps, c.c1, [0.17, hPlate, 0.17], [sx * demi, hPlate / 2, sz * demi])
      }
    }
    // Croisillons sur les quatre faces, a deux niveaux.
    for (let n = 0; n < 2; n++) {
      const y0 = 0.2 + n * (hPlate - 0.4) * 0.5
      const y1 = y0 + (hPlate - 0.4) * 0.5
      for (const [ax, az, bx, bz] of [
        [-demi, -demi, demi, -demi],
        [-demi, demi, demi, demi],
        [-demi, -demi, -demi, demi],
        [demi, -demi, demi, demi],
      ] as const) {
        pTube(c.corps, teinter(c.c1, 0.88), 0.06, [ax, y0, az], [bx, y1, bz])
        pTube(c.corps, teinter(c.c1, 0.88), 0.06, [bx, y0, bz], [ax, y1, az])
      }
    }
    pBoite(c.corps, teinter(c.c1, 1.05), [c.l - 0.2, 0.14, c.p - 0.2], [0, hPlate, 0])
    // Garde-corps ajoure.
    for (const sz of [1, -1]) {
      pBoite(c.corps, c.c1, [c.l - 0.2, 0.09, 0.09], [0, hPlate + 0.62, (sz * (c.p - 0.2)) / 2])
      for (let i = 0; i < 4; i++) {
        const x = -c.l / 2 + 0.35 + (i * (c.l - 0.7)) / 3
        pBoite(c.corps, c.c1, [0.07, 0.62, 0.07], [x, hPlate + 0.31, (sz * (c.p - 0.2)) / 2])
      }
    }
    for (const sx of [1, -1]) {
      pBoite(c.corps, c.c1, [0.09, 0.09, c.p - 0.2], [(sx * (c.l - 0.2)) / 2, hPlate + 0.62, 0])
    }
    // Quatre poteaux et un toit pyramidal.
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        pBoite(c.corps, c.c1, [0.1, 0.9, 0.1], [sx * demi, hPlate + 0.52, sz * demi])
      }
    }
    pCone(c.corps, c.c2, c.l * 0.78, 0.85, 4, [0, hPlate + 1.4, 0], [0, Math.PI / 4, 0])
    pEchelle(c.corps, teinter(c.c1, 0.92), 0.6, hPlate, tr([0, 0, -c.p / 2 - 0.12], [-0.12, 0, 0]))
    // Une longue-vue, pour dire a quoi sert la tour.
    pCyl(c.corps, METAL, 0.06, 0.08, 0.5, 8, [0.5, hPlate + 0.75, -c.p / 2 + 0.4], [-1.1, 0.4, 0])
    pBoite(c.corps, METAL, [0.06, 0.5, 0.06], [0.5, hPlate + 0.35, -c.p / 2 + 0.45])
  },

  epouvantail: (c) => {
    pBoite(c.corps, BOIS_FONCE, [0.1, c.h, 0.1], [0, c.h / 2, 0])
    pBoite(c.corps, BOIS_FONCE, [1.15, 0.09, 0.09], [0, c.h * 0.72, 0])
    // Chemise, manches, tetes de paille aux poignets.
    pBoite(c.corps, c.c2, [0.62, 0.66, 0.36], [0, c.h * 0.6, 0])
    for (const sx of [1, -1]) {
      pBoite(c.corps, teinter(c.c2, 0.9), [0.28, 0.22, 0.22], [sx * 0.44, c.h * 0.72, 0])
      for (let i = 0; i < 3; i++) {
        pBoite(c.corps, PAILLE, [0.22, 0.05, 0.05], [sx * 0.62, c.h * 0.72, 0], [0, 0, (i - 1) * 0.4])
      }
    }
    for (let i = 0; i < 4; i++) {
      pBoite(c.corps, PAILLE, [0.06, 0.3, 0.06], [-0.18 + i * 0.12, c.h * 0.42, 0.04], [(i - 1.5) * 0.2, 0, 0])
    }
    pSphere(c.corps, 0xe8d9a8, 0.23, [0, c.h * 0.87, 0], [1, 1.05, 1], 8, 6)
    pBoite(c.corps, NOIR, [0.05, 0.05, 0.05], [-0.1, c.h * 0.89, -0.2])
    pBoite(c.corps, NOIR, [0.05, 0.05, 0.05], [0.1, c.h * 0.89, -0.2])
    pBoite(c.corps, 0xd8543f, [0.16, 0.04, 0.05], [0, c.h * 0.82, -0.21])
    pCyl(c.corps, teinter(c.c1, 0.9), 0.45, 0.45, 0.04, 10, [0, c.h * 0.98, 0])
    pCyl(c.corps, c.c1, 0.2, 0.24, 0.24, 10, [0, c.h * 1.05, 0])
    pCyl(c.corps, 0xd8543f, 0.25, 0.25, 0.05, 10, [0, c.h * 0.99, 0])
  },

  statue: (c) => {
    pBoite(c.corps, teinter(c.c1, 0.86), [c.l, 0.3, c.p], [0, 0.15, 0])
    pBoite(c.corps, teinter(c.c1, 0.92), [c.l - 0.3, 0.25, c.p - 0.3], [0, 0.42, 0])
    pBoite(c.corps, laver(c.c1, 0.1), [c.l - 0.55, 0.1, c.p - 0.55], [0, 0.6, 0])
    // Un personnage stylise, bras leve.
    for (const sx of [1, -1]) {
      pBoite(c.corps, c.c1, [0.16, 0.6, 0.18], [sx * 0.13, 0.95, 0])
    }
    pBoite(c.corps, c.c1, [0.48, 0.62, 0.3], [0, 1.56, 0])
    pBoite(c.corps, c.c1, [0.62, 0.14, 0.34], [0, 1.82, 0])
    pBoite(c.corps, c.c1, [0.14, 0.55, 0.14], [-0.34, 1.7, 0], [0, 0, 0.5])
    pBoite(c.corps, c.c1, [0.14, 0.62, 0.14], [0.38, 1.95, 0], [0, 0, -0.35])
    pSphere(c.corps, c.c1, 0.19, [0, 2.02, 0], [1, 1.1, 1], 8, 6)
    pCyl(c.corps, laver(c.c1, 0.3), 0.13, 0.16, 0.06, 8, [0.52, 2.28, 0])
  },

  totem: (c) => {
    const niveaux = 4
    const hNiveau = (c.h - 0.5) / niveaux
    for (let i = 0; i < niveaux; i++) {
      const y = 0.12 + i * hNiveau + hNiveau / 2
      const teinte = i % 2 === 0 ? c.c1 : c.c2
      pBoite(c.corps, teinte, [0.56, hNiveau, 0.56], [0, y, 0])
      // Yeux, bec, et une paire d'ailes a mi-hauteur.
      pBoite(c.corps, BLANC, [0.13, 0.13, 0.05], [-0.13, y + hNiveau * 0.18, -0.29])
      pBoite(c.corps, BLANC, [0.13, 0.13, 0.05], [0.13, y + hNiveau * 0.18, -0.29])
      pBoite(c.corps, NOIR, [0.05, 0.05, 0.04], [-0.13, y + hNiveau * 0.18, -0.32])
      pBoite(c.corps, NOIR, [0.05, 0.05, 0.04], [0.13, y + hNiveau * 0.18, -0.32])
      pCone(c.corps, i % 2 === 0 ? c.c2 : c.c1, 0.13, 0.3, 4, [0, y - hNiveau * 0.16, -0.36], [-Math.PI / 2, 0, 0])
      if (i === niveaux - 2) {
        for (const sx of [1, -1]) {
          pBoite(c.corps, teinter(teinte, 1.15), [0.34, 0.16, 0.4], [sx * 0.44, y, 0], [0, 0, sx * 0.3])
        }
      }
    }
    pBoite(c.corps, teinter(c.c1, 0.8), [0.7, 0.12, 0.7], [0, 0.06, 0])
    pCone(c.corps, c.c2, 0.34, 0.46, 6, [0, c.h - 0.19, 0])
  },

  drapeau: (c) => {
    pCyl(c.corps, teinter(c.c1, 0.8), 0.34, 0.4, 0.22, 8, [0, 0.11, 0])
    pCyl(c.corps, laver(c.c1, 0.15), 0.055, 0.07, c.h, 8, [0, c.h / 2 + 0.1, 0])
    pSphere(c.corps, 0xe8c169, 0.09, [0, c.h + 0.16, 0], undefined, 6, 4)
    // Le drapeau ondule: quatre les decalees, alternees.
    for (let i = 0; i < 4; i++) {
      const x = 0.09 + i * 0.31
      const dz = (i % 2 === 0 ? 1 : -1) * 0.06
      pBoite(
        c.corps,
        i % 2 === 0 ? c.c2 : teinter(c.c2, 1.12),
        [0.32, 0.62, 0.03],
        [x, c.h - 0.35 - i * 0.02, dz],
        [0, (i % 2 === 0 ? 1 : -1) * 0.22, 0],
      )
    }
  },

  panneau: (c) => {
    pBoite(c.corps, c.c1, [0.11, c.h, 0.11], [0, c.h / 2, 0])
    pBoite(c.corps, teinter(c.c1, 0.85), [0.34, 0.1, 0.34], [0, 0.05, 0])
    pBoite(c.corps, c.c2, [c.l - 0.2, 0.44, 0.07], [0, c.h - 0.28, -0.02])
    pBoite(c.corps, teinter(c.c2, 0.78), [c.l - 0.3, 0.07, 0.09], [0, c.h - 0.19, -0.03])
    pBoite(c.corps, teinter(c.c2, 0.78), [c.l - 0.5, 0.07, 0.09], [-0.06, c.h - 0.36, -0.03])
    // Une fleche en dessous, pour indiquer la direction.
    pBoite(c.corps, teinter(c.c2, 0.92), [c.l - 0.5, 0.24, 0.06], [0.06, c.h - 0.62, 0.02], [0, 0.2, 0])
    pCone(c.corps, teinter(c.c2, 0.92), 0.16, 0.22, 3, [c.l / 2 - 0.16, c.h - 0.62, 0.08], [0, 0.2, -Math.PI / 2])
  },

  boite_lettres: (c) => {
    pBoite(c.corps, c.c1, [0.1, c.h - 0.2, 0.1], [0, (c.h - 0.2) / 2, 0])
    pBoite(c.corps, teinter(c.c1, 0.85), [0.3, 0.1, 0.3], [0, 0.05, 0])
    const y = c.h - 0.18
    pBoite(c.corps, c.c2, [0.4, 0.26, 0.58], [0, y, 0])
    for (const [dx, angle] of [
      [-0.16, 0.6],
      [0, 0],
      [0.16, -0.6],
    ] as const) {
      pBoite(c.corps, teinter(c.c2, 1.1), [0.16, 0.07, 0.58], [dx, y + 0.15 - Math.abs(dx) * 0.25, 0], [0, 0, angle])
    }
    pBoite(c.corps, NOIR, [0.3, 0.05, 0.04], [0, y + 0.02, -0.3])
    pBoite(c.corps, laver(c.c2, 0.4), [0.42, 0.28, 0.05], [0, y, 0.3])
    // Le petit drapeau du courrier, leve.
    pBoite(c.corps, 0xd83a2a, [0.03, 0.26, 0.03], [0.23, y + 0.24, 0.06])
    pBoite(c.corps, 0xd83a2a, [0.03, 0.14, 0.16], [0.23, y + 0.3, 0.16])
  },
}

function construireAmenagement(m: Maillage, b: Batiment, def: BatimentDef, style: StyleBatiment): void {
  const c: Chantier = {
    m,
    b,
    l: def.taille.l,
    p: def.taille.p,
    h: style.hauteurMur,
    style,
    c1: choisirCouleur('murs', b, def, style),
    c2: choisirCouleur('toit', b, def, style),
    c3: choisirCouleur('porte', b, def, style),
    corps: [],
    translucide: [],
    lueur: [],
    nuit: [],
    pousses: [],
  }

  const constructeur = MODELES[b.kind]
  if (!constructeur) {
    journal.warn('batiment', `aucune geometrie pour ${String(b.kind)}`)
    return
  }
  constructeur(c)

  // Le plan se leve avant la fusion: apres, le lot est vide.
  const plan = planifierRecolte(c.corps, c.pousses)
  const plat = style.hauteurMur < 0.45
  const corps = poser(m, c.corps, { ombre: !plat })
  if (plan && corps) m.recolte = fixerRecolte(corps, plan)
  poser(m, c.translucide, { mat: { transparent: true, opacite: 0.72 }, ombre: false })
  poser(m, c.lueur, { mat: { emissive: 0xff7a26 }, ombre: false })
  const nuit = poser(m, c.nuit, { ombre: false })
  if (nuit) m.fenetres.push(nuit)

  const veilleuse = style.veilleuse ?? 0
  if (veilleuse > 0) {
    m.veutLumiere = true
    m.veilleuse = veilleuse
    // Les modeles qui placent leur source eux-memes le font en repere MONDE
    // (b.y compris): ne rien ajouter, sinon la lumiere monte deux fois.
    if (m.posLumiere.lengthSq() === 0) m.posLumiere.set(b.pos.x, b.y + style.hauteurMur, b.pos.z)
  }
}

function construireMaillage(b: Batiment, terrain: ITerrain): Maillage {
  const def = batimentDef(b.kind)
  const style = STYLES[b.kind]
  const m = maillageVide(`batiment:${b.id}`)

  if (!def.habitable) construireAmenagement(m, b, def, style)
  else if (b.kind === 'tente') construireTente(m, b, def, style)
  else construireHabitation(m, b, def, style, terrain)

  return m
}

// ------------------------------------------------------------------ classe

interface Entree {
  batiment: Batiment
  maillage: Maillage
  /** Opacite visee du toit: 1 dehors, 0 dedans. */
  cibleToit: number
  opaciteToit: number
  /**
   * Opacite courante de la TERRASSE et du REZ-DE-CHAUSSEE.
   *
   * Le meme mecanisme que le toit, applique a l'etage: on ne voit que le niveau
   * ou l'on se tient. Dans le salon, la terrasse s'efface comme un toit
   * s'efface; sur la terrasse, c'est le plancher du dessous qui part.
   */
  opaciteEtage: number
  /** Opacite du deuxieme niveau de l'hotel: meme mecanisme, un cran plus haut. */
  opaciteEtage2: number
  opaciteBas: number
  /** Opacite courante par mur, indexee comme `maillage.murs`. */
  opaciteMurs: number[]
}

/** Opacite de repos de l'eau: assez pour la voir, assez peu pour voir a travers. */
const OPACITE_EAU = 0.62

/** Applique une opacite a un lot de mailles, et les cache quand elles ne servent plus. */
function appliquerOpacite(objets: readonly THREE.Object3D[], opacite: number): void {
  const visible = opacite > 0.02
  for (const o of objets) {
    o.visible = visible
    const mt = (o as THREE.Mesh).material as THREE.Material | undefined
    if (!mt) continue
    mt.opacity = opacite
    mt.transparent = opacite < 0.99
  }
}

export class RenduBatiments {
  readonly groupe: THREE.Group
  private scene: THREE.Scene
  private entrees = new Map<string, Entree>()
  private fantomeGroupe: THREE.Group | null = null
  private fantomeSignature = ''
  private fantomeMateriaux: THREE.Material[] = []
  private fantomeMaillage: Maillage | null = null
  private facteurNuit = 0
  /** Pool fixe de lumieres, reattribuees aux batiments les plus proches. */
  private lumieres: THREE.PointLight[] = []
  /** Le batiment que chaque lampe du pool eclaire (meme indice), ou null si elle est libre. */
  private lumiereDe: (string | null)[] = []
  private versCamera = new THREE.Vector3()
  private normaleTemp = new THREE.Vector3()
  private axeY = new THREE.Vector3(0, 1, 0)
  /** Liste des batiments, mise en cache: elle etait rebatie 8 fois par tick. */
  /** Temps ecoule, pour les ailes du moulin et les balancoires. */
  private temps = 0

  /**
   * LE TERRAIN, pour les modeles qui posent quelque chose SUR le sol.
   *
   * Une aerogare porte une piste, et une piste doit EPOUSER le relief: un ruban
   * plat de trente metres se planterait dans la butte a un bout et flotterait a
   * l'autre — et surtout l'avion, lui, roule a la hauteur du terrain
   * (`majAvion`), donc il s'enfoncerait dans son propre bitume. Le rendu des
   * batiments n'avait jamais eu besoin du sol jusqu'ici; il l'a maintenant.
   */
  private readonly terrain: ITerrain

  constructor(scene: THREE.Scene, terrain: ITerrain) {
    this.scene = scene
    this.terrain = terrain
    this.groupe = new THREE.Group()
    this.groupe.name = 'batiments'
    scene.add(this.groupe)

    for (let i = 0; i < POOL_LUMIERES; i++) {
      const l = new THREE.PointLight(0xffd9a0, 0, PORTEE_LUMIERE, 1.6)
      l.castShadow = false
      l.name = `lumiere-batiment-${i}`
      this.groupe.add(l)
      this.lumieres.push(l)
      this.lumiereDe.push(null)
    }
  }

  /**
   * Attribue les lumieres du pool aux sources allumees les plus proches.
   *
   * Est allumee la nuit toute source qui declare une `veilleuse` (lampadaire,
   * torche, guirlande, feu de camp, lanterne du phare); est allumee en plus la
   * maison dans laquelle se trouve le joueur.
   */
  /*
   * UNE LAMPE GARDE SA MAISON. Les lampes du pool etaient redistribuees a
   * chaque image par RANG de distance: deux maisons a egale distance
   * echangeaient leur rang a chaque pas, et la lampe SAUTAIT de l'une a
   * l'autre a pleine intensite — la maison au bord des six plus proches
   * clignotait tres vite, et elle seule (vu au telephone le 13 octobre, a
   * l'aube). Chaque lampe retient donc QUI elle eclaire; une maison eclairee
   * garde sa lampe tant qu'aucune autre n'est plus proche de MARGE_LUMIERE; et
   * une lampe ne change de maison qu'eteinte: elle s'efface, puis demenage.
   */
  private majLumieres(joueur: THREE.Vector3, interieurId: string | null, dt: number): void {
    const candidats: { id: string; pos: THREE.Vector3; intensite: number; score: number }[] = []
    for (const e of this.entrees.values()) {
      if (!e.maillage.veutLumiere) continue
      const intensite = Math.max(
        e.maillage.veilleuse * this.facteurNuit,
        e.batiment.id === interieurId ? 1.1 : 0,
      )
      if (intensite <= 0.01) continue
      const p = e.maillage.posLumiere
      const tenu = this.lumiereDe.includes(e.batiment.id)
      candidats.push({
        id: e.batiment.id,
        pos: p,
        intensite,
        score: p.distanceTo(joueur) - (tenu ? MARGE_LUMIERE : 0),
      })
    }
    candidats.sort((a, b) => a.score - b.score)
    const voulus = new Map(candidats.slice(0, this.lumieres.length).map((c) => [c.id, c]))

    const pas = VITESSE_FONDU * dt
    // Les lampes dont la maison est toujours voulue la suivent; les autres
    // s'eteignent, et ne sont libres qu'une fois eteintes.
    for (let i = 0; i < this.lumieres.length; i++) {
      const l = this.lumieres[i]
      if (!l) continue
      const id = this.lumiereDe[i] ?? null
      const c = id === null ? undefined : voulus.get(id)
      if (c) {
        l.position.copy(c.pos)
        l.intensity += clamp(c.intensite - l.intensity, -pas, pas)
        voulus.delete(c.id)
      } else {
        l.intensity += clamp(0 - l.intensity, -pas, pas)
        if (l.intensity <= 0.001) this.lumiereDe[i] = null
      }
    }
    // Les maisons voulues sans lampe prennent une lampe libre (donc eteinte).
    for (const c of voulus.values()) {
      const i = this.lumiereDe.findIndex((id, k) => id === null && (this.lumieres[k]?.intensity ?? 1) <= 0.001)
      if (i < 0) break
      this.lumiereDe[i] = c.id
      this.lumieres[i]?.position.copy(c.pos)
    }
  }

  ajouter(b: Batiment): void {
    // Un `kind` inconnu (renommage, base abimee) ferait jeter la construction du
    // maillage, donc echouer le demarrage entier. On ignore et on signale.
    if (!(b.kind in STYLES)) {
      journal.warn('batiment', `modele inconnu ignore: ${String(b.kind)}`, { id: b.id })
      return
    }
    if (this.entrees.has(b.id)) this.retirer(b.id)
    const maillage = construireMaillage(b, this.terrain)
    maillage.groupe.position.set(b.pos.x, b.y, b.pos.z)
    maillage.groupe.rotation.y = (b.rot * Math.PI) / 2
    this.groupe.add(maillage.groupe)
    // Un lampadaire pose de nuit restait eteint jusqu'au prochain changement de
    // lumiere du jour: on lui applique tout de suite l'etat courant.
    const couleur = couleurFenetre(this.facteurNuit)
    for (const f of maillage.fenetres) (f.material as THREE.MeshLambertMaterial).emissive.setHex(couleur)
    this.entrees.set(b.id, {
      batiment: b,
      maillage,
      cibleToit: 1,
      opaciteToit: 1,
      opaciteEtage: 1,
      opaciteEtage2: 1,
      opaciteBas: 1,
      opaciteMurs: maillage.murs.map(() => 1),
    })
    journal.info('batiment', `pose ${b.kind} en (${b.pos.x.toFixed(1)}, ${b.pos.z.toFixed(1)}) rot ${b.rot}`)
  }

  retirer(id: string): void {
    const e = this.entrees.get(id)
    if (!e) return
    this.groupe.remove(e.maillage.groupe)
    libererMaillage(e.maillage)
    this.entrees.delete(id)
    journal.info('batiment', `retire ${id}`)
  }

  /**
   * Fondu du toit et des murs genants, et parties animees.
   *
   * `interieurId` est l'identifiant du batiment dans lequel se trouve le
   * joueur, ou null. `dirCamera` est la direction dans laquelle la camera
   * regarde (de la camera vers la scene): un mur dont la normale sortante
   * pointe vers la camera masque l'interieur, donc on l'efface.
   *
   * `niveau` est l'etage ou se tient le joueur (0 au sol). C'est lui qui decide
   * QUEL niveau on voit: en bas, la terrasse s'efface comme un toit; en haut,
   * c'est le rez-de-chaussee qui part. Le meme fondu, la meme vitesse, le meme
   * mecanisme — il n'y avait aucune raison d'en inventer un second.
   */
  majFondu(
    interieurId: string | null,
    dirCamera: THREE.Vector3,
    dt: number,
    joueur?: THREE.Vector3,
    niveau = 0,
  ): void {
    // Reutilise: cloner un Vector3 par image nourrit le ramasse-miettes.
    this.versCamera.copy(dirCamera).negate().normalize()
    const versCamera = this.versCamera
    const pas = VITESSE_FONDU * dt
    this.temps += dt
    if (joueur) this.majLumieres(joueur, interieurId, dt)

    for (const e of this.entrees.values()) {
      const dedans = e.batiment.id === interieurId
      // « En haut » ne veut dire quelque chose que pour le batiment ou l'on se
      // trouve: la terrasse du voisin reste entiere quand on marche sur la sienne.
      const enHaut = dedans && niveau > 0
      const auSalon = dedans && !enHaut
      e.cibleToit = auSalon ? 0 : 1

      e.opaciteToit += clamp(e.cibleToit - e.opaciteToit, -pas, pas)
      appliquerOpacite(e.maillage.toit, e.opaciteToit)

      /*
       * ON NE VOIT QUE LE NIVEAU OU L'ON SE TIENT. Chaque niveau suit la meme
       * regle que le toit: il s'efface des qu'on est DEDANS sans etre sur
       * lui — en dessous, c'est un couvercle; au-dessus, il montrerait deux
       * etages a la fois. Dehors, tout est plein: la facade entiere.
       */
      e.opaciteEtage += clamp((dedans && niveau !== 1 ? 0 : 1) - e.opaciteEtage, -pas, pas)
      appliquerOpacite(e.maillage.etage, e.opaciteEtage)
      e.opaciteEtage2 += clamp((dedans && niveau !== 2 ? 0 : 1) - e.opaciteEtage2, -pas, pas)
      appliquerOpacite(e.maillage.etage2, e.opaciteEtage2)
      // L'eau des etages s'efface AVEC eux, mais sans jamais redevenir
      // opaque: son opacite de repos est celle de l'eau, pas 1.
      appliquerOpacite(e.maillage.etageEau, e.opaciteEtage * OPACITE_EAU)
      appliquerOpacite(e.maillage.etage2Eau, e.opaciteEtage2 * OPACITE_EAU)

      // Et le rez-de-chaussee disparait quand on est monte: on ne voit que
      // l'etage ou l'on se tient.
      e.opaciteBas += clamp((enHaut ? 0 : 1) - e.opaciteBas, -pas, pas)
      appliquerOpacite(e.maillage.bas, e.opaciteBas)

      for (let i = 0; i < e.maillage.murs.length; i++) {
        const mur = e.maillage.murs[i]
        if (!mur) continue
        let cible = 1
        if (enHaut) {
          // Les murs du bas ne masquent rien depuis la terrasse, mais les
          // laisser pleins reviendrait a montrer les deux niveaux a la fois.
          cible = 0.12
        } else if (dedans) {
          // La normale locale doit passer dans le repere du monde: seule la
          // rotation autour de y compte, et elle est un quart de tour.
          const n = this.normaleTemp.copy(mur.normale).applyAxisAngle(this.axeY, e.maillage.groupe.rotation.y)
          cible = n.dot(versCamera) > 0.25 ? 0.12 : 1
        }
        const courant = e.opaciteMurs[i] ?? 1
        const suivant = courant + clamp(cible - courant, -pas, pas)
        e.opaciteMurs[i] = suivant
        const mt = mur.maillage.material as THREE.Material
        mt.opacity = suivant
        mt.transparent = suivant < 0.99
        mur.maillage.visible = suivant > 0.02
      }

      for (const a of e.maillage.animes) {
        if (a.genre === 'rotation') a.objet.rotation.z = this.temps * a.vitesse + a.phase
        else a.objet.rotation.x = Math.sin(this.temps * a.vitesse + a.phase) * 0.32
      }
    }
  }

  /**
   * Fait pousser ou fane la vegetation des jardins.
   *
   * `maturite` va de 0 (tout juste cueilli) a 1 (mur). On ne touche aux tampons
   * que si la valeur a bouge d'un centieme: sur une journee de repousse cela
   * fait une centaine de reecritures, contre une par image sinon.
   */
  majJardins(maturite: (id: string) => number): void {
    for (const e of this.entrees.values()) {
      const r = e.maillage.recolte
      if (!r) continue
      const m = clamp(maturite(e.batiment.id), 0, 1)
      if (Math.abs(m - r.maturite) < 0.01) continue
      appliquerRecolte(r, m)
    }
  }

  /** Allume les fenetres, les lampadaires et les guirlandes la nuit. */
  majNuit(facteurNuit: number): void {
    if (Math.abs(facteurNuit - this.facteurNuit) < 0.01) return
    this.facteurNuit = facteurNuit
    const couleur = couleurFenetre(facteurNuit)
    for (const e of this.entrees.values()) {
      for (const f of e.maillage.fenetres) {
        const mt = f.material as THREE.MeshLambertMaterial
        mt.emissive.setHex(couleur)
      }
    }
  }

  // ----------------------------------------------------------- fantome

  /**
   * Apercu translucide du batiment avant de le poser. On reconstruit le
   * maillage seulement quand le modele ou les couleurs changent; deplacer le
   * fantome ne coute alors qu'une affectation de position.
   */
  fantome(
    kind: BatimentKind,
    pos: Vec2,
    y: number,
    rot: number,
    valide: boolean,
    couleurs: { murs: number; toit: number; porte: number },
  ): void {
    const signature = `${kind}:${rot}:${couleurs.murs}:${couleurs.toit}:${couleurs.porte}`
    if (signature !== this.fantomeSignature) {
      this.cacherFantome()
      const faux: Batiment = { id: '__fantome', kind, pos: { x: 0, z: 0 }, y: 0, rot: 0, proprietaire: '', couleurs }
      const m = construireMaillage(faux, this.terrain)
      // Le fantome ne projette pas d'ombre et ne recoit rien: c'est un apercu.
      m.groupe.traverse((o) => {
        const maille = o as THREE.Mesh
        if (!maille.isMesh) return
        maille.castShadow = false
        maille.receiveShadow = false
      })
      this.fantomeMaillage = m
      this.fantomeGroupe = m.groupe
      this.fantomeMateriaux = m.materiaux
      this.groupe.add(m.groupe)
      this.fantomeSignature = signature
    }
    const g = this.fantomeGroupe
    if (!g) return
    g.position.set(pos.x, y + 0.02, pos.z)
    g.rotation.y = (rot * Math.PI) / 2
    g.visible = true

    const teinte = valide ? 0x63c76a : 0xef5f5f
    for (const mt of this.fantomeMateriaux) {
      const lam = mt as THREE.MeshLambertMaterial
      lam.transparent = true
      lam.opacity = 0.55
      lam.emissive.setHex(teinte)
      lam.depthWrite = false
    }
  }

  cacherFantome(): void {
    if (!this.fantomeGroupe || !this.fantomeMaillage) return
    this.groupe.remove(this.fantomeGroupe)
    // Tourner un modele ou changer sa couleur reconstruit le fantome: sans une
    // liberation complete, geometries et materiaux s'accumulaient a chaque fois.
    libererMaillage(this.fantomeMaillage)
    this.fantomeGroupe = null
    this.fantomeMaillage = null
    this.fantomeMateriaux = []
    this.fantomeSignature = ''
  }

  dispose(): void {
    for (const id of [...this.entrees.keys()]) this.retirer(id)
    this.cacherFantome()
    for (const l of this.lumieres) l.dispose()
    this.lumieres = []
    this.lumiereDe = []
    this.scene.remove(this.groupe)
  }
}
